import React, { useState, useEffect, useMemo, useRef, useId } from "react";
import {
  getSettings,
  updateSettings,
  appendTrade,
  appendLesson,
  fetchJournalRows,
  SHEET_HEADERS,
  getNextTradeNumber,
  getUserSettings,
  updateUserSettings
} from "../services/FirestoreService";
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { FaRegClock, FaCalculator, FaChartLine, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import PreTradeChecklistModal from './PreTradeChecklistModal';
import { savePreTradeChecklist, getTodayPreTradeChecklist,setChecklistShown } from "../services/FirestoreService";
import { createPortal } from "react-dom";
import SimpleCalculator from './SimpleCalculator'; 
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase"
import { checkFirstTradeAnalysis } from "../services/FirstTradeAnalysisService"; // Logic
import FirstTradeAnalysisPopup from "../components/FirstTradeAnalysisPopup";     // UI
import { FUTURES_PRODUCTS, FOREX_PAIRS, FOREX_INDICES, isIndexSymbol } from "../constants/instruments";

// --- CONSTANTS ---

const ASSET_CLASSES = ["Futures", "Forex", "Stocks", "Options"];



const TIMEFRAME_OPTIONS = ["30s","1m","2m","3m","5m","10m","15m","30m","1h","2h","4h","D","W","M"];
const SESSION_OPTIONS = ["New York", "London", "Asian", "Sydney","Mixed"];
const RULES_OPTIONS = ["Yes", "No"];
const GRADE_OPTIONS = ["A", "B", "C", "D", "E", "F"];
const POSITION_OPTIONS = ["Buy", "Sell"];
const MOOD_OPTIONS = ["1", "2", "3", "4", "5"];

// --- UI HELPERS ---

function SummaryRow({ label, value, red }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className={red ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}>
        {value}
      </span>
    </div>
  );
}

function getSessionFromTime(time24) {
  if (!time24) return "";
  
  const [hour] = time24.split(":").map(Number);

  // New York: 8:00 AM - 5:00 PM (17:00)
  if (hour >= 8 && hour < 17) return "New York";
  
  // London: 3:00 AM - 8:00 AM
  if (hour >= 3 && hour < 8) return "London";
  
  // Asian (Tokyo): 7:00 PM (19:00) - 3:00 AM 
  // (Using || because it crosses midnight)
  if (hour >= 19 || hour < 3) return "Asian";
  
  // Sydney: 5:00 PM (17:00) - 7:00 PM (19:00)
  // (Captures the gap after NY closes and before Tokyo opens)
  if (hour >= 17 && hour < 19) return "Sydney";
  
  return "";
}

const IconGoal = () => (
  <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 mr-2 shadow-sm">
    <svg width="24" height="24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="#16a34a" strokeWidth="2"/>
      <path d="M8 12l2 2l4-4" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  </span>
);

function Tooltip({ children, content, variant = "neutral", backdrop = false, closeOnEsc = true, lockScroll = true }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  
  const palette = {
    neutral: { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" },
    yellow: { panel: "bg-yellow-600/95 text-white ring-yellow-300/30", arrow: "bg-yellow-600/95", focus: "focus:ring-yellow-500" },
    red: { panel: "bg-red-800/95 text-white ring-red-300/30", arrow: "bg-red-800/95", focus: "focus:ring-red-500" },
    blue: { panel: "bg-slate-900/95 text-white ring-blue-300/30", arrow: "bg-slate-900/95", focus: "focus:ring-blue-500" },
  }[variant] || { panel: "bg-gray-900/95 text-white ring-white/10", arrow: "bg-gray-900/95", focus: "focus:ring-yellow-500" };

  useEffect(() => {
    if (open && backdrop && closeOnEsc) {
      const handleEsc = (e) => e.key === "Escape" && setOpen(false);
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [open, backdrop, closeOnEsc]);

  return (
    <>
      <span
        ref={triggerRef}
        aria-describedby={id}
        onMouseEnter={() => !backdrop && setOpen(true)}
        onMouseLeave={() => !backdrop && setOpen(false)}
        onClick={() => setOpen(!open)}
        className={`relative inline-flex items-center underline decoration-dotted underline-offset-2 cursor-help focus:outline-none ${palette.focus} rounded-[3px]`}
      >
        {children}
      </span>
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
          {backdrop && <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] pointer-events-auto" onClick={() => setOpen(false)} />}
          <div
            id={id}
            role="tooltip"
            style={{ 
              position: 'absolute', 
              top: triggerRef.current ? triggerRef.current.getBoundingClientRect().top - 10 : 0,
              left: triggerRef.current ? triggerRef.current.getBoundingClientRect().left : 0,
              transform: 'translate(-50%, -100%)'
            }}
            className={`pointer-events-auto select-text max-w-[min(90vw,28rem)] w-max break-words rounded-lg px-4 py-3 text-[16px] leading-relaxed font-normal shadow-2xl ring-1 backdrop-blur-sm transition-opacity duration-200 opacity-100 scale-100 ${palette.panel}`}
          >
            {content}
            <span className={`absolute top-full left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 ${palette.arrow} shadow -mt-1.5`} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// --- UTILITIES ---

function formatCurrency(val, currency = "USD") {
  if (val === "" || val === null || isNaN(Number(val))) return "";
  return Number(val).toLocaleString("en-US", { style: "currency", currency: currency, minimumFractionDigits: 2 });
}

function parseCurrency(str) {
  const n = Number(String(str ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function getTodayLocalISO() {
  const today = new Date();
  const tzOffset = today.getTimezoneOffset() * 60000;
  return new Date(today - tzOffset).toISOString().slice(0, 10);
}

function to24HourTime(str) {
  if (!str) return "";
  if (/^\d{2}:\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2}):(\d{2})\s*([APap][Mm])$/);
  if (m) {
    let [, h, mins, ap] = m;
    let hour = parseInt(h, 10);
    if (ap.toUpperCase() === "PM" && hour !== 12) hour += 12;
    if (ap.toUpperCase() === "AM" && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, "0")}:${mins}`;
  }
  return "";
}

function timeStringToSeconds(str) {
  if (!str) return 0;
  const parts = str.split(":").map(Number);
  if (parts.length >= 2) {
    return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
  }
  return 0;
}

function secondsToHHMMSS(totalSeconds) {
  totalSeconds = Math.max(0, totalSeconds);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [
    h.toString().padStart(2, "0"),
    m.toString().padStart(2, "0"),
    s.toString().padStart(2, "0"),
  ].join(":");
}

function getDurationHHMMSS(start, end) {
  if (!start || !end) return "";
  let s = timeStringToSeconds(to24HourTime(start));
  let e = timeStringToSeconds(to24HourTime(end));
  let diff = e - s;
  if (diff < 0) diff += 24 * 3600; // Crossed midnight
  return secondsToHHMMSS(diff);
}

function getPipSizeForSymbol(symbol) {
  if (!symbol) return 0.0001;
  const s = String(symbol).toUpperCase();
  
  // Back to 1! (1 Pip = 1 Point)
  if (isIndexSymbol(s)) return 1; 
  
  // JPY Pairs, Gold, and Silver all use a 0.01 pip size
  if (s.includes("JPY") || s.includes("XAU") || s.includes("GOLD") || s.includes("XAG") || s.includes("SILVER")) {
      return 0.01;
  }
  return 0.0001;
}

function computePipsFromPricesAbs(product, entry, exit) {
  const e = Number(entry);
  const x = Number(exit);
  if (!Number.isFinite(e) || !Number.isFinite(x)) return null;
  
  const diff = Math.abs(x - e);
  const pipSize = getPipSizeForSymbol(product);
  return pipSize > 0 ? Math.round(diff / pipSize) : 0;
}

function toAmPm(str) {
  if (!str) return "";
  if (!/^\d{2}:\d{2}$/.test(str)) return str;
  let [h, m] = str.split(":");
  h = parseInt(h, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return `${hour12.toString().padStart(2, "0")}:${m} ${ampm}`;
}

// 500ms delay is usually the sweet spot for UX vs Performance
const DEBOUNCE_DELAY = 500; 


// --- LOGIC HELPERS ---

function getMultiplier(assetClass, product, settings = {}) {
  if (assetClass === "Futures") {
    const match = String(product).match(/\$\s?([0-9,.]+)/);
    return match ? parseFloat(match[1].replace(/,/g, "")) : 0;
  }
  if (assetClass === "Forex") {
    if (settings["Pip Value"]) return Number(settings["Pip Value"]);
    const sym = (product || "").toUpperCase();
    
    if (sym.startsWith("XAU") || sym.includes("GOLD")) return 1;    
    if (sym.startsWith("XAG") || sym.includes("SILVER")) return 5;    
    if (sym.includes("JPY")) return 9.09;   
    
    const FOREX_INDICES = ["US30", "NAS100", "SPX500", "GER30", "UK100"];
    if (FOREX_INDICES.some(idx => sym.includes(idx))) return 1;
    
    return 10; 
  }
  if (assetClass === "Options") return 100;
  return 1;
}

function getUnitLabel(assetClass) {
  if (assetClass === "Futures") return "Contracts";
  if (assetClass === "Forex") return "Lots";
  if (assetClass === "Options") return "Contracts"; // Changed from Shares to Contracts
  return "Shares";
}

function getCompoundingProjection({ assetClass, balance, goal, riskPercent, riskMode, riskDollar, stopLoss, takeProfit, maxContracts, minContracts, multiplier, entryPrice, investmentAmount, investmentCapMode }) {
  let trades = [];
  let currentBalance = balance;
  let tradeCount = 0;

  while (currentBalance < goal && tradeCount < 10000) {
    const riskBudget = riskMode === "$" ? riskDollar : (currentBalance * riskPercent / 100);
    
    let currentInvestCap = investmentAmount;
    if (investmentCapMode === "%") {
        currentInvestCap = currentBalance * (investmentAmount / 100);
    }

    let size = 0;
    
    if (assetClass === "Stocks") {
        // FIX: Sizing is based on Risk Budget / Distance to Stop Loss
        const riskSize = stopLoss > 0 ? (riskBudget / stopLoss) : 0;
        const cashSize = entryPrice > 0 ? (currentBalance / entryPrice) : 0;
        const capSize = (currentInvestCap > 0 && entryPrice > 0) ? (currentInvestCap / entryPrice) : 999999;
        size = Math.min(riskSize, cashSize, capSize);
    } else if (assetClass === "Options") {
        const riskPerContract = stopLoss * multiplier;
        const costPerContract = entryPrice * multiplier;
        const riskSize = riskPerContract > 0 ? Math.floor(riskBudget / riskPerContract) : 0;
        const cashSize = costPerContract > 0 ? Math.floor(currentBalance / costPerContract) : 0;
        const capSize = (currentInvestCap > 0 && costPerContract > 0) ? Math.floor(currentInvestCap / costPerContract) : 999999;
        size = Math.min(riskSize, cashSize, capSize);
    } else {
        if (stopLoss > 0 && multiplier > 0) size = riskBudget / (stopLoss * multiplier);
    }

    size = Math.min(size, maxContracts);
    
    // Proper decimal rounding
    if (assetClass === "Forex") size = Math.floor(size * 100) / 100;
    else if (assetClass === "Stocks") size = Math.floor(size * 10000) / 10000;
    else size = Math.floor(size); 

    const usedSize = Math.max(size, minContracts);
    const tradeRisk = usedSize * (assetClass === "Stocks" ? stopLoss : (stopLoss * multiplier));
    const tradeProfit = usedSize * (assetClass === "Stocks" ? takeProfit : (takeProfit * multiplier));

    // 2. Check if underfunded
    let isUnderfunded = false;
    let tradeCost = 0;

    if (assetClass === "Stocks") {
        tradeCost = usedSize * entryPrice;
        if (tradeCost > currentBalance) isUnderfunded = true;
    } else if (assetClass === "Options" && entryPrice > 0) {
        tradeCost = usedSize * entryPrice * multiplier; 
        if (tradeCost > currentBalance) isUnderfunded = true;
    } else {
        if (size < minContracts) isUnderfunded = true;
    }

    trades.push({
      trade: tradeCount + 1,
      balance: currentBalance,
      riskDollar: riskBudget,
      size: usedSize,
      totalRisk: tradeRisk,
      tradeProfit: tradeProfit,
      nextBalance: currentBalance + tradeProfit,
      underfunded: isUnderfunded,
      cost: tradeCost 
    });

    currentBalance += tradeProfit;
    tradeCount++;
    if (currentBalance >= goal) break;
  }
  return { trades, tradeCount: trades.length };
}

function formatThreeLossMessage(line) {
  if (!line) return null;
  const highlights = {
    "Take a break!": "font-bold text-red-500 dark:text-red-500",
    "Trading is 80% Trading Psychology.": "font-bold text-blue-500 dark:text-blue-400",
    "Continue being Awesome!": "font-bold text-green-600 dark:text-green-400", 
    "BIG": "font-bold text-green-600 dark:text-green-400" 
  };
  const escaped = Object.keys(highlights).map(s => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`(${escaped})`, "g");
    return line.split(regex).map((part, i) =>
      highlights[part]
        ? <span key={i} className={highlights[part]}>{part}</span>
        : <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    );
  }

  const TWO_LOSS_WARNING_TEXT = `You've had 2 losses in a row.

Evaluate: Take a break and review your recent trades. What can you learn from them before placing your next order?

Accept: Remember that losses are a part of trading. Learning to be okay with them is crucial for success.

Refocus: No strategy is 100%, but following your rules consistently is the only way to improve your chances.

You've got this. Apply what you learned in the next one!`;

function highlightHundredPercent(line) {
  if (!line) return null;
  return line.split(/(100%)/g).map((part, i) =>
    part === "100%" ? (
      <span key={i} className="font-extrabold text-yellow-500 dark:text-yellow-400">100%</span>
    ) : (
      <React.Fragment key={i}>{renderWithTiltTooltip(part)}</React.Fragment>
    )
  );
}

function renderWithTiltTooltip(text) {
  const parts = text.split(/(\btilt\b)/gi);
  return parts.map((part, idx) => {
    if (part.toLowerCase() === "tilt") {
      return (
        <Tooltip
          key={idx}
          variant="yellow"
          content={
            <div className="space-y-1">
              <div className="text-sm uppercase tracking-wide font-bold opacity-100">On Tilt</div>
              <p className="mt-5 text-[14px] leading-relaxed">
                A heightened emotional state (like frustration or euphoria) that leads to impulsive, undisciplined trading, ignoring your plan.
              </p>
              <p className="mt-5 text-[14px] leading-relaxed">
                Your brain has entered "fight or flight" mode. Your logical decision-making is offline, and you are no longer in control.
              </p>
              <p className="mt-6 text-[14px] font-bold leading-relaxed">What to do:</p>
              <ul className="mt-2 list-disc pl-4 text-[14px] opacity-100">
                <li>STOP TRADING IMMEDIATELY. This is the most important step.</li>
                <li>Step away from the screen. Reset your mind with a walk, exercise (particularly weight lifting), or another non-trading activity.</li>
                <li>Do not resume trading until you are calm and focused.</li>
              </ul>
            </div>
          }
        >
          tilt
        </Tooltip>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function getLossProjection({ assetClass, balance, riskPercent, riskMode, riskDollar, stopLoss, maxContracts, minContracts, multiplier, entryPrice, investmentAmount, investmentCapMode }) {
  let trades = [];
  let currentBalance = Number(balance);
  let tradeCount = 0;
  const limit = assetClass === "Stocks" ? 1 : 0;

while (currentBalance > (limit + 0.001) && tradeCount < 10000) {
    const riskBudget = riskMode === "$" ? riskDollar : (currentBalance * riskPercent / 100);
    
    let currentInvestCap = investmentAmount;
    if (investmentCapMode === "%") {
        currentInvestCap = currentBalance * (investmentAmount / 100);
    }

    let size = 0;

    if (assetClass === "Stocks") {
      // FIX: Apply the same Risk Budget / Distance logic here
      const riskSize = stopLoss > 0 ? (riskBudget / stopLoss) : 0;
      const cashSize = entryPrice > 0 ? (currentBalance / entryPrice) : 0;
      const capSize = (currentInvestCap > 0 && entryPrice > 0) ? (currentInvestCap / entryPrice) : 999999;
      size = Math.min(riskSize, cashSize, capSize);
    } else if (assetClass === "Options") {
      const riskPerContract = stopLoss * multiplier;
      const costPerContract = entryPrice * multiplier;
      const riskBasedSize = riskPerContract > 0 ? Math.floor(riskBudget / riskPerContract) : 0;
      const cashBasedSize = costPerContract > 0 ? Math.floor(currentBalance / costPerContract) : 0;
      const investmentCapSize = (currentInvestCap > 0 && costPerContract > 0) ? Math.floor(currentInvestCap / costPerContract) : 999999;
      size = Math.min(riskBasedSize, cashBasedSize, investmentCapSize);
    } else {
      if (stopLoss > 0 && multiplier > 0) size = riskBudget / (stopLoss * multiplier);
    }

    size = Math.min(size, maxContracts);
    
    if (assetClass === "Forex") size = Math.floor(size * 100) / 100;
    else if (assetClass === "Stocks") size = Math.floor(size * 10000) / 10000;
    else size = Math.floor(size);

    let usedSize = Math.max(size, minContracts);
    
    // Stop if you literally can't afford the minimum size anymore
    if (assetClass === "Options" && (usedSize * entryPrice * multiplier) > currentBalance) break;
    if (assetClass === "Stocks" && (usedSize * entryPrice) > currentBalance) break;

    let lossAmount = usedSize * (assetClass === "Stocks" ? stopLoss : (stopLoss * multiplier));
    lossAmount = Math.round(lossAmount * 100) / 100;

    if (lossAmount <= 0) break;

    const nextBalance = Math.round((currentBalance - lossAmount) * 100) / 100;
    if (nextBalance <= limit) {
      trades.push({
        trade: tradeCount + 1,
        balanceBefore: currentBalance,
        size: usedSize,
        loss: Math.round((currentBalance - limit) * 100) / 100, 
        balanceAfter: limit
      });
      break; 
    }

    trades.push({
      trade: tradeCount + 1,
      balanceBefore: currentBalance,
      size: usedSize,
      loss: lossAmount,
      balanceAfter: nextBalance
    });

    currentBalance = nextBalance;
    tradeCount++;
  }
  return { trades, tradeCount: trades.length };
}

function getUnitsMeta(symbol) {
  const s = String(symbol || "").toUpperCase();

  // Metals → show ounces
  if (s.startsWith("XAU")) return { perLot: 100,   label: "oz" };
  if (s.startsWith("XAG")) return { perLot: 5000,  label: "oz" };

  // Indices (CFDs) → 1 lot = 1 unit (show as units)
  if (isIndexSymbol(s)) return { perLot: 1, label: "units" };

  // Forex pairs → classic base units per 1.00 lot
  return { perLot: 100000, label: "units" };
}

// --- MAIN COMPONENT ---

const SettingsLabelWithTooltip = ({ label, tooltipContent }) => (
  <div className="flex items-center gap-1 mb-1">
    <label className="block font-bold text-gray-800 dark:text-gray-200">{label}</label>
    <Tooltip variant="neutral" backdrop content={tooltipContent}>
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-300 cursor-help">?</span>
    </Tooltip>
  </div>
);

// Add isAdmin = false to the props
export default function CalculateForm({ userId, accountId, isAdmin = false }) {
  const [assetClass, setAssetClass] = useState("Futures");
  const [settings, setSettings] = useState({ 
    "Risk Mode": "%", 
    "Stop Loss Mode": "$", 
    "Take Profit Mode": "$",
    "Pretrade Checklist": "Yes",
    "Calculator Only": false,
    "Minimum Contracts": 1 
  });
  const [journal, setJournal] = useState({ 
    Date: getTodayLocalISO(), 
    Timeframe: "",
    "Actual Balance": "",
    "Entry Strategy": "",
    "Exit Strategy": "",
    "Session": "",
    "Win": false,
    "Loss": false,
    "Needs Review": false,
    "Emotional Notes": "",
    "Target Reached": false
  });
  const [calculatedSummary, setCalculatedSummary] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingTrade, setSavingTrade] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [popupQueue, setPopupQueue] = useState([]);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [showZeroContractsPopup, setShowZeroContractsPopup] = useState(false);
  const [lastTotalRisk, setLastTotalRisk] = useState(0);
  const [lastActualRiskPercent, setLastActualRiskPercent] = useState(0);
  const [userPrefs, setUserPrefs] = useState(() => {
    // Initialize synchronously from localStorage to prevent balance flashing
    const cached = localStorage.getItem("isPrivacyMode");
    return cached === "true" ? { "Privacy Mode": true } : {};
  });
  const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;
  const [userFavorites, setUserFavorites] = useState({ futures: [], forex: [] });
  const [pendingWarning, setPendingWarning] = useState(null); 

  const [projectionOpen, setProjectionOpen] = useState(false);
  const [lossProjectionOpen, setLossProjectionOpen] = useState(false);
  const [showDaySeparators, setShowDaySeparators] = useState(false);
  const [winProjection, setWinProjection] = useState({ tradeCount: 0, trades: [] });
  const [lossProjection, setLossProjection] = useState({ tradeCount: 0, trades: [] });

  const popupsAllowedRef = useRef(false);
  const entryTimeRef = useRef();
  const exitTimeRef = useRef();
  const actualBalRef = useRef(null);
  const loadedFromServer = useRef(false);
  const [dailyGoalProg, setDailyGoalProg] = useState({ done: 0, target: 0, pct: 0 });
  const [dailyLossProg, setDailyLossProg] = useState({ lost: 0, limit: 0, pct: 0 });
  const [entryStrategies, setEntryStrategies] = useState([]);
  const [exitStrategies, setExitStrategies] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(true);

  const DEFAULT_TIME_ZONE = undefined;
  
  const { width, height } = useWindowSize();
  const [subStatus, setSubStatus] = useState("");

  const canCalculate = useMemo(() => {
      if (Number(settings["Running Balance"] || 0) <= 0) return false;
      // 1. Product is always required
      if (!settings["Product"]) return false;

      // 2. Risk Amount/Percentage must be > 0
      const riskVal = settings["Risk Mode"] === "$" 
        ? Number(settings["Risk Dollar Amount"]) 
        : Number(settings["Risk Percentage"]);
      if (!riskVal || riskVal <= 0) return false;

      // 3. Stop Loss must be present and > 0
      const stopLoss = Number(settings["Stop Loss"]);
      if (isNaN(stopLoss) || stopLoss <= 0) return false;

      // 4. Target (Take Profit) must be present and > 0
      const takeProfit = Number(settings["Take Profit"]);
      if (isNaN(takeProfit) || takeProfit <= 0) return false;

      // 4. Asset-specific or Mode-specific requirements
      // FIX: Ensures the math engine stops if Entry Price is missing in Price mode
      if (
        assetClass === "Options" || 
        assetClass === "Stocks" || 
        settings["Stop Loss Mode"] === "Price" || 
        settings["Take Profit Mode"] === "Price"
      ) {
        const entryPrice = Number(settings["Entry Price"]);
        if (isNaN(entryPrice) || entryPrice <= 0) return false;
      }

      // 5. OPTIONS SPECIFIC: If using Underlying Price for exits, Delta & Underlying Entry are REQUIRED
      if (assetClass === "Options") {
         if (settings["Stop Loss Mode"] === "Price" || settings["Take Profit Mode"] === "Price") {
             const underlying = Number(settings["Underlying Entry Price"]);
             const delta = Number(settings["Delta"]);
             if (!underlying || underlying <= 0 || !delta || delta <= 0) {
                 return false;
             }
         }
      }

      return true;
    }, [settings, assetClass]);

  useEffect(() => {
    if (!userId) return;
    const subscribersRef = collection(db, "subscribers");
    const q = query(subscribersRef, where("userId", "==", userId));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        setSubStatus(snapshot.docs[0].data().status || "");
      } else {
        setSubStatus("");
      }
    });
    return () => unsubscribe();
  }, [userId]);

  const isTrialing = subStatus === "TRIALING";

  const handleChecklistConfirm = async (selections) => {
    try {
      if (selections && typeof selections === "object") {
        await savePreTradeChecklist(userId, accountId, selections); 
      }
      const today = getTodayLocalISO();
      await updateUserSettings(userId, { PretradeChecklistShownDate: today });
      setUserPrefs((p) => ({ ...p, PretradeChecklistShownDate: today }));
    } catch (e) {
      console.error("[PTC] confirm error", e);
      setUserPrefs((p) => ({ ...p, PretradeChecklistShownDate: getTodayLocalISO() }));
    } finally {
      setShowChecklist(false);
    }
  };

// --- PRE-TRADE CHECKLIST TRIGGER ---
  useEffect(() => {
    const checkChecklistStatus = async () => {
      if (!userId) return;

      try {
        // Fetch the user's prefs to check the last date they saw the checklist
        const prefs = await getUserSettings(userId) || {};
        const today = getTodayLocalISO();

        setUserPrefs(prefs);

        // ✅ CACHE PRIVACY MODE HERE to prevent balance flashing on next load
        localStorage.setItem("isPrivacyMode", prefs["Privacy Mode"] === true ? "true" : "false");

        // If the checklist is enabled in settings AND hasn't been shown today, trigger it
        if (settings["Pretrade Checklist"] !== "No" && prefs?.PretradeChecklistShownDate !== today) {
          setShowChecklist(true);
        }
      } catch (error) {
        console.error("Error fetching checklist status:", error);
      }
    };

    // We wait for settings to be defined so we know if the user toggled the checklist to "No"
    if (settings && Object.keys(settings).length > 0) {
      checkChecklistStatus();
    }
  }, [userId, settings["Pretrade Checklist"]]);

  useEffect(() => {
    if (!userId) return;
    const ref = doc(db, "users", userId, "settings", "userSettings"); 
    const unsub = onSnapshot(ref, snap => {
      const favs = snap.exists() ? (snap.data().favorites || { futures: [], forex: [] }) 
                                 : { futures: [], forex: [] };
      setUserFavorites(favs);
    });
    return unsub;
  }, [userId]);

  const { favOpts, nonFavOpts } = useMemo(() => {
    const isFutures = assetClass === "Futures";
    const allOpts = isFutures ? FUTURES_PRODUCTS : [...FOREX_PAIRS, ...FOREX_INDICES];
    const favList = isFutures ? (userFavorites?.futures || []) : (userFavorites?.forex || []);
    
    // Create a Set of favorite codes for fast lookup
    const favSet = new Set(favList.map(v => String(v).toUpperCase()));

    const fav = [];
    const non = [];

    for (const opt of allOpts) {
      const val = Array.isArray(opt) ? opt[0] : opt;
      const label = Array.isArray(opt) ? opt[1] : opt;
      
      // FOR FUTURES: Extract "YM" from "YM - Dow - $5.00"
      // FOR FOREX: Use the value directly (e.g., "EURUSD")
      const checkVal = isFutures 
        ? val.split(" - ")[0].trim().toUpperCase() 
        : String(val).toUpperCase();
      
      if (favSet.has(checkVal)) {
        fav.push([val, label || val]);
      } else {
        non.push([val, label || val]);
      }
    }
    return { favOpts: fav, nonFavOpts: non };
  }, [assetClass, userFavorites]);

useEffect(() => {
    const load = async () => {
      try {
        const data = await getSettings(userId, accountId);
        if (data) {
          let loadedSettings = { ...data };

          // --- AUTOMATIC T+1 SETTLEMENT SWEEP (OVERNIGHT RESET) ---
          const today = getTodayLocalISO();
          const lastSettlement = data["LastSettlementDate"];

          // If it's a new day, sweep unsettled funds and reset the Beginning Balance
          if (today !== lastSettlement) {
            const currentRunning = Number(data["Running Balance"] || 0);
            const currentUnsettled = Number(data["Unsettled Balance"] || 0);

            // Only sweep if it's an asset class that settles (like Options/Stocks) 
            // or just to reset the Beginning Balance for the new day
            const newTotal = Math.round((currentRunning + currentUnsettled) * 100) / 100;

            loadedSettings = {
              ...loadedSettings,
              "Running Balance": newTotal,
              "Beginning Balance": newTotal, // Set today's starting line
              "Unsettled Balance": 0,        // Clear unsettled funds
              "LastSettlementDate": today    // Stamp today so it doesn't run again until tomorrow
            };

            // Save the newly settled balances to the database immediately
            await updateSettings(userId, accountId, loadedSettings);
          }
          // ---------------------------------------------------------

          setSettings(prev => ({ ...prev, ...loadedSettings }));
          loadedFromServer.current = true;
          
          if (loadedSettings.AssetClass) {
            setAssetClass(loadedSettings.AssetClass);
            if (loadedSettings.AssetClass === "Forex" && (!loadedSettings["Minimum Contracts"] || loadedSettings["Minimum Contracts"] > 0.9)) {
              setSettings(p => ({ ...p, "Minimum Contracts": 0.01 }));
            }
            if (loadedSettings.AssetClass === "Stocks" && (!loadedSettings["Minimum Contracts"] || loadedSettings["Minimum Contracts"] >= 1)) {
               setSettings(p => ({ ...p, "Minimum Contracts": 0.0001 }));
            }
          }
          setJournal(prev => ({
            ...prev,
            Product: loadedSettings.Product || prev.Product,
            Timeframe: loadedSettings["Default Timeframe"] || prev.Timeframe,
            "Entry Strategy": loadedSettings["Default Entry Strategy"] || "",
            "Exit Strategy": loadedSettings["Default Exit Strategy"] || "",
            "Session": loadedSettings["Default Session"] || "",
          }));
        }
        
        const rows = await fetchJournalRows(userId, accountId);
        const entries = [...new Set(rows.map(r => r["Entry Strategy"]).filter(Boolean))];
        const exits = [...new Set(rows.map(r => r["Exit Strategy"]).filter(Boolean))];
        setEntryStrategies(entries);
        setExitStrategies(exits);
      } catch (err) { console.error("Load error:", err); }
    };
    load();
  }, [userId, accountId]);

  useEffect(() => {
    if (!userId) return;
    refreshDailyBars();
    const initial = Number(settings["Initial Balance"] || 0);
    const goal = Number(settings["Account Goal"] || 0);
    const running = Number(settings["Running Balance"] || 0);
    let pct = 0;
    if (goal > initial) pct = ((running - initial) / (goal - initial)) * 100;
    setProgress(Math.max(0, Math.min(100, pct)));
  }, [userId, accountId, settings["Daily Goal"], settings["Daily Loss Limit"], settings["Running Balance"]]);


// --- Universal Auto-Save ---
  useEffect(() => {
    if (!loadedFromServer.current) return;

    const delayDebounceFn = setTimeout(() => {
      updateSettings(userId, accountId, {
        ...settings,
        AssetClass: assetClass
      }).catch(err => console.error("Auto-save failed:", err));
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [settings, assetClass, userId, accountId]);

  // Auto-calculate when required fields are populated
// Auto-calculate when required fields are populated
  useEffect(() => {
    if (canCalculate) {
      const delayDebounceFn = setTimeout(() => {
        handleCalculate(false, true); 
      }, 300); 
      
      return () => clearTimeout(delayDebounceFn);
    } else {
      setCalculatedSummary(null); 
    }
  }, [
    canCalculate, 
    settings["Product"], 
    settings["Risk Mode"], 
    settings["Risk Dollar Amount"], 
    settings["Risk Percentage"], 
    settings["Stop Loss"], 
    settings["Take Profit"], 
    settings["Entry Price"], 
    settings["Running Balance"],
    settings["Account Goal"],
    settings["Investment Amount"],
    settings["Investment Cap Mode"],
    assetClass,
    // --- ADD THESE NEW DEPENDENCIES ---
    settings["Option Type"],
    settings["Delta"],
    settings["Underlying Entry Price"],
    settings["Stop Loss Mode"],
    settings["Take Profit Mode"]
  ]);


  const refreshDailyBars = async () => {
    const rows = await fetchJournalRows(userId, accountId);
    const today = getTodayLocalISO();
    const todayRows = rows.filter(r => (r.Date || "").startsWith(today));
    const net = todayRows.reduce((sum, r) => sum + (Number(r["Actual Reward"]) || 0), 0);
    const goal = Number(settings["Daily Goal"] || 0);
    const limit = Number(settings["Daily Loss Limit"] || 0);
    const done = Math.max(0, net);
    const lost = Math.max(0, -net);
    setDailyGoalProg({ done, target: goal, pct: goal > 0 ? (done/goal)*100 : 0 }); 
    setDailyLossProg({ lost, limit, pct: limit > 0 ? (lost/limit)*100 : 0 });
    return { net, goal, limit }; 
  };


  // --- Auto-sync Size, Entry, and Exit Prices to Journal ---
  useEffect(() => {
if (!autoSyncEnabled) return; 

    setJournal((prev) => {
      const next = { ...prev };
      let changed = false;

    // 1. Sync Size (Always keep in sync with the calculator when autoSyncEnabled is true)
      if (calculatedSummary && calculatedSummary.size !== undefined) {
        if (next["Size"] !== calculatedSummary.size) {
          next["Size"] = calculatedSummary.size;
          changed = true;
        }
      }

      // 2. Sync Entry Price
      const entryPrice = Number(settings["Entry Price"] || 0);
      if (settings["Entry Price"] !== undefined && settings["Entry Price"] !== "") {
        if (next["Entry Price"] !== settings["Entry Price"]) {
          next["Entry Price"] = settings["Entry Price"];
          changed = true;
        }
      }

      // 3. Sync Exit Price (Calculate Estimated Target)
      const tpVal = Number(settings["Take Profit"] || 0);
      let estTargetPrice = "";

      if (entryPrice > 0 && tpVal > 0) {
        if (assetClass === "Options") {
          if (calculatedSummary?.takeProfitPremium > 0) {
            estTargetPrice = Number(calculatedSummary.takeProfitPremium.toFixed(2));
          }
        } else if (settings["Take Profit Mode"] === "Price") {
          estTargetPrice = tpVal;
        } else {
          if (assetClass === "Stocks") {
            const addAmt = settings["Take Profit Mode"] === "Ticks" ? tpVal * 0.01 : tpVal;
            estTargetPrice = Number((entryPrice + addAmt).toFixed(2));
          } else if (assetClass === "Futures") {
            const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
            estTargetPrice = entryPrice + tpVal * tickSize;
          } else if (assetClass === "Forex") {
            const pipSize = getPipSizeForSymbol(settings["Product"]);
            const isIdx = isIndexSymbol(settings["Product"]);
            let ptsDist = tpVal;
            if (settings["Take Profit Mode"] === "Ticks") {
              ptsDist = isIdx ? ptsDist / 100 : ptsDist / 10; 
            }
            estTargetPrice = Number((entryPrice + (ptsDist * pipSize)).toFixed(isIdx ? 2 : 5));
          }
        }
      }

      if (estTargetPrice !== "") {
        if (next["Exit Price"] !== estTargetPrice) {
          next["Exit Price"] = estTargetPrice;
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [
    settings["Entry Price"],
    settings["Take Profit"],
    settings["Take Profit Mode"],
    settings["Product"],
    assetClass,
    calculatedSummary,
    autoSyncEnabled
  ]);

const handleAssetClassChange = async (ac) => {
    setAutoSyncEnabled(true);
    setAssetClass(ac);
    
    // Determine the default minimum size based on the new asset class
    const newMin = ac === "Forex" ? 0.01 : (ac === "Stocks" ? 0.0001 : 1);
    
    const updatedSettings = { 
      ...settings, 
      AssetClass: ac, 
      Product: "", 
      "Minimum Contracts": newMin,
      ...(ac === "Stocks" && { "Minimum Shares": 0.0001 }),
      "Minimum Contracts": newMin,
      // RESET PRICING FIELDS ON ASSET SWITCH
      "Entry Price": "",
      "Stop Loss": "",
      "Take Profit": "",
      // Set defaults for specific asset modes
      ...(ac === "Stocks" && {
         "Stop Loss Mode": "Points",
         "Take Profit Mode": "Points"
      }),
      ...(ac === "Options" && {
        "Stop Loss Mode": "Premium",
        "Take Profit Mode": "Premium",
        "Delta": "",
        "Underlying Entry Price": ""
      })
    };

    setSettings(updatedSettings);
    setCalculatedSummary(null); // Clear the previous calculation results
  
    try {
      // Persist the asset class change and cleared fields to the database
      await updateSettings(userId, accountId, updatedSettings);
    } catch (err) {
      console.error("Failed to save platform preference:", err);
    }
};

  function getDailyGoalMessage(timeZone = DEFAULT_TIME_ZONE) {
    const now = new Date();
    const weekday = new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      ...(timeZone ? { timeZone } : {})
    }).format(now);

    if (weekday === 'Sunday' || weekday === 'Monday') return "You are off to a great start of the week. Keep the momentum, consider stopping for the day and enjoy the things that matter outside of trading!";
    if (weekday === 'Tuesday' || weekday === 'Wednesday') return "You have the rest of the week!! The market will always be there, but this moment in time will be gone forever. Don't forget to enjoy the things that matter outside of trading!";
    if (weekday === 'Thursday') return "Go and enjoy the rest of your day!";
    if (weekday === 'Friday') return "Today is Capital Preservation Friday! Consider stopping for the day and get ready for a great weekend!!";
    return "Go and enjoy the rest of your day!";
  }

// --- Define which fields should be saved as numbers ---
  const numericFields = [
    "Initial Balance",
    "Account Goal",
    "Beginning Balance",
    "Running Balance",
    "Risk Dollar Amount",
    "Risk Percentage",
    "Stop Loss",
    "Take Profit",
    "Entry Price",
    "Investment Amount",
    "Underlying Entry Price",
    "Delta",
    "Daily Goal",
    "Daily Loss Limit",
    "Maximum Loss Limit"
  ];

  const handleSettingChange = (key, val) => {

    setAutoSyncEnabled(true);
    setSettings(prev => {
      // Safely parse numbers so the database doesn't reject string inputs
      const parsedVal = numericFields.includes(key) ? (val === "" ? "" : Number(val)) : val;
      const next = { ...prev, [key]: parsedVal };

      // --- CLEAR VALUES ON MODE SWITCH ---
      if (key === "Stop Loss Mode") next["Stop Loss"] = "";
      if (key === "Take Profit Mode") next["Take Profit"] = "";
      
      // Balance synchronization logic
      if (key === "Initial Balance" && !prev["Running Balance"]) {
        next["Running Balance"] = parsedVal;
        next["Beginning Balance"] = parsedVal;
      }
      return next;
    });
    
    setCalculatedSummary(null);

    // Prepare override for the summary engine to reflect the cleared/parsed values immediately
    const overrideVal = numericFields.includes(key) ? (val === "" ? "" : Number(val)) : val;
    const summaryOverride = { [key]: overrideVal };
    if (key === "Stop Loss Mode") summaryOverride["Stop Loss"] = "";
    if (key === "Take Profit Mode") summaryOverride["Take Profit"] = "";

    runSummaryOnly(summaryOverride);
  };

  const handleJournalChange = (key, val) => setJournal(prev => ({ ...prev, [key]: val }));

  const calculateProjections = (s, assetClassOverride) => {
      const ac = assetClassOverride || assetClass;
      
      const balance = Number(s["Running Balance"] ?? s["Initial Balance"] ?? 0);
      const riskVal = Number(s["Risk Mode"] === "$" ? s["Risk Dollar Amount"] : s["Risk Percentage"]);
      const riskDollar = s["Risk Mode"] === "$" ? riskVal : (balance * (riskVal / 100));
      
      const stopLoss = Number(s["Stop Loss"] || 0);
      const takeProfit = Number(s["Take Profit"] || 0);
      
      let minSize = 0;
      let maxSize = 0;

      if (ac === "Forex") {
        minSize = Number(s["Min Lot Size"] || s["Minimum Contracts"] || 0.01);
        maxSize = Number(s["Max Lot Size"] || s["Maximum Contracts"] || 1000);
      } else if (ac === "Stocks") {
        minSize = Number(s["Minimum Shares"] ?? 0.0001);
        maxSize = Number(s["Maximum Shares"] ?? s["Maximum Contracts"] ?? 1000);
      } else {
        minSize = Number(s["Minimum Contracts"] || 1);
        maxSize = Number(s["Maximum Contracts"] || 1000);
      }
      
      const entryPrice = Number(s["Entry Price"] || 0);
      const product = s["Product"] || "";
      const multiplier = getMultiplier(ac, product, s);

      let calculatedStopAmt = stopLoss; 
      let calculatedTpAmt = takeProfit;
      let calculatedSize = 0;
      let maxAllowableStopPrice = 0;

      // Variables to pass to summary specifically for Options
      let stopLossPrice = 0;
      let takeProfitPrice = 0;
      let stopLossPremium = 0;
      let takeProfitPremium = 0;

      if (ac === "Options") {
          const underlyingEntry = Number(s["Underlying Entry Price"] || 0);
          const optDelta = Number(s["Delta"] || 0);
          const optType = s["Option Type"] || "Call";

          let stopPremium = 0;
          let tpPremium = 0;
          let stopPrice = 0;
          let tpPrice = 0;

          // --- DYNAMIC STOP LOSS LOGIC ---
          if (s["Stop Loss Mode"] === "Price") {
              stopPrice = stopLoss; 
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diff = stopPrice - underlyingEntry;
                  if (optType === "Put") diff = underlyingEntry - stopPrice;
                  stopPremium = entryPrice + (diff * optDelta);
              }
          } else if (s["Stop Loss Mode"] === "Ticks") {
              const tickDist = stopLoss * 0.01;
              stopPremium = entryPrice - tickDist;
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diffPrem = stopPremium - entryPrice;
                  stopPrice = optType === "Put" ? underlyingEntry - (diffPrem / optDelta) : underlyingEntry + (diffPrem / optDelta);
              }
          } else {
              stopPremium = stopLoss;
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diffPrem = stopLoss - entryPrice;
                  stopPrice = optType === "Put" ? underlyingEntry - (diffPrem / optDelta) : underlyingEntry + (diffPrem / optDelta);
              }
          }

          // --- DYNAMIC TAKE PROFIT LOGIC ---
          if (s["Take Profit Mode"] === "Price") {
              tpPrice = takeProfit;
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diff = tpPrice - underlyingEntry;
                  if (optType === "Put") diff = underlyingEntry - tpPrice;
                  tpPremium = entryPrice + (diff * optDelta);
              }
          } else if (s["Take Profit Mode"] === "Ticks") {
              const tickDist = takeProfit * 0.01;
              tpPremium = entryPrice + tickDist;
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diffPrem = tpPremium - entryPrice;
                  tpPrice = optType === "Put" ? underlyingEntry - (diffPrem / optDelta) : underlyingEntry + (diffPrem / optDelta);
              }
          } else {
              tpPremium = takeProfit;
              if (underlyingEntry > 0 && optDelta > 0) {
                  let diffPrem = takeProfit - entryPrice;
                  tpPrice = optType === "Put" ? underlyingEntry - (diffPrem / optDelta) : underlyingEntry + (diffPrem / optDelta);
              }
          }

          calculatedStopAmt = Math.max(0, Number(stopPremium.toFixed(2)));
          calculatedTpAmt = Math.max(0, Number(tpPremium.toFixed(2)));
          stopLossPremium = calculatedStopAmt;
          takeProfitPremium = calculatedTpAmt;
          stopLossPrice = stopPrice;
          takeProfitPrice = tpPrice;

        const diff = calculatedStopAmt > 0 ? (entryPrice - calculatedStopAmt) : entryPrice;
        const safeDiff = Math.round(diff * 10000) / 10000;
        const riskPerContract = safeDiff * multiplier;
        const riskBasedSize = riskPerContract > 0 ? Math.floor(riskDollar / riskPerContract) : 0;

        const costPerContract = entryPrice * multiplier;
        const cashBasedSize = (costPerContract > 0 && balance > 0) ? Math.floor(balance / costPerContract) : 9999;

        const investLimitValue = Number(s["Investment Amount"] || 0);
        const investLimitMode = s["Investment Cap Mode"] || "$";
        let investLimitDollar = investLimitValue;

        if (investLimitMode === "%" && balance > 0) {
            investLimitDollar = balance * (investLimitValue / 100);
        }

        let investCapSize = 9999;
        if (investLimitDollar > 0 && costPerContract > 0) {
            investCapSize = Math.floor(investLimitDollar / costPerContract);
        }

        calculatedSize = Math.min(riskBasedSize, cashBasedSize, investCapSize);
        const activeSize = calculatedSize > 0 ? calculatedSize : minSize;
        if (activeSize > 0 && entryPrice > 0) {
            const totalCostOfPosition = entryPrice * activeSize * 100;
            const effectiveRiskDollar = Math.min(riskDollar, totalCostOfPosition);
            
            const maxRiskPerShare = effectiveRiskDollar / (activeSize * 100);
            const rawStopPrice = entryPrice - maxRiskPerShare;
            
            maxAllowableStopPrice = Math.max(0, Math.ceil(rawStopPrice * 100) / 100);
        }
      }
      // Find the "else if (ac === "Stocks")" block (around line 954) and replace it with:
        else if (ac === "Stocks") {
         const slMode = s["Stop Loss Mode"] || "Points";
         const tpMode = s["Take Profit Mode"] || "Points";

         // 1. Calculate the dollar distance for the stop and target
         calculatedStopAmt = (slMode === "Price" && entryPrice > 0) 
            ? Math.abs(entryPrice - stopLoss) 
            : (slMode === "Ticks" ? stopLoss * 0.01 : stopLoss); // <-- UPDATED

         calculatedTpAmt = (tpMode === "Price" && entryPrice > 0) 
            ? Math.abs(takeProfit - entryPrice) 
            : (tpMode === "Ticks" ? takeProfit * 0.01 : takeProfit); // <-- UPDATED

         if (entryPrice > 0 && calculatedStopAmt > 0) {
            // 2. Risk-Based Sizing: How many shares to lose exactly 'riskDollar'?
            const riskBasedSize = riskDollar / calculatedStopAmt;
            
            // 3. Buying Power Limit: How many shares can I actually afford?
            const cashBasedSize = balance / entryPrice;

            // 4. Investment Cap Limit: Optional hard dollar limit
            const investLimitValue = Number(s["Investment Amount"] || 0);
            const investLimitMode = s["Investment Cap Mode"] || "$";
            let investLimitDollar = investLimitValue;
            if (investLimitMode === "%" && balance > 0) {
                investLimitDollar = balance * (investLimitValue / 100);
            }
            const investCapSize = (investLimitDollar > 0) ? (investLimitDollar / entryPrice) : 999999;

            // Take the smallest of the three to be safe
            calculatedSize = Math.floor(Math.min(riskBasedSize, cashBasedSize, investCapSize) * 10000) / 10000;
         }
      }      else {
        // --- NEW: FUTURES & FOREX LOGIC (Price vs distance) ---
        const slMode = s["Stop Loss Mode"] || (ac === "Futures" ? "Ticks" : "Pips");
        const tpMode = s["Take Profit Mode"] || (ac === "Futures" ? "Ticks" : "Pips");
        
        let slDist = 0;
        let tpDist = 0;

        // Calculate Stop Loss distance (how many ticks/pips away)
        if (slMode === "Price" && entryPrice > 0) {
          const rawDist = Math.abs(entryPrice - stopLoss);
          if (ac === "Futures") {
            const ts = getTickSizeForProduct(product) || 0.25;
            slDist = ts > 0 ? (rawDist / ts) : 0;
          } else {
            const ps = getPipSizeForSymbol(product);
            slDist = ps > 0 ? (rawDist / ps) : 0;
          }
        } else {
          slDist = stopLoss; 
          if (ac === "Forex" && slMode === "Ticks") {
              slDist = isIndexSymbol(product) ? slDist / 100 : slDist / 10; 
          }
        }

        // Calculate Take Profit distance (how many ticks/pips away)
        if (tpMode === "Price" && entryPrice > 0) {
          const rawDist = Math.abs(entryPrice - takeProfit);
          if (ac === "Futures") {
            const ts = getTickSizeForProduct(product) || 0.25;
            tpDist = ts > 0 ? (rawDist / ts) : 0;
          } else {
            const ps = getPipSizeForSymbol(product);
            tpDist = ps > 0 ? (rawDist / ps) : 0;
          }
        } else {
          tpDist = takeProfit; 
          if (ac === "Forex" && tpMode === "Ticks") {
              tpDist = isIndexSymbol(product) ? tpDist / 100 : tpDist / 10; 
          }
        }

        calculatedStopAmt = slDist;
        calculatedTpAmt = tpDist;

        if (calculatedStopAmt > 0 && multiplier > 0) {
          let raw = riskDollar / (calculatedStopAmt * multiplier);
          calculatedSize = ac === "Forex" ? (Math.floor(raw * 100) / 100) : Math.floor(raw);
        }
      }

      calculatedSize = Math.max(minSize, Math.min(maxSize, calculatedSize));

      let priceMoveRequired = 0;
      const delta = Number(s["Delta"] || 0);

      if (ac === "Options" && delta > 0) {
          const premiumChangeNeeded = calculatedTpAmt - entryPrice; 
          priceMoveRequired = premiumChangeNeeded / delta;
      }
      
      if (ac === "Options") {
         if ((entryPrice * multiplier) > balance) calculatedSize = 0;
      }
      else if (ac === "Stocks") {
          if (entryPrice > 0 && (minSize * entryPrice) > riskDollar) calculatedSize = 0;
      } else {
          // FIX: Use calculatedStopAmt (which is now correctly the distance in ticks/pips)
          let minRisk = minSize * calculatedStopAmt * multiplier;
          if (minRisk > riskDollar) calculatedSize = 0;
      }

      return {
          ac, balance, riskDollar, calculatedSize, minSize, maxSize, 
          calculatedStopAmt, calculatedTpAmt, entryPrice, stopLoss, takeProfit, multiplier,
          maxAllowableStopPrice, priceMoveRequired, delta,
          stopLossPrice, takeProfitPrice, stopLossPremium, takeProfitPremium,
          // Add these for the new summary rows
          slMode: s["Stop Loss Mode"],
          tpMode: s["Take Profit Mode"]
      };
  };

const handleCalculate = async (save = true, silent = false) => {
    // [!code highlight] FIX: Only block the UI if we are explicitly saving to the DB. 
    // This prevents Auto-Calc from disabling the button while you are typing/testing.
    if (save) setSavingSettings(true);
    
    try {
      if (save) await updateSettings(userId, accountId, { ...settings, AssetClass: assetClass });

      const calc = calculateProjections(settings, assetClass);
      
      let totalRisk = 0;
      let targetReward = 0;
      const validSize = calc.calculatedSize > 0 ? calc.calculatedSize : calc.minSize;

      if (assetClass === "Stocks") {
        totalRisk = validSize * calc.calculatedStopAmt; 
        targetReward = validSize * calc.calculatedTpAmt;
      } else if (assetClass === "Options") {
        const riskPerShare = calc.stopLossPremium > 0 ? (calc.entryPrice - calc.stopLossPremium) : calc.entryPrice;
        const rewardPerShare = Math.max(0, calc.takeProfitPremium - calc.entryPrice);
        totalRisk = validSize * riskPerShare * 100;
        targetReward = validSize * rewardPerShare * 100;
      } else {
        // FIX: Use 'calculatedStopAmt' and 'calculatedTpAmt' (which represent distance)
        // instead of 'stopLoss' and 'takeProfit' (which might be absolute prices)
        totalRisk = validSize * calc.calculatedStopAmt * calc.multiplier;
        targetReward = validSize * calc.calculatedTpAmt * calc.multiplier;
      }

      let maxStopLossSizeForMin = 0;
      if (assetClass !== "Stocks" && validSize > 0 && calc.multiplier > 0) {
          maxStopLossSizeForMin = Math.floor(calc.riskDollar / (validSize * calc.multiplier));
      }

      let riskRewardRatio = "";
      if (assetClass === "Stocks") {
         if (calc.calculatedStopAmt > 0 && calc.calculatedTpAmt > 0) {
             const ratio = calc.calculatedTpAmt / calc.calculatedStopAmt;
             riskRewardRatio = `${ratio % 1 === 0 ? ratio : ratio.toFixed(2)}:1`;
         }
      } else if (assetClass === "Options") {
         const riskPerShare = calc.stopLossPremium > 0 ? (calc.entryPrice - calc.stopLossPremium) : calc.entryPrice;
         const rewardPerShare = Math.max(0, calc.takeProfitPremium - calc.entryPrice);
         if (riskPerShare > 0 && rewardPerShare > 0) {
             const ratio = rewardPerShare / riskPerShare;
             riskRewardRatio = `${ratio % 1 === 0 ? ratio : ratio.toFixed(2)}:1`;
         }
        } else {
         // FIX: Use the calculated distances (stopAmt / tpAmt) instead of absolute prices
         if (calc.calculatedStopAmt > 0 && calc.calculatedTpAmt > 0) {
             const ratio = calc.calculatedTpAmt / calc.calculatedStopAmt;
             riskRewardRatio = `${ratio % 1 === 0 ? ratio : ratio.toFixed(2)}:1`;
         }
      }

      setCalculatedSummary({
        size: calc.calculatedSize,
        riskDollar: calc.riskDollar,
        totalRisk,
        targetReward,
        unit: getUnitLabel(assetClass),
        multiplier: calc.multiplier,
        pricePerTick: calc.multiplier,   
        maxStopLossSizeForMin,      
        minContracts: calc.minSize,      
        percentOfBalance: calc.riskDollar, 
        stopLossVal: calc.stopLoss,
        takeProfitVal: calc.takeProfit,
        stopAmt: calc.calculatedStopAmt,
        tpAmt: calc.calculatedTpAmt,
        entryPrice: calc.entryPrice, 
        rr: riskRewardRatio,
        riskRewardRatio: riskRewardRatio,
        // [!code highlight] Add these 4 lines
        stopLossPrice: calc.stopLossPrice,
        takeProfitPrice: calc.takeProfitPrice,
        stopLossPremium: calc.stopLossPremium,
        takeProfitPremium: calc.takeProfitPremium
      });

      if (calc.calculatedSize === 0) {
         setLastTotalRisk(totalRisk);
         const bal = Number(settings["Running Balance"] || settings["Initial Balance"] || 1);
         const pct = (totalRisk / bal) * 100;
         setLastActualRiskPercent(pct.toFixed(2));
         
         if (!silent) {
            setShowZeroContractsPopup(true);
         }
      }

      const investmentCap = Number(settings["Investment Amount"] || 0);
      const investMode = settings["Investment Cap Mode"] || "$"

      // Calculate the move distance (delta) for projections
      let projStop = calc.calculatedStopAmt;
      let projTp = calc.calculatedTpAmt;

      if (assetClass === "Options") {
          const stopPrem = Number(calc.stopLossPremium || 0);
          const tpPrem = Number(calc.takeProfitPremium || 0);
          
          projStop = stopPrem > 0 ? Math.abs(calc.entryPrice - stopPrem) : calc.entryPrice;
          projTp = tpPrem > calc.entryPrice ? Math.abs(tpPrem - calc.entryPrice) : 0;
      } 

      // Update the balance property to use Total Equity
      const totalEquityForProjection = Number(settings["Beginning Balance"] || settings["Running Balance"] || 0);


      // // === NEW: CALCULATE ESTIMATED TARGET PRICE FOR JOURNAL PRE-FILL ===
      // let estTargetPrice = "";
      // if (calc.entryPrice > 0 && calc.takeProfit > 0) {
      //     if (assetClass === "Options") {
      //         estTargetPrice = calc.takeProfitPremium > 0 ? Number(calc.takeProfitPremium.toFixed(2)) : "";
      //     } else if (settings["Take Profit Mode"] === "Price") {
      //         estTargetPrice = Number(calc.takeProfit);
      //     } else {
      //         if (assetClass === "Stocks") {
      //             const addAmt = settings["Take Profit Mode"] === "Ticks" ? calc.takeProfit * 0.01 : calc.takeProfit;
      //             estTargetPrice = Number((calc.entryPrice + addAmt).toFixed(2));
      //         } else if (assetClass === "Futures") {
      //             const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
      //             estTargetPrice = calc.entryPrice + (calc.takeProfit * tickSize);
      //         } else if (assetClass === "Forex") {
      //             const pipSize = getPipSizeForSymbol(settings["Product"]);
      //             const isIdx = isIndexSymbol(settings["Product"]);
      //             let ptsDist = calc.takeProfit;
      //             if (settings["Take Profit Mode"] === "Ticks") {
      //                 ptsDist = isIdx ? ptsDist / 100 : ptsDist / 10;
      //             }
      //             estTargetPrice = Number((calc.entryPrice + (ptsDist * pipSize)).toFixed(isIdx ? 2 : 5));
      //         }
      //     }
      // }
      // // ==================================================================


      setWinProjection(getCompoundingProjection({
        assetClass, 
        balance: totalEquityForProjection, 
        goal: Number(settings["Account Goal"] || 0), 
        riskPercent: Number(settings["Risk Percentage"] || 0), 
        riskMode: settings["Risk Mode"], 
        riskDollar: Number(settings["Risk Dollar Amount"] || 0),
        stopLoss: projStop, 
        takeProfit: projTp, 
        maxContracts: calc.maxSize, 
        minContracts: calc.minSize, 
        multiplier: calc.multiplier, 
        entryPrice: calc.entryPrice,
        investmentAmount: investmentCap, 
        investmentCapMode: investMode
      }));

      setLossProjection(getLossProjection({
        assetClass, 
        balance: totalEquityForProjection, 
        riskPercent: Number(settings["Risk Percentage"] || 0), 
        riskMode: settings["Risk Mode"], 
        riskDollar: Number(settings["Risk Dollar Amount"] || 0),
        stopLoss: projStop, 
        maxContracts: calc.maxSize, 
        minContracts: calc.minSize, 
        multiplier: calc.multiplier, 
        entryPrice: calc.entryPrice,
        investmentAmount: investmentCap,
        investmentCapMode: investMode 
      }));

      // // === UPDATED: PRE-FILL ENTRY AND EXIT PRICES ===
      // setJournal(prev => ({
      //       ...prev, 
      //       "Actual Balance": "", "Win": false, "Loss": false, "Notes": "", "Movement": "", "Potential": "",
      //       "Size": calc.calculatedSize ?? "", "Did I follow my Rules?": "", "Mood_Mindset": "", "Mood": "", 
      //       "Lesson": "", "LogLesson": false, "Position": "", "Entry Time": "", "Exit Time": "",
      //       "Entry Price": calc.entryPrice || "",     // <--- Pulls Entry from Setup
      //       "Exit Price": estTargetPrice || "",       // <--- Pulls calculated Target
      //       "Grade": "", "Links_Screenshots": "",
      //       "Session": settings["Default Session"] || "",
      //       "Timeframe": settings["Default Timeframe"] || "",
      //       "Entry Strategy": settings["Default Entry Strategy"] || "",
      //       "Exit Strategy": settings["Default Exit Strategy"] || "",
      //     }));

    } catch (e) { 
        console.error(e); 
    } finally { 
        if (save) setSavingSettings(false); 
    }
  };

  const TICK_SIZES = {
    YM: 1, MYM: 1, ES: 0.25, MES: 0.25, NQ: 0.25, MNQ: 0.25,
    RTY: 0.1, M2K: 0.1, GC: 0.1, MGC: 0.1, CL: 0.01, MCL: 0.01,
    NG: 0.001, MNG: 0.001, SI: 0.005, SIL: 0.005, BTC: 5, MBT: 5,
  };

  function computeMovementAbs(assetClass, productStr, entryPriceRaw, exitPriceRaw) {
  const entryStr = entryPriceRaw != null ? String(entryPriceRaw).trim() : "";
  const exitStr  = exitPriceRaw  != null ? String(exitPriceRaw).trim()  : "";
  if (!entryStr || !exitStr) return null;
  
  const entry = Number(entryStr);
  const exit  = Number(exitStr);
  if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;
  
  const diff = Math.abs(exit - entry);

  if (assetClass === "Futures") {
      const tickSize = getTickSizeForProduct(productStr) || 0.25;
      return Math.round(diff / tickSize);
  }
  if (assetClass === "Forex") {
      const pipSize = getPipSizeForSymbol(productStr);
      return Math.round((diff / pipSize) * 100) / 100; // Round to 2 decimals
  }
  // For Stocks (Points) and Options (Premium)
  return Math.round(diff * 100) / 100; 
}

  function getTickSizeForProduct(productStr) {
    if (!productStr) return null;
    const firstPart = String(productStr).split(" - ")[0];
    const sym = firstPart.split("·")[0].trim().toUpperCase();
    return TICK_SIZES[sym] ?? null;
  }

  function computeTicksFromPricesAbs(productStr, entryPriceRaw, exitPriceRaw) {
    const tickSize = getTickSizeForProduct(productStr);
    const entryStr = entryPriceRaw != null ? String(entryPriceRaw).trim() : "";
    const exitStr  = exitPriceRaw  != null ? String(exitPriceRaw).trim()  : "";
    if (!entryStr || !exitStr || !tickSize || tickSize <= 0) return null;
    const entry = Number(entryStr);
    const exit  = Number(exitStr);
    if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;
    const diff = Math.abs(exit - entry);
    return Math.round(diff / tickSize);
  }

  const runSummaryOnly = (override = {}) => {
    const s = { ...settings, ...override };
    const calc = calculateProjections(s, assetClass);
    const investVal = Number(s["Investment Amount"] || 0);
    const investMode = s["Investment Cap Mode"] || "$";
    const investmentCap = (investMode === "%" && calc.balance > 0) 
        ? calc.balance * (investVal / 100) 
        : investVal;
    // Calculate the move distance (delta) for projections
    let projStop = calc.stopLoss;
    let projTp = calc.takeProfit;

    if (assetClass === "Options") {
       projStop = Math.abs(calc.entryPrice - calc.stopLossPremium);
       projTp = Math.abs(calc.takeProfitPremium - calc.entryPrice);
    } else if (assetClass === "Stocks") {
       projStop = calc.calculatedStopAmt;
       projTp = calc.calculatedTpAmt;
    }

// Update the balance property to use Total Equity
    const totalEquityForProjection = Number(s["Beginning Balance"] || s["Running Balance"] || 0);

    setWinProjection(getCompoundingProjection({
        assetClass, 
        balance: totalEquityForProjection, // <-- UPDATED THIS LINE
        goal: Number(s["Account Goal"] || 0), 
        riskPercent: Number(s["Risk Percentage"] || 0), 
        riskMode: s["Risk Mode"], 
        riskDollar: calc.riskDollar,
        stopLoss: projStop, 
        takeProfit: projTp,
        maxContracts: calc.maxSize, 
        minContracts: calc.minSize, 
        multiplier: calc.multiplier, 
        entryPrice: calc.entryPrice,
        investmentAmount: investVal, 
        investmentCapMode: investMode
    }));

    setLossProjection(getLossProjection({
        assetClass, 
        balance: totalEquityForProjection, // <-- UPDATED THIS LINE
        riskPercent: Number(s["Risk Percentage"] || 0), 
        riskMode: s["Risk Mode"], 
        riskDollar: calc.riskDollar,
        stopLoss: projStop,
        maxContracts: calc.maxSize, 
        minContracts: calc.minSize, 
        multiplier: calc.multiplier, 
        entryPrice: calc.entryPrice,
        investmentAmount: investVal,
        investmentCapMode: investMode
    }));

    };

  const handleReplenish = async () => {
      setSavingSettings(true);
      try {
        // Your Total Equity is stored in Beginning Balance
        const currentTotalEquity = Number(settings["Beginning Balance"] || settings["Running Balance"] || 0);
        
        const nextSettings = {
          ...settings,
          "Running Balance": currentTotalEquity,
          "Unsettled Balance": 0
        };
        
        // Update Firebase
        await updateSettings(userId, accountId, nextSettings);
        
        // Update local state instantly
        setSettings(nextSettings);
        runSummaryOnly({ "Running Balance": currentTotalEquity, "Unsettled Balance": 0 });
        
      } catch (error) {
        console.error("Failed to replenish funds:", error);
      } finally {
        setSavingSettings(false);
      }
    };  

const handleSaveTrade = async () => {
  const errors = [];
  const abRaw = journal["Actual Balance"];
  const abIsEmpty = abRaw === "" || abRaw === null || abRaw === undefined;
  const abNum = parseCurrency(abRaw);
  
  if (abIsEmpty || !Number.isFinite(abNum)) errors.push("- Actual Balance");
  if (!journal["Date"]) errors.push("- Date");
  if (!settings["Product"]) errors.push("- Product");
  if (!journal["Did I follow my Rules?"]) errors.push("- Did I follow my Rules?");
  if (!journal["Win"] && !journal["Loss"]) errors.push("- Win/Loss");

  // --- OPTIONS SPECIFIC VALIDATION ---
  if (assetClass === "Options") {
      // By checking the value directly in the journal state OR setting it to Call safely
      const finalOptionType = journal["Option Type"] || settings["Option Type"] || "Call";
      
      // We no longer need to error check Option Type since it defaults safely to "Call"
      if (!journal["Strike"]) errors.push("- Strike Price");
      if (!journal["Expiration"]) errors.push("- Expiration Date");
  }
  
  const rawLesson = journal["Lesson"];
  const lessonString = (typeof rawLesson === 'string' ? rawLesson : String(rawLesson || "")).trim();

  const rawSize = journal["Size"];
  const hasManualSize = rawSize !== "" && rawSize !== null && rawSize !== undefined;
  const calculatedVal = Number(calculatedSummary?.contracts ?? calculatedSummary?.size ?? 0);
  const effectiveContracts = hasManualSize ? Number(rawSize) : calculatedVal;

  if (!Number.isFinite(effectiveContracts) || effectiveContracts <= 0) {
     errors.push(`- # of ${getUnitLabel(assetClass)}`);
  }
  if (!calculatedSummary) errors.push("- Click the Calculate button before saving");

  if (errors.length > 0) {
    setMissingFields(errors);
    return;
  }

  setSavingTrade(true);
  popupsAllowedRef.current = true;

  try {
    const tradeNum = await getNextTradeNumber(userId, accountId);
    
    // 1. GET PREVIOUS BALANCES
    const prevAvailable = Number(settings["Running Balance"]) || 0;
    const prevTotalEquity = Number(settings["Beginning Balance"]) || 0;

    // 2. CALCULATE P/L BASED ON USER INPUT
    const newTotalEquity = abIsEmpty ? prevTotalEquity : parseCurrency(abRaw);
    const actualRewardDollar = newTotalEquity - prevTotalEquity;
    
    // 3. UPDATE LOGIC (Cash Settlement vs Margin)
    // IMPORTANT: These are now 'let' so they can be modified by the Options/Stocks logic below
    let nextAvailable = prevAvailable + actualRewardDollar;
    let nextTotalEquity = newTotalEquity;
    let nextUnsettled = Number(settings["Unsettled Balance"] || 0);
    const isCashSettled = assetClass === "Options";

    if (isCashSettled) {
        const entryPrice = Number(journal["Entry Price"] || settings["Entry Price"] || 0);
        const multiplier = getMultiplier(assetClass, settings["Product"], settings);
        
        // Calculate the total upfront cost of the trade
        const tradeCost = entryPrice * effectiveContracts * multiplier;
        // The money coming back (Initial Cost + Profit/Loss)
        const tradeRevenue = Math.max(0, tradeCost + actualRewardDollar);

        // Add the returned funds to the Unsettled bucket
        let rawUnsettled = nextUnsettled + tradeRevenue;
        // Buying power is your total equity MINUS whatever is tied up
        let rawAvailable = nextTotalEquity - rawUnsettled;
        
        nextUnsettled = Math.round(rawUnsettled * 100) / 100;
        nextAvailable = Math.round(rawAvailable * 100) / 100;
    }
    
    const product = settings["Product"] || "";
    // Create an effective position that forces "Buy" for Options and Stocks
    const effectivePosition = (assetClass === "Options" || assetClass === "Stocks") ? "Buy" : (journal["Position"] || "");
    const isDidNotTake = String(effectivePosition).trim().toLowerCase() === "did not take";
    
// === STRICT ASSET-BASED MOVEMENT CALCULATION ===
    let rawMovement = null;
    const manualMove = journal["Movement"] ?? journal["Ticks"] ?? journal["Pips"] ?? journal["Points"];
    
    if (manualMove !== "" && manualMove != null) {
        rawMovement = Number(manualMove);
    } else if (!isDidNotTake && journal["Entry Price"] > 0 && journal["Exit Price"] > 0) {
        const entry = Number(journal["Entry Price"]);
        const exit = Number(journal["Exit Price"]);
        const diff = Math.abs(exit - entry);
        
        if (assetClass === "Futures") {
            const ts = getTickSizeForProduct(product) || 0.25;
            rawMovement = Math.round(diff / ts); // Saves TICKS
        } else if (assetClass === "Forex") {
            const ps = getPipSizeForSymbol(product);
            const isIdx = isIndexSymbol(product);
            let pips = diff / ps;
            if (isIdx) pips = pips * 10;
            rawMovement = Math.round(pips * 100) / 100; // Saves PIPS
        } else if (assetClass === "Options") {
            const delta = Number(settings["Delta"] || 0);
            if (delta > 0) {
                // Calculate the underlying stock's point movement (e.g., 0.75 / 0.30 = 2.50)
                const uPoints = diff / delta;
                rawMovement = Math.round(uPoints * 100) / 100; // Saves UNDERLYING POINTS (2.50)
            } else {
                rawMovement = Math.round(diff * 100) / 100; // Fallback to premium points if no delta
            }
        } else {
                rawMovement = Math.round(diff * 100) / 100; // Saves POINTS
        }
    }
    
    const isLossFlag = !!journal["Loss"];
    const finalMovement = rawMovement !== null ? (isLossFlag ? -Math.abs(rawMovement) : Math.abs(rawMovement)) : null;

    // =====================================================================
    const pricePerTick = getMultiplier(assetClass, settings["Product"], settings);
    
    let effectiveStopLoss = Number(settings["Stop Loss"] || 0);
    let effectiveTakeProfit = Number(settings["Take Profit"] || 0);

    if (assetClass === "Options") {
        const entry = Number(settings["Entry Price"] || 0);
        const slPrem = Number(calculatedSummary?.stopLossPremium || 0);
        const tpPrem = Number(calculatedSummary?.takeProfitPremium || 0);
        
        effectiveStopLoss = slPrem > 0 ? Math.abs(entry - slPrem) : entry;
        effectiveTakeProfit = tpPrem > entry ? Math.abs(tpPrem - entry) : 0;
    } else {
        // Universal fix: Use the pre-calculated distances from the math engine.
        // This flawlessly maps Stocks, Futures, and Forex distances regardless of mode
        effectiveStopLoss = Number(calculatedSummary?.stopAmt ?? effectiveStopLoss);
        effectiveTakeProfit = Number(calculatedSummary?.tpAmt ?? effectiveTakeProfit);
    }

    const tradeObj = {};

    SHEET_HEADERS.forEach((header) => {
      if (header === "Trade #") tradeObj[header] = tradeNum;
      else if (header === "tradeNum") tradeObj["tradeNum"] = tradeNum;
      else if (header === "Product") tradeObj[header] = settings["Product"];
      
      // Save the TOTAL EQUITY to the trade history, not the split
      else if (header === "Running Balance") tradeObj[header] = newTotalEquity;
      
      else if (header === "Price Per Tick") tradeObj[header] = pricePerTick;
      else if (header === "Risk To Reward Ratio") {
        if (effectiveTakeProfit && effectiveStopLoss) {
          const rr = effectiveTakeProfit / effectiveStopLoss;
          tradeObj[header] = `1:${rr % 1 === 0 ? rr : rr.toFixed(2).replace(/\.?0+$/, "")}`;
        } else tradeObj[header] = "";
      }
      else if (header === "Percent of Balance") {
        const riskMode = String(settings["Risk Mode"] || "%");
        const riskDollarAmt = Number(settings["Risk Dollar Amount"] || 0);
        tradeObj[header] = (riskMode === "$") ? riskDollarAmt : prevAvailable * (Number(settings["Risk Percentage"] || 0) / 100);
      }
      else if (header === "Stop Loss_Take Profit") {
        if (assetClass === "Options") {
          // Options: Strict 2 decimals to preserve the .00 for premiums (e.g., 2.00 / 4.00)
          const slPrem = Number(calculatedSummary?.stopLossPremium || 0);
          const tpPrem = Number(calculatedSummary?.takeProfitPremium || 0);
          
          if (slPrem > 0 || tpPrem > 0) {
            const slStr = slPrem > 0 ? slPrem.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "–";
            const tpStr = tpPrem > 0 ? tpPrem.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "–";
            tradeObj[header] = `${slStr} / ${tpStr}`;
          } else {
            tradeObj[header] = journal[header] ?? "";
          }
        } else {
          // All other assets (Futures, Forex, Stocks): Keep necessary decimals, but drop trailing .00
          if (effectiveStopLoss > 0 || effectiveTakeProfit > 0) {
            // maximumFractionDigits: 5 handles Forex pips, but cleanly drops .00 for Futures/Stocks
            const slStr = effectiveStopLoss > 0 ? effectiveStopLoss.toLocaleString('en-US', { maximumFractionDigits: 5 }) : "–";
            const tpStr = effectiveTakeProfit > 0 ? effectiveTakeProfit.toLocaleString('en-US', { maximumFractionDigits: 5 }) : "–";
            tradeObj[header] = `${slStr} / ${tpStr}`;
          } else {
            tradeObj[header] = journal[header] ?? "";
          }
        }
      }
      else if (header === "Suggested Max # of Contracts") tradeObj[header] = calculatedVal;
      else if (header === "Total Risk") {
        tradeObj[header] = effectiveContracts * effectiveStopLoss * pricePerTick;
      }
      else if (header === "Size") tradeObj[header] = effectiveContracts;
      
      // FIX: Leave this block completely empty. We assign the exact unit below the loop now!
      else if (header === "Tcks" || header === "Ticks" || header === "Pips" || header === "Points") {
          // Handled explicitly below loop
      }
      
      else if (header === "Total Target Reward") {
        tradeObj[header] = effectiveContracts * effectiveTakeProfit * pricePerTick;
      }
      else if (header === "Actual Reward") tradeObj[header] = actualRewardDollar;
      else if (header === "Duration (h:mm)") tradeObj[header] = getDurationHHMMSS(journal["Entry Time"], journal["Exit Time"]);
      else if (header === "Entry Time") tradeObj[header] = journal["Entry Time"] ? to24HourTime(journal["Entry Time"]) : "";
      else if (header === "Exit Time") tradeObj[header] = journal["Exit Time"] ? to24HourTime(journal["Exit Time"]) : "";
      else if (header === "Actual Percent Growth") {
        const rawType = String(settings["Account Type"] || settings.type || settings.Platform || settings["Trade Mode"] || "").toLowerCase();
        const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
        const isExplicitProp = settings.isProp || settings.isPropFirm || settings.isPropEval || settings.isPropFunded;
        
        // Use prevTotalEquity for growth calc
        let baseBalance = prevTotalEquity;
        if (isExplicitProp && maxLoss > 0) baseBalance = Math.max(1, prevTotalEquity - maxLoss);

        tradeObj[header] = baseBalance > 0 ? ((actualRewardDollar / baseBalance) * 100).toFixed(2) + "%" : "0.00%";
      }
      else if (header === "Actual Risk to Reward") {
        const riskDollar = effectiveContracts * effectiveStopLoss * pricePerTick;
        if (riskDollar > 0 && actualRewardDollar !== 0) {
          const ratio = Math.abs(actualRewardDollar) / riskDollar;
          tradeObj[header] = `${actualRewardDollar < 0 ? "-" : ""}${Number.isInteger(ratio) ? ratio : ratio.toFixed(2)}:1`;
        } else tradeObj[header] = "";
      }
      else if (header === "Lesson") tradeObj[header] = lessonString;
      else tradeObj[header] = journal[header] ?? "";
    });

    tradeObj["tradeNum"] = tradeNum;
    tradeObj["Timeframe"] = journal["Timeframe"] || tradeObj["Timeframe"] || "";
    tradeObj["Potential"] = journal["Potential"] || "";
    tradeObj["Win"] = journal["Win"];
    tradeObj["Loss"] = journal["Loss"];
    tradeObj["LogLesson"] = journal["LogLesson"];
    tradeObj["Account Type"] = assetClass;
    tradeObj["Strike"] = journal["Strike"] || "";
    tradeObj["Expiration"] = journal["Expiration"] || "";
    tradeObj["Option Type"] = journal["Option Type"] || settings["Option Type"] || "Call";
    tradeObj["Underlying Entry Price"] = settings["Underlying Entry Price"] || "";
    tradeObj["Delta"] = settings["Delta"] || "";
    tradeObj["Position"] = effectivePosition;

    // --- EXPLICIT BALANCE SNAPSHOTS FOR THE JOURNAL ---
    tradeObj["Buying Power"] = nextAvailable;
    tradeObj["Unsettled Balance"] = nextUnsettled;
    tradeObj["Total Equity"] = nextTotalEquity;
    
    const logicTotalRisk = effectiveContracts * effectiveStopLoss * pricePerTick;
    tradeObj["Total Risk"] = logicTotalRisk;
    let currentTickSize = 0.01; // Default fallback for Stocks and Options

    if (assetClass === "Futures") {
        currentTickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
    } else if (assetClass === "Forex") {
        const isIdx = isIndexSymbol(settings["Product"]);
        // Force 0.1 for Indices so the Stats page calculates true Pips (50 / 0.1 = 500 Pips)
        currentTickSize = isIdx ? 0.1 : (getPipSizeForSymbol(settings["Product"]) || 0.0001);
    }

    tradeObj["tickSize"] = currentTickSize;
    tradeObj["assetClass"] = assetClass.toUpperCase();

    // STRICT UNIT SAVING
    if (finalMovement !== null) {
        tradeObj["Movement"] = finalMovement;
        if (assetClass === "Futures") tradeObj["Ticks"] = finalMovement;
        else if (assetClass === "Forex") tradeObj["Pips"] = finalMovement;
        else tradeObj["Points"] = finalMovement;
    }
    tradeObj["Needs Review"] = !!journal["Needs Review"];
    tradeObj["Emotional Notes"] = journal["Emotional Notes"] || "";
    tradeObj["Target Reached"] = !!journal["Target Reached"];
    await appendTrade(userId, accountId, tradeObj);

    tradeObj["Flag for Review"] = !!journal["Flag for Review"];
    if (journal["LogLesson"] && lessonString) {
      await appendLesson(lessonString);
    }

    // UPDATE SETTINGS WITH THE NEW SPLIT BALANCES
    const nextSettings = { 
        ...settings, 
        "Running Balance": nextAvailable,
        "Beginning Balance": nextTotalEquity,
        "Unsettled Balance": nextUnsettled,
        ...(assetClass !== "Options" && {
            "Entry Price": "",
            "Underlying Entry Price": "",
            "Delta": ""
        })
        // Notice we removed "Stop Loss" and "Take Profit" so they persist for the next trade!
    };
    
    await updateSettings(userId, accountId, nextSettings);
    setSettings(nextSettings);
    runSummaryOnly({ "Running Balance": nextAvailable });

    try {
      const me = await getSettings(userId, accountId);
      if (me?.CopyTrade?.role === "leader" || me?.settings?.CopyTrade?.role === "leader") {
        const { copyTradeToFollowers } = await import("../services/CopyTradeService");
        await copyTradeToFollowers({
          userId,
          leaderAccountId: accountId,
          leaderTrade: tradeObj,
          delta: actualRewardDollar,
          accountType: "futures"
        });
      }
    } catch (e) { console.error("[CopyTrade] failed", e); }

    setCurrentPopup({ id: 'tradeSaved' });

    try {
      const today = getTodayLocalISO();
      let currentSettings = { ...nextSettings }; 
      const localQueue = []; 

      const queueAdapter = (id, data) => {
          localQueue.push({ id, data });
      };
      
      await checkFirstTradeAnalysis(tradeObj, userId, queueAdapter);

      const rawHistory = await fetchJournalRows(userId, accountId);
      const cleanHistory = rawHistory.filter(r => String(r.tradeNum) !== String(tradeNum));
      const allRows = [tradeObj, ...cleanHistory]; 

      const rowTimestamp = (row) => {
        const d = row?.["Date"];
        if (!d) return 0;
        const dt = (d.includes && d.includes("-")) ? new Date(d) : (d.seconds ? new Date(d.seconds * 1000) : new Date());
        return dt.getTime();
      };
      
      const sortedRows = allRows.sort((a, b) => {
         const ta = Number(a?.tradeNum ?? 0);
         const tb = Number(b?.tradeNum ?? 0);
         if (ta !== tb) return tb - ta;
         return rowTimestamp(b) - rowTimestamp(a);
      });

      const markShown = async (key) => {
          currentSettings[key] = today;
          await updateSettings(userId, accountId, { [key]: today });
          setSettings(prev => ({ ...prev, [key]: today }));
      };

      const maxLossLimit = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
      const initBal = Number(settings["Initial Balance"] || 0);
      const isProp = (initBal > 0 && maxLossLimit > 0);
      const blownThreshold = isProp ? (initBal - maxLossLimit) : 0; 

      if (newTotalEquity <= blownThreshold) {
         if (currentSettings["MaxLossLimitBlownShownDate"] !== today) {
             setCurrentPopup({ id: 'maxLossLimitBlown', data: { endingBalance: newTotalEquity, startingBalance: prevTotalEquity } });
             await markShown("MaxLossLimitBlownShownDate");
         }
         return; 
      }

      const todayTrades = sortedRows.filter(t => (t["Date"] || "").startsWith(today));
      const todayTradesAsc = [...todayTrades].sort((a,b) => (Number(a.tradeNum||0) - Number(b.tradeNum||0)));

      const dailyLossLim = Number(settings["Daily Loss Limit"] || 0);
      const maxLosses = Number(settings["Max Loss Trades Per Day"] || 0);
      const maxTrades = Number(settings["Max Trades Per Day"] || 0);
      const dailyGoalAmount = Number(settings["Daily Goal"] || 0);
      
      const netPnL = todayTrades.reduce((acc, r) => acc + (Number(r["Actual Reward"]) || 0), 0);

      let goalWasReachedToday = false;
      let runningDailyPnL = 0;
      if (dailyGoalAmount > 0) {
          for (const t of todayTradesAsc) {
              runningDailyPnL += (Number(t["Actual Reward"]) || 0);
              if (runningDailyPnL >= dailyGoalAmount) {
                  goalWasReachedToday = true;
                  break; 
              }
          }
      }

      const isLoss = r => (r && (r.Loss === true || String(r.Loss).toLowerCase() === "true"));
      const isWin = r => (r && (r.Win === true || String(r.Win).toLowerCase() === "true"));
      const followedRules = (journal["Did I follow my Rules?"] === "Yes");
      const tradeGrowth = prevTotalEquity > 0 ? (actualRewardDollar / prevTotalEquity) * 100 : 0;

      if (settings["Max Trades Per Day Toggle"] && maxTrades > 0 && todayTrades.length > maxTrades && currentSettings["OvertradingPopupShownDate"] !== today) {
          localQueue.push({ id: 'overtradingPopup', data: { maxTradesPerDay: maxTrades } });
          await markShown("OvertradingPopupShownDate");
      }

      if (currentSettings["LossLimitWarningShownDate"] !== today && dailyLossLim > 0 && netPnL <= -dailyLossLim) {
          localQueue.push({ id: 'lossLimitWarning', data: { lossForDay: Math.abs(netPnL), lossLimitAmount: dailyLossLim } });
          await markShown("LossLimitWarningShownDate");
      }

      if (settings["Max Loss Trades Per Day Toggle"] && maxLosses > 0 && todayTrades.filter(isLoss).length >= maxLosses && currentSettings["TooManyLossesPopupShownDate"] !== today) {
          localQueue.push({ id: 'tooManyLossesPopup', data: { maxLossesPerDay: maxLosses } });
          await markShown("TooManyLossesPopupShownDate");
      }

      if (actualRewardDollar < 0 && currentSettings["OverRiskWarningShownDate"] !== today) {
          const lossAmt = Math.abs(actualRewardDollar);
          if (logicTotalRisk >= 0 && lossAmt > (logicTotalRisk + 0.01)) { 
              localQueue.push({ id: 'overRiskWarning', data: { riskAmount: logicTotalRisk, amountLost: lossAmt } });
              await markShown("OverRiskWarningShownDate");
          }
      }

      const initBalForRuin = Number(settings["Initial Balance"] || 0);
      const ruinThreshold = initBalForRuin * 0.25; 

      if (initBalForRuin > 0 && newTotalEquity <= ruinThreshold && currentSettings["RiskOfRuinWarningShownDate"] !== today) {
          localQueue.push({ id: 'riskOfRuinWarning' });
          await markShown("RiskOfRuinWarningShownDate");
      }
      else if (newTotalEquity > ruinThreshold && currentSettings["RiskOfRuinWarningShownDate"] === today) {
          const resetSettings = { ...currentSettings, "RiskOfRuinWarningShownDate": "" };
          await updateSettings(userId, accountId, resetSettings);
          setSettings(prev => ({ ...prev, "RiskOfRuinWarningShownDate": "" }));
          currentSettings["RiskOfRuinWarningShownDate"] = ""; 
      }

      const last3 = sortedRows.slice(0, 3);

      if (currentSettings["ThreeLossWarningShownDate"] !== today && last3.length === 3 && last3.every(isLoss)) {
          localQueue.push({ 
            id: 'threeLossWarning', 
            data: { 
              message: "You've lost 3 trades in a row.\n\nTake a break! \n\nTake some time to evaluate what is really going on. Make sure you are fresh and have a clear mind before taking another trade.\n\nThe Key is to avoid going on tilt and revenge trading.\n\n Trading is 80% Trading Psychology. This may be a good time to watch some psychology videos.  The video below from Psychology of Profits is a good one to start with.", 
              videoLink: "https://www.youtube.com/watch?v=6vZG4epFEOM" 
            } 
          });
          await markShown("ThreeLossWarningShownDate");
      } 
      else if (
          currentSettings["TwoLossAfterGoalWarningShownDate"] !== today && 
          dailyGoalAmount > 0 && 
          goalWasReachedToday && 
          last3.length >= 2 && 
          isLoss(last3[0]) && isLoss(last3[1])
      ) {
           localQueue.push({ id: 'twoLossAfterGoalWarning', data: { message: "You have taken 2 losses after reaching your daily goal.\n\nYou could easily go on tilt if you continue to lose trying to get it back. \n\nConsider stopping for the day to protect your gains." } });
           await markShown("TwoLossAfterGoalWarningShownDate");
      }
      else if (currentSettings["TwoLossWarningShownDate"] !== today && last3.length >= 2 && isLoss(last3[0]) && isLoss(last3[1])) {
          localQueue.push({ id: 'twoLossWarning' });
          await markShown("TwoLossWarningShownDate");
      }

      if (journal["Win"] === true && tradeGrowth >= 10 && !followedRules && currentSettings["Win10NoRulesPopupShownDate"] !== today) {
          localQueue.push({ 
              id: 'win10NoRulesPopup', 
              data: { message: "Winning BIG is always exciting!\n\n However you want to be careful. Having a big win without following your rules can be dangerous for your mindset.\n\nContinuing to do this could rewire your brain to seek dopamine hits instead of the true accomplishment of following your process.\n\nThis is another way to easily lose control and go on tilt.\n\nTreat this as a neutral event, and be mindful not to get hooked by big wins that happen outside your plan.\n\nDiscipline > excitement!" } 
          });
          await markShown("Win10NoRulesPopupShownDate");
      }

      const highMoodToday = todayTrades.filter(t => {
          const v = Number(t["Mood_Mindset"] || t["Mood"] || 0);
          return v === 4 || v === 5;
      });
      if (highMoodToday.length >= 3 && currentSettings["MoodVolatilityPopupShownDate"] !== today) {
          localQueue.push({ id: 'moodVolatilityPopup' });
          await markShown("MoodVolatilityPopupShownDate");
      }

      const ddToggle = (settings["Drawdown Limit % Toggle"] ?? true);
      if (ddToggle && currentSettings["DrawdownPopupShownDate"] !== today && sortedRows.length > 5) {
          const balances = sortedRows.map(r => Number(r["Actual Balance"] || r["Running Balance"])).filter(b => !isNaN(b) && b > 0);
          const peak = Math.max(...balances, initBal);
          const currentDrawdown = (peak - newTotalEquity);
          const currentDrawdownPct = peak > 0 ? (currentDrawdown / peak) * 100 : 0;
          const limitPct = Number(settings["Drawdown Limit %"] || 20);

          if (currentDrawdownPct >= limitPct) {
              localQueue.push({ 
                  id: 'drawdownWarning', 
                  data: { drawdownPct: currentDrawdownPct, drawdownAmount: currentDrawdown, drawdownLimitPct: limitPct }
              });
              await markShown("DrawdownPopupShownDate");
          }
      }

      const lessonsTodayCount = todayTrades.filter(t => 
          (t["LogLesson"] === true || String(t["LogLesson"]).toLowerCase() === "true")
      ).length;
      
      if (lessonsTodayCount >= 3 && currentSettings["LessonsLearnedStreakWarningShownDate"] !== today) {
           localQueue.push({ id: 'lessonsLearnedStreakWarning' });
           await markShown("LessonsLearnedStreakWarningShownDate");
      }

      if (Number(settings["Account Goal"] || 0) > 0 && newTotalEquity >= Number(settings["Account Goal"]) && currentSettings["AccountGoalReachedShownDate"] !== today) {
          localQueue.push({ id: 'accountGoalReached' });
          await markShown("AccountGoalReachedShownDate");
      }
      else if (dailyGoalAmount > 0 && netPnL >= dailyGoalAmount && currentSettings["DailyGoalPopupShownDate"] !== today && !isLoss(journal)) {
          localQueue.push({ id: 'dailyGoalPopup', data: { dailyGoal: dailyGoalAmount } });
          await markShown("DailyGoalPopupShownDate");
      }

      if (journal["Win"] === true && tradeGrowth >= 20 && followedRules && currentSettings["Win10WithRulesPopupShownDate"] !== today) {
          localQueue.push({ id: 'win10WithRulesPopup', data: { message: "Congratulations! You followed your rules and had a BIG win! \n\nDon't get over confident, stay consistent and continue following your rules. \n\nContinue being Awesome!", confetti: true } });
          await markShown("Win10WithRulesPopupShownDate");
      }

      const last5 = sortedRows.slice(0, 5);
      if (last5.length === 5 && last5.every(isWin)) {
          const sixthTrade = sortedRows[5]; 
          const isStartOfStreak = !sixthTrade || !isWin(sixthTrade);
          if (isStartOfStreak) {
              localQueue.push({ id: 'consecutiveWinsPopup' });
          }
      }

      if (localQueue.length > 0) {
          setPopupQueue(prev => [...prev, ...localQueue]);
      }

    } catch (analyzeErr) { console.error("Analysis Error:", analyzeErr); }

    setJournal(prev => ({
      ...prev, "Actual Balance": "", "Win": false, "Loss": false, "Notes": "", "Movement": "", "Potential": "",
      "Size": "", "Did I follow my Rules?": "", "Mood_Mindset": "", "Mood": "", "Lesson": "", "LogLesson": false,
      "Entry Time": "", "Exit Time": "", "Entry Price": "", "Exit Price": "", "Links_Screenshots": "", "Needs Review": false, "Emotional Notes": ""
    }));
    
    // Clear the calculated math from the UI
    setAutoSyncEnabled(false);

  } catch (err) { console.error("handleSaveTrade error:", err); } finally { setSavingTrade(false); }
};

const closeCurrentPopup = () => {
    const wasTradeSaved = currentPopup?.id === 'tradeSaved';
    setCurrentPopup(null);

    if (wasTradeSaved && pendingWarning) {
        setCurrentPopup(pendingWarning);
        setPendingWarning(null);
        return;
    }

    if (popupQueue.length > 0) {
        const next = popupQueue[0];
        setPopupQueue(prev => prev.slice(1));
        setCurrentPopup(next);
    }
};

const renderSummaryContent = () => {
    const buyingPower = Number(settings["Running Balance"] || 0);
    const totalEquity = Number(settings["Total Equity"] || 0); // Assuming this is your unsettled/total field
    
    // 1. Check if the account is completely empty (Blown)
    if (buyingPower <= 0 && totalEquity <= 0) {
        return (
            <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-xl border border-red-200 dark:border-red-800 text-center animate-fadeIn">
                <div className="flex justify-center mb-3">
                    <div className="p-3 bg-red-100 dark:bg-red-800/40 rounded-full">
                        <FaExclamationTriangle className="text-red-600 dark:text-red-400" size={30} />
                    </div>
                </div>
                <h4 className="font-bold text-red-700 dark:text-red-300 text-lg">Account Balance: $0.00</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 leading-relaxed">
                    It looks like your account has reached zero. To continue calculating new trades, please <strong>add additional funds</strong> to your brokerage account to get back in the game.
                </p>
            </div>
        );
    }

    // 2. Check if just Buying Power is 0 (Waiting for settlement)
    if (buyingPower <= 0 && totalEquity > 0) {
        return (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-6 rounded-xl border border-amber-200 dark:border-amber-800 text-center animate-fadeIn">
                <div className="flex justify-center mb-3">
                    <div className="p-3 bg-amber-100 dark:bg-amber-800/40 rounded-full">
                        <FaRegClock className="text-amber-600 dark:text-amber-400" size={30} />
                    </div>
                </div>
                <h4 className="font-bold text-amber-700 dark:text-amber-300 text-lg">Waiting for Settlement</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 leading-relaxed">
                    Your Buying Power is currently $0.00, but you still have equity in your account. Please <strong>wait for your broker to settle your trades</strong> or reset your daily buying power.
                </p>
            </div>
        );
    }

    // 3. Normal Calculation Summary (Existing code)
    if (!calculatedSummary) return <div className="text-center text-gray-500 py-4">Click Calculate to see summary.</div>;

    const LabelWithTooltip = ({ label, tooltipContent }) => (
      <span className="flex items-center gap-2">
        {label}
        <Tooltip variant="neutral" backdrop content={tooltipContent}>
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-300 cursor-help">?</span>
        </Tooltip>
      </span>
    );

const formatExit = (val, mode, isStopLoss) => {
        if (!val || val <= 0) return "–";

        const entry = Number(settings["Entry Price"] || 0);
        const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
        const pipSize = getPipSizeForSymbol(settings["Product"]);
        const isIdx = isIndexSymbol(settings["Product"]);
        const isFx = assetClass === "Forex";

        let dist = null;
        let price = null;

        if (mode === "Price") {
            price = val;
            if (entry > 0) {
                const diff = Math.abs(price - entry);
                if (assetClass === "Futures") dist = Math.round(diff / tickSize);
                else if (isFx) dist = diff / pipSize; // Distance in Points/Pips
                else dist = diff;
            }
        } else {
            dist = val; 
            if (entry > 0) {
                let move = dist * (assetClass === "Futures" ? tickSize : (isFx ? pipSize : 1));
                // If user entered Ticks for Forex, divide by 10 to get the Point/Pip price move
                if (isFx && mode === "Ticks") move = move / 10; 
                price = isStopLoss ? (entry - move) : (entry + move);
            }
        }


            // Helper to format Distance string
                const getDistStr = (d, parens) => {
                    if (d === null) return "";
                    let str = "";
                    if (isFx) {
                        if (isIdx) {
                            // 1 Point = 10 Pips = 100 Ticks
                            const pts = Math.round(d * 100) / 100;
                            const pips = Math.round(d * 10 * 100) / 100;
                            const ticks = Math.round(d * 100);
                            str = `${pts} points / ${pips} pips / ${ticks} ticks`;
                        } else {
                            const pips = Math.round(d * 100) / 100;
                            const ticks = Math.round(d * 10 * 100) / 100;
                            str = `${pips} pips / ${ticks} ticks`;
                        }
                    } else if (assetClass === "Stocks") {
                        str = `${Math.round(d * 100) / 100} pts / ${Math.round(d * 100)} ticks`;
                    } else {
                        const outUnit = assetClass === "Futures" ? "ticks" : "pts";
                        str = `${Math.round(d * 100) / 100} ${outUnit}`;
                    }
                    return parens ? `(${str})` : str;
                };

        if (price !== null) {
            const isNegative = isStopLoss && price < 0;
            return (
              <span className="flex items-center gap-1">
                {isNegative && (
                  <Tooltip variant="red" content="The Stop Loss distance is greater than the Entry Price.">
                    <FaExclamationTriangle className="text-red-500 cursor-help" size={12} />
                  </Tooltip>
                )}
                <span className={isNegative ? "text-red-600 font-bold" : ""}>
                  {/* Hide absolute price for Forex, show it for Futures/Stocks */}
                  {isFx ? getDistStr(dist, false) : `${formatCurrency(price)} ${getDistStr(dist, true)}`}
                </span>
              </span>
            );
        }

        return getDistStr(dist, false);
    };

    return (
      <div className="space-y-1">
        
        {/* PRICE PER TICK (Futures Only) */}
        {assetClass === "Futures" && (
           <SummaryRow 
             label={<LabelWithTooltip label="Price Per Tick" tooltipContent="The value of a single minimum price movement for this contract." />} 
             value={calculatedSummary.pricePerTick ? formatCurrency(calculatedSummary.pricePerTick) : ""} 
           />
        )}
        
        {/* TOTAL COST / CASH REQUIRED (Options Only) */}
        {assetClass === "Options" && (
           <SummaryRow 
             label={
               <LabelWithTooltip 
                 label="Total Cost (Cash Required)" 
                 tooltipContent={
                   <div className="space-y-2">
                     <p><strong>Buying Power Impact</strong></p>
                     <p>This is the actual cash amount deducted from your account to enter this trade.</p>
                     <div className="text-xs bg-gray-800 p-2 rounded border border-gray-600 font-mono text-white">
                       {calculatedSummary.entryPrice} × 100 × {calculatedSummary.size} contracts
                     </div>
                     <p className="text-xs text-yellow-300">
                       Note: This is different from "Risk". You pay this amount upfront, but you only "lose" the difference between Entry and Stop Loss.
                     </p>
                   </div>
                 } 
               />
             } 
             value={formatCurrency(calculatedSummary.size * calculatedSummary.entryPrice * 100)} 
           />
        )}
        
        {/* R:R RATIO */}
        <SummaryRow 
            label={
              <LabelWithTooltip 
                label="Reward to Risk Ratio" 
                tooltipContent={
                   assetClass === "Options" 
                   ? "For every $1 of value you risk losing (if stop is hit), you expect to make this amount in profit (if target is hit)."
                   : "The ratio of potential profit vs. potential loss."
                } 
              />
            }
            value={calculatedSummary.riskRewardRatio || calculatedSummary.rr} 
        />
        
        {/* RISK AMOUNT SETTING */}
        <SummaryRow 
             label={
               <LabelWithTooltip 
                 label={
                   settings["Risk Mode"] === "%" ? `${settings["Risk Percentage"]}% of Balance` : `Risk Amount ($)`
                 }
                 tooltipContent={
                    assetClass === "Options" 
                    ? "This is your 'Budget for Loss'. It is the maximum amount of value you are willing to lose if the trade goes against you and hits your stop loss."
                    : "The calculated dollar amount you are risking based on your settings."
                 }
               />
             } 
             value={formatCurrency(calculatedSummary.riskDollar)} 
        />

        {/* POSITION VALUE / CASH REQUIRED (Stocks Only) */}
        {assetClass === "Stocks" && calculatedSummary.entryPrice > 0 && (
           <SummaryRow 
             label={
               <LabelWithTooltip 
                 label="Position Value (Cash Required)" 
                 tooltipContent="The total market value of the stocks being purchased. This is the amount of cash required to open the position." 
               />
             } 
             value={formatCurrency(calculatedSummary.size * calculatedSummary.entryPrice)} 
           />
        )}

 {/* --- DYNAMIC EXIT SUMMARY --- */}
        {assetClass !== "Options" && (
          <div className="space-y-1 border-y border-gray-100 dark:border-gray-800 my-1 py-1">
            
            {/* STOP LOSS ROW */}
           {/* STOP LOSS ROW */}
            <SummaryRow 
              label={<LabelWithTooltip label="Stop Loss" tooltipContent="Planned exit price and distance for a loss." />} 
              value={(() => {
                const slVal = calculatedSummary.stopLossVal;
                if (!slVal || slVal <= 0) return "–";

                const entry = Number(settings["Entry Price"] || 0);
                const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
                const pipSize = getPipSizeForSymbol(settings["Product"]);
                const isFx = assetClass === "Forex";
                const isIdx = isIndexSymbol(settings["Product"]);
                const unit = assetClass === "Futures" ? "ticks" : (isFx ? (isIdx ? "points" : "pips") : "pts");


                // Helper to format Distance string
                const getDistStr = (d, parens) => {
                    if (d === null) return "";
                    let str = "";
                    if (isFx) {
                        if (isIdx) {
                            const pts = Math.round(d * 100) / 100;
                            const pips = Math.round(d * 10 * 100) / 100;
                            const ticks = Math.round(d * 100);
                            str = `${pts} points / ${pips} pips / ${ticks} ticks`;
                        } else {
                            const pips = Math.round(d * 100) / 100;
                            const ticks = Math.round(d * 10 * 100) / 100;
                            str = `${pips} pips / ${ticks} ticks`;
                        }
                    } else if (assetClass === "Stocks") {
                        str = `${Math.round(d * 100) / 100} pts / ${Math.round(d * 100)} ticks`;
                    } else if (assetClass === "Futures") {
                        const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
                        const pts = d * tickSize;
                        const formattedPts = pts % 1 === 0 ? pts : pts.toFixed(2);
                        str = `${formattedPts} pts / ${Math.round(d)} ticks`;
                    } else {
                        str = `${Math.round(d * 100) / 100} pts`;
                    }
                    return parens ? `(${str})` : str;
                };

                if (settings["Stop Loss Mode"] === "Price") {
                  const dist = entry > 0 ? Math.abs(entry - slVal) / (assetClass === "Futures" ? tickSize : (isFx ? pipSize : 1)) : null;
                  
                  // IF FOREX: Just return the distance, hide the price
                  if (isFx && dist !== null) return getDistStr(dist, false);

                  const isNegative = slVal < 0;
                  return (
                     <span className="flex items-center gap-1">
                        {isNegative && (
                          <Tooltip variant="red" content="The Stop Loss distance is greater than the Entry Price.">
                            <FaExclamationTriangle className="text-red-500 cursor-help" size={12} />
                          </Tooltip>
                        )}
                        <span className={isNegative ? "text-red-600 font-bold" : ""}>
                          {formatCurrency(slVal)} {dist !== null ? getDistStr(dist, true) : ""}
                        </span>
                     </span>
                  );
                }

                // Mode is Distance (Ticks/Pips)
                let ptsDist = slVal;
                if (isFx && settings["Stop Loss Mode"] === "Ticks") {
                    ptsDist = isIdx ? slVal / 100 : slVal / 10;
                } else if (assetClass === "Stocks" && settings["Stop Loss Mode"] === "Ticks") {
                    ptsDist = slVal * 0.01;
                }

                if (entry > 0) {
                  // IF FOREX: Just return the distance, hide the calculated price
                  if (isFx) return getDistStr(ptsDist, false);

                  let subtractAmt = slVal; // <-- UPDATED subtraction logic
                  if (assetClass === "Futures") subtractAmt = slVal * tickSize;
                  else if (assetClass === "Stocks" && settings["Stop Loss Mode"] === "Ticks") subtractAmt = slVal * 0.01;

                  const price = entry - subtractAmt;
                  const isNegative = price < 0;
                  return (
                     <span className="flex items-center gap-1">
                        {isNegative && (
                          <Tooltip variant="red" content="The Stop Loss distance is greater than the Entry Price.">
                            <FaExclamationTriangle className="text-red-500 cursor-help" size={12} />
                          </Tooltip>
                        )}
                        <span className={isNegative ? "text-red-600 font-bold" : ""}>
                          {formatCurrency(price)} {getDistStr(ptsDist, true)}
                        </span>
                     </span>
                  );
                }

                return getDistStr(ptsDist, false);
              })()} 
            />

           {/* TAKE PROFIT ROW */}
                       <SummaryRow 
                         label={<LabelWithTooltip label="Take Profit" tooltipContent="Planned exit price and distance for a profit." />} 
                         value={(() => {
                           const tpVal = calculatedSummary.takeProfitVal;
                           if (!tpVal || tpVal <= 0) return "–";

                           const entry = Number(settings["Entry Price"] || 0);
                           const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
                           const pipSize = getPipSizeForSymbol(settings["Product"]);
                           const isFx = assetClass === "Forex";
                           const isIdx = isIndexSymbol(settings["Product"]);
                           const unit = assetClass === "Futures" ? "ticks" : (isFx ? (isIdx ? "points" : "pips") : "pts");

                           // Helper to format Distance string
                           const getDistStr = (d, parens) => {
                               if (d === null) return "";
                               let str = "";
                               if (isFx) {
                                   if (isIdx) {
                                       const pts = Math.round(d * 100) / 100;
                                       const pips = Math.round(d * 10 * 100) / 100;
                                       const ticks = Math.round(d * 100);
                                       str = `${pts} points / ${pips} pips / ${ticks} ticks`;
                                   } else {
                                       const pips = Math.round(d * 100) / 100;
                                       const ticks = Math.round(d * 10 * 100) / 100;
                                       str = `${pips} pips / ${ticks} ticks`;
                                   }
                               } else if (assetClass === "Stocks") {
                                   str = `${Math.round(d * 100) / 100} pts / ${Math.round(d * 100)} ticks`;
                               } else if (assetClass === "Futures") {
                                   const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
                                   const pts = d * tickSize;
                                   const formattedPts = pts % 1 === 0 ? pts : pts.toFixed(2);
                                   str = `${formattedPts} pts / ${Math.round(d)} ticks`;
                               } else {
                                   str = `${Math.round(d * 100) / 100} pts`;
                               }
                               return parens ? `(${str})` : str;
                           };

                           if (settings["Take Profit Mode"] === "Price") {
                             const dist = entry > 0 ? Math.abs(tpVal - entry) / (assetClass === "Futures" ? tickSize : (isFx ? pipSize : 1)) : null;
                             
                             // IF FOREX: Just return the distance, hide the price
                             if (isFx && dist !== null) return getDistStr(dist, false);

                             return `${formatCurrency(tpVal)} ${dist !== null ? getDistStr(dist, true) : ""}`;
                           }

                           let ptsDist = tpVal;
                           if (isFx && settings["Take Profit Mode"] === "Ticks") {
                               ptsDist = tpVal / 10;
                           } else if (assetClass === "Stocks" && settings["Take Profit Mode"] === "Ticks") {
                               ptsDist = tpVal * 0.01;
                           }

                           if (entry > 0) {
                             // IF FOREX: Just return the distance, hide the calculated price
                             if (isFx) return getDistStr(ptsDist, false);

                             let addAmt = tpVal; // <-- UPDATED addition logic
                             if (assetClass === "Futures") addAmt = tpVal * tickSize;
                             else if (assetClass === "Stocks" && settings["Take Profit Mode"] === "Ticks") addAmt = tpVal * 0.01;

                             const price = entry + addAmt;
                             return `${formatCurrency(price)} ${getDistStr(ptsDist, true)}`;
                           }

                           return getDistStr(ptsDist, false);
                         })()} 
                       />
          </div>
        )}

        {/* SEPARATED STOP LOSS (Options Only) */}
        {assetClass === "Options" && (
          <SummaryRow 
            label={
              <LabelWithTooltip 
          label="Stop Loss (Underlying / Premium)"
          tooltipContent={
            <div className="space-y-2 p-1">
              <p className="text-[15px] leading-relaxed text-gray-100">
                The Underlying Stop Loss Price and Option Premium is where you plan to exit for a loss.
              </p>
              {/* BOLD STATEMENT WITH COLOR POP */}
              <p className="text-[15px] leading-relaxed text-blue-100">
                The Underlying Stop Loss Price is provided when the Underlying Entry Price and Delta are entered. These are optional fields.
              </p>
               <p className="text-[15px] leading-relaxed font-bold text-blue-100">
                You will not be able to get the exact Delta values because  <span className="text-yellow-400">is constantly changing at every price</span> but you can use the average value.
              </p>
            </div>
               }
            />
          }

            value={(() => {
               const underlyingEntry = Number(settings["Underlying Entry Price"] || 0);
               const delta = Number(settings["Delta"] || 0);
               const slMode = settings["Stop Loss Mode"];
               const premStr = formatCurrency(calculatedSummary.stopLossPremium);

               if ((slMode === "Premium" || slMode === "Ticks") && (!underlyingEntry || !delta)) {
                  return `– / ${premStr}`; 
               }
               
               if (underlyingEntry > 0 && delta > 0) {
                   return `${formatCurrency(calculatedSummary.stopLossPrice)} / ${premStr}`;
               }

               return (
                 <span className="flex items-center justify-end gap-1">
                   <Tooltip content="Please enter Underlying Entry and Delta above.">
                     <span className="text-yellow-600 dark:text-yellow-500 text-[11px] font-bold cursor-help uppercase bg-yellow-50 dark:bg-yellow-900/30 px-1.5 py-0.5 rounded">
                       Entry Required
                     </span>
                   </Tooltip>
                   <span className="text-gray-400">/</span> {premStr}
                 </span>
               );
            })()} 
          />
        )}

        {/* SEPARATED TAKE PROFIT (Options Only) */}
        {assetClass === "Options" && (
          <SummaryRow 
            label={<LabelWithTooltip label="Take Profit (Underlying / Premium)" tooltipContent="The Underlying Price and Option Premium where you plan to take profit." />} 
            label={
              <LabelWithTooltip 
          label="Take Profit (Underlying / Premium)"
          tooltipContent={
            <div className="space-y-2 p-1">
              <p className="text-[15px] leading-relaxed text-gray-100">
                The Underlying Price and Option Premium is where you plan to take profit.
              </p>
              {/* BOLD STATEMENT WITH COLOR POP */}
              <p className="text-[15px] leading-relaxed text-blue-100">
                The Underlying Take Profit Price is provided when the Underlying Entry Price and Delta are entered. These are optional fields.
              </p>
               <p className="text-[15px] leading-relaxed font-bold text-blue-100">
                You will not be able to get the exact Delta values because  <span className="text-yellow-400">is constantly changing at every price</span> but you can use the average value.
              </p>
            </div>
               }
            />
          }
            value={(() => {
               const underlyingEntry = Number(settings["Underlying Entry Price"] || 0);
               const delta = Number(settings["Delta"] || 0);
               const tpMode = settings["Take Profit Mode"];
               const premStr = formatCurrency(calculatedSummary.takeProfitPremium);

               if ((tpMode === "Premium" || tpMode === "Ticks") && (!underlyingEntry || !delta)) {
                  return `– / ${premStr}`; 
               }
               if (underlyingEntry > 0 && delta > 0) {
                   return `${formatCurrency(calculatedSummary.takeProfitPrice)} / ${premStr}`;
               }

               return (
                 <span className="flex items-center justify-end gap-1">
                   <Tooltip content="Please enter Underlying Entry and Delta above.">
                     <span className="text-yellow-600 dark:text-yellow-500 text-[11px] font-bold cursor-help uppercase bg-yellow-50 dark:bg-yellow-900/30 px-1.5 py-0.5 rounded">
                       Entry Required
                     </span>
                   </Tooltip>
                   <span className="text-gray-400">/</span> {premStr}
                 </span>
               );
            })()} 
          />
        )}

        {/* EST PRICES (Stocks Only) */}
        {assetClass === "Stocks" && calculatedSummary.entryPrice > 0 && (
          <>
            <SummaryRow label="Est. Stop Price" value={formatCurrency(calculatedSummary.entryPrice - calculatedSummary.stopAmt)} />
            <SummaryRow label="Est. Target Price" value={formatCurrency(calculatedSummary.entryPrice + calculatedSummary.tpAmt)} />
          </>
        )}

        {/* TOTAL RISK / PROJECTED LOSS */}
                <SummaryRow 
                    label={
                      <LabelWithTooltip 
                        label={assetClass === "Stocks" ? "Projected Loss (at Stop)" : "Total Risk"} 
                        tooltipContent={(() => {
                          const size = calculatedSummary.size || calculatedSummary.minContracts || 0;
                          const riskDollar = calculatedSummary.riskDollar;
                          // Handle Options risk math separately due to premium difference
                          const totalRisk = assetClass === "Options" 
                            ? (calculatedSummary.entryPrice - (calculatedSummary.stopLossPremium || 0)) * 100 * size
                            : (calculatedSummary.totalRisk || 0);

                          const budgetLabel = settings["Risk Mode"] === "%" ? `${settings["Risk Percentage"]}% of balance` : "Risk budget ($)";

                          return (
                            <div className="space-y-3 p-1">
                              <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                                How we calculate {assetClass === "Stocks" ? "loss" : "total risk"}
                              </div>
                              
                              <div className="space-y-2">
                                <div className="font-mono text-[14px] leading-relaxed">
                                  {assetClass === "Futures" && (
                                    <>
                                      <div className="text-gray-400">Price Per Tick × Stop Loss (Ticks) × {getUnitLabel(assetClass)}</div>
                                      <div className="text-white">{formatCurrency(calculatedSummary.pricePerTick)} × {Math.round(calculatedSummary.stopAmt)} × {size} = <span className="font-bold text-blue-300">{formatCurrency(totalRisk)}</span></div>
                                    </>
                                  )}

                                  {assetClass === "Forex" && (
                                    <>
                                      <div className="text-gray-400">Price Per Pip × Stop Loss (Pips) × {getUnitLabel(assetClass)}</div>
                                      <div className="text-white">{formatCurrency(calculatedSummary.multiplier)} × {calculatedSummary.stopAmt.toFixed(1)} × {size.toFixed(2)} = <span className="font-bold text-blue-300">{formatCurrency(totalRisk)}</span></div>
                                    </>
                                  )}

                                  {assetClass === "Stocks" && (
                                    <>
                                      <div className="text-gray-400">Risk per Share × {getUnitLabel(assetClass)}</div>
                                      <div className="text-white">{formatCurrency(calculatedSummary.stopAmt)} × {size} = <span className="font-bold text-blue-300">{formatCurrency(totalRisk)}</span></div>
                                    </>
                                  )}

                                  {assetClass === "Options" && (
                                    <>
                                      <div className="text-gray-400">(Entry Premium - Stop Premium) × 100 × {getUnitLabel(assetClass)}</div>
                                      <div className="text-white">({calculatedSummary.entryPrice.toFixed(2)} - {calculatedSummary.stopLossPremium.toFixed(2)}) × 100 × {size} = <span className="font-bold text-blue-300">{formatCurrency(totalRisk)}</span></div>
                                    </>
                                  )}
                                </div>

                                <div className="text-[14px] border-t border-white/10 pt-2">
                                  <div className="text-gray-400">{budgetLabel}: <span className="text-white font-medium">{formatCurrency(riskDollar)}</span></div>
                                  <div className={`flex items-center gap-1.5 mt-1 font-bold ${totalRisk <= riskDollar + 0.05 ? "text-green-400" : "text-yellow-400"}`}>
                                    {totalRisk <= riskDollar + 0.05 
                                      ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg> Total risk is ≤ budget</>
                                      : <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> Minimum size exceeds budget</>
                                    }
                                  </div>
                                </div>
                              </div>

                              <p className="text-[13px] text-gray-400 italic leading-snug border-t border-white/10 pt-2">
                                If you want to trade with more {getUnitLabel(assetClass).toLowerCase()} you can increase the risk percentage to an amount you are comfortable with.
                              </p>
                            </div>
                          );
                        })()}
                      />
                    } 
                    value={(() => {
                      if (assetClass === "Options") {
                          const entry = Number(calculatedSummary.entryPrice || 0);
                          const stopPrem = Number(calculatedSummary.stopLossPremium || 0);
                          const size = Number(calculatedSummary.size || 0);
                          const riskPerShare = stopPrem > 0 ? (entry - stopPrem) : entry;
                          return formatCurrency(riskPerShare * 100 * size);
                      }
                      return formatCurrency(calculatedSummary.totalRisk);
                    })()}
                    red={calculatedSummary.size === 0}
                />

                <SummaryRow 
                          label={
                            <LabelWithTooltip 
                              label="Target Reward" 
                              tooltipContent={(() => {
                                const size = calculatedSummary.size || calculatedSummary.minContracts || 0;
                                const targetReward = assetClass === "Options" 
                                  ? (calculatedSummary.takeProfitPremium - calculatedSummary.entryPrice) * 100 * size
                                  : (calculatedSummary.targetReward || 0);

                                return (
                                  <div className="space-y-3 p-1">
                                    <div className="font-bold text-green-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                                      Potential Profit Breakdown
                                    </div>
                                    
                                    <div className="space-y-2">
                                      <div className="font-mono text-[15px] leading-relaxed">
                                        {assetClass === "Options" ? (
                                          <>
                                            <div className="text-gray-400">(Target Premium - Entry Premium) × 100 × {getUnitLabel(assetClass)}</div>
                                            <div className="text-white">
                                              (<span className="text-yellow-400">{calculatedSummary.takeProfitPremium.toFixed(2)}</span> - <span className="text-yellow-400">{calculatedSummary.entryPrice.toFixed(2)}</span>) × 100 × {size} = <span className="font-bold text-green-400">{formatCurrency(targetReward)}</span>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="text-gray-400">Reward per Unit × Quantity</div>
                                            <div className="text-white">
                                              {formatCurrency(calculatedSummary.tpAmt)} × {size} = <span className="font-bold text-green-400">{formatCurrency(targetReward)}</span>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    <p className="text-[13px] text-gray-400 italic leading-snug border-t border-white/10 pt-2">
                                      This represents your gross profit if the {assetClass === "Options" ? "option premium" : "asset price"} reaches your target.
                                    </p>
                                  </div>
                                );
                              })()} 
                            />
                          } 
                          value={calculatedSummary.targetReward >= 0 ? formatCurrency(calculatedSummary.targetReward) : "–"} 
                        />



{/* MAXIMUM STOP LOSS PREMIUM (Options Only) */}
{assetClass === "Options" && (
    <SummaryRow 
      label={
        <LabelWithTooltip 
          label="Max Stop Loss (Premium Floor)"
          tooltipContent={
            <div className="space-y-2 p-1">
              <p className="text-[15px] leading-relaxed text-gray-100">
                The lowest premium price you can set for your Stop Loss while staying within your risk budget.
              </p>
              {/* BOLD SECOND SENTENCE */}
              <p className="text-[15px] leading-relaxed font-bold text-blue-100">
                A value of <span className="text-yellow-400">$0.00</span> indicates your budget covers 100% of the trade cost, allowing for a total loss without exceeding your limit.
              </p>
            </div>
          }
        />
      } 
      value={(() => {
          let maxPrice = calculatedSummary.maxAllowableStopPrice;
          
          if (calculatedSummary.size > 0) {
              const riskDollar = calculatedSummary.riskDollar;
              const size = calculatedSummary.size;
              const entry = calculatedSummary.entryPrice;
              
              const totalValue = entry * size * 100;
              const cappedRisk = Math.min(riskDollar, totalValue);
              
              const maxRiskPerShare = cappedRisk / (size * 100);
              maxPrice = Math.max(0, Math.ceil((entry - maxRiskPerShare) * 100) / 100);
          }
          return maxPrice > 0 ? formatCurrency(maxPrice) : "$0.00";
      })()}
    />
)}

{/* MAXIMUM STOP LOSS (Futures & Forex) */}
        {assetClass !== "Options" && assetClass !== "Stocks" && (
          <SummaryRow 
            label={
              <LabelWithTooltip 
                label={`Max Stop Loss for ${calculatedSummary.minContracts} ${calculatedSummary.unit}`}
                tooltipContent={
                  <div className="space-y-2 p-1">
                    <p className="text-[15px] leading-relaxed text-gray-100">
                      This shows the maximum distance your stop loss can be if you trade your minimum size.
                    </p>
                    {/* BOLD SECOND SENTENCE WITH DYNAMIC TEXT AND COLOR POP */}
                    <p className="text-[15px] leading-relaxed font-bold text-blue-100">
                      This value is based on your <span className="text-yellow-400">{assetClass === "Forex" ? "minimum lots" : "minimum contracts"}</span> value in your settings. 
                      If the maximum stop loss is a very large number, that can indicate you should increase it.
                    </p>
                  </div>
                }
              />
            } 
            value={(() => {
              const risk = calculatedSummary.riskDollar;
              const minQty = calculatedSummary.minContracts;
              const mult = calculatedSummary.multiplier;
              
              if (mult > 0 && minQty > 0) {
                // FIX: Add + 0.000001 to bypass the JavaScript floating-point bug
                const val = Math.floor((risk / (minQty * mult)) + 0.000001); 
                
                if (assetClass === "Forex") {
                    const isIdx = isIndexSymbol(settings["Product"]);
                    const formatBase = Math.round(val * 100) / 100;
                    
                    if (isIdx) {
                        const formatPips = Math.round(val * 10 * 100) / 100;
                        const formatTks = Math.round(val * 100);
                        return `${formatPips} pips / ${formatTks} ticks`;
                    }
                    const formatTks = Math.round(val * 10 * 100) / 100;
                    return `${formatBase} pips / ${formatTks} ticks`;
                }
                
                // NEW: Dynamic Points/Ticks for Futures
                if (assetClass === "Futures") {
                    const tickSize = getTickSizeForProduct(settings["Product"]) || 0.25;
                    const pts = val * tickSize;
                    const formattedPts = pts % 1 === 0 ? pts : pts.toFixed(2);
                    return `${formattedPts} pts / ${val} ticks`;
                }
                
                return `${val} ticks`;
              }
              return "0";
            })()}
          />
        )}

        {/* NEW: True Position Size in Units for Forex/CFDs */}
                  {assetClass === "Forex" && calculatedSummary.size > 0 && (
                     <div>
                        <SummaryRow 
                          label={
                            <span>
                              <LabelWithTooltip 
                                label="Total Units" 
                                tooltipContent="The true underlying position size. (e.g. 1 standard lot = 100,000 units, 1 lot of Gold = 100 oz)." 
                              />
                            </span>
                          } 
                          value={(() => {
                             const meta = getUnitsMeta(settings["Product"]);
                             const totalUnits = calculatedSummary.size * meta.perLot;
                             
                             // Format perfectly with commas
                             return <span className="font-medium">{totalUnits.toLocaleString('en-US')} {meta.label}</span>;
                          })()} 
                        />
                     </div>
                  )}
           
             

        {/* SUGGESTED SIZE */}
        <div className={calculatedSummary.size === 0 ? "bg-red-50 dark:bg-red-900/30 rounded p-1" : "bg-blue-50 dark:bg-blue-900/30 rounded p-1"}>
          <SummaryRow 
            label={
              <LabelWithTooltip 
                label={
                  assetClass === "Forex" ? "Suggested Lot Size" : 
                  assetClass === "Options" || assetClass === "Futures" ? "Suggested # of Contracts" : 
                  "Suggested # of Shares"
                }
                tooltipContent="The recommended size based on your risk and capital settings."
              />
            } 
            value={calculatedSummary.size === 0 ? "0" : calculatedSummary.size} 
            red={calculatedSummary.size === 0}
          />
        </div>
      </div>
    );
  };

const renderFuturesSettings = () => (
    <div className="space-y-6">
      
{/* --- COLLAPSIBLE BALANCES SECTION --- */}
    <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span>Account Cash Status</span>
          <div className="flex flex-wrap gap-2 text-sm font-normal">
            <span className="text-gray-500">
              Total Equity: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(settings["Beginning Balance"] || 0)}</span>
            </span>
            <span className="text-gray-500 hidden sm:inline">|</span>
            <span className="text-gray-500">
              Buying Power: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"] || 0)}</span>
            </span>
          </div>
        </div>
        <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
        <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
      </summary>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
          <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Account Equity</label>
          <input type="number" value={settings["Beginning Balance"] ?? ""} onChange={e => handleSettingChange("Beginning Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buying Power</label>
         <input type="number" value={settings["Running Balance"] !== undefined && settings["Running Balance"] !== "" ? Math.round(settings["Running Balance"] * 100) / 100 : ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
      </div>
    </details>



      {/* --- STEP 1: THE SETUP --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Product<span className="text-red-500 text-lg">*</span></label>
                <select 
                  value={settings["Product"] || ""} 
                  onChange={e => handleSettingChange("Product", e.target.value)} 
                  className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">Select Futures</option>
                  
                  {/* FAVORITES GROUP */}
                  {favOpts.length > 0 && (
                    <optgroup label="♥ Favorites">
                      {favOpts.map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </optgroup>
                  )}

                  {/* ALL OTHER PRODUCTS GROUP */}
                  <optgroup label="All Products">
                    {nonFavOpts.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div>
                <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
                  Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
                </label>
                <input 
                  type="number" 
                  placeholder="e.g. 4500.25" 
                  value={settings["Entry Price"] || ""} 
                  onChange={e => handleSettingChange("Entry Price", e.target.value)} 
                  className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${
                    showEntryWarning 
                      ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                      : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
                  }`} 
                />
            {showEntryWarning && (
              <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
                <FaExclamationTriangle size={10} /> Entry Price required for Price-based exits
              </p>
            )}
              </div>
        </div>
      </div>

{/* --- STEP 2: EXITS --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Stop Loss Group */}
          <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
              {/* ADD SPEED TIP TOOLTIP HERE */}
                    <Tooltip 
                      variant="blue" 
                      backdrop 
                      content={
                        <div className="space-y-3 p-1">
                          <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                            Speed Tip
                          </div>
                          <p className="text-[15px] leading-relaxed text-gray-100">
                            In fast markets, you don’t need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">10–20 ticks</span> on ES, <span className="font-bold text-white">20–40 ticks</span> on NQ).
                          </p>
                          <p className="text-[15px] font-bold text-blue-300">
                            Backtesting your strategy is an excellent way to determine that number.
                          </p>
                          <p className="text-[13px] text-gray-400 border-t border-white/10 pt-2 italic">
                            The goal is to pre-calculate risk so you already know your contract size when the setup appears.
                          </p>
                        </div>
                      }
                    >
                      <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                    </Tooltip>
                  </div>
              <select 
                value={settings["Stop Loss Mode"] || "Ticks"} 
                onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Ticks">by Ticks</option>
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : "Ticks"} value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400"/>
            
            {/* Dynamic Helper Label */}
            {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Stop Loss Mode"] === "Price" 
                  ? (() => {
                      const pts = Math.abs(settings["Entry Price"] - settings["Stop Loss"]);
                      const ticks = Math.round(pts / (getTickSizeForProduct(settings["Product"]) || 0.25));
                      return `Est. Distance: ${pts.toFixed(2)} Points / ${ticks} Ticks`;
                    })()
                  : (() => {
                      const pts = Number(settings["Stop Loss"]) * (getTickSizeForProduct(settings["Product"]) || 0.25);
                      const price = Number(settings["Entry Price"]) - pts;
                      return `Est. Stop Price: ${price.toFixed(2)} (${pts.toFixed(2)} Points)`;
                    })()
                }
              </p>
            )}
          </div>

          {/* Take Profit Group */}
          <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
            <div className="flex justify-between items-center mb-2">
              <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
              <select 
                value={settings["Take Profit Mode"] || "Ticks"} 
                onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Ticks">by Ticks</option>
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : "Ticks"} value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
            
            {/* Dynamic Helper Label */}
            {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Take Profit Mode"] === "Price" 
                  ? (() => {
                      const pts = Math.abs(settings["Take Profit"] - settings["Entry Price"]);
                      const ticks = Math.round(pts / (getTickSizeForProduct(settings["Product"]) || 0.25));
                      return `Est. Distance: ${pts.toFixed(2)} Points / ${ticks} Ticks`;
                    })()
                  : (() => {
                      const pts = Number(settings["Take Profit"]) * (getTickSizeForProduct(settings["Product"]) || 0.25);
                      const price = Number(settings["Entry Price"]) + pts;
                      return `Est. Target Price: ${price.toFixed(2)} (${pts.toFixed(2)} Points)`;
                    })()
                }
              </p>
            )}
          </div>
        </div>
      </div>

      {/* --- STEP 3: RISK PARAMETERS --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
        <div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1">
              <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget<span className="text-red-500 text-lg">*</span></label>
              <Tooltip variant="neutral" backdrop content="The calculated dollar amount you are willing to lose if stopped out."><span className="cursor-help text-[10px] text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span></Tooltip>
            </div>
            <select value={settings["Risk Mode"]||"$"} onChange={e=>handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
              <option value="$">$ Amount</option>
              <option value="%">% of Balance</option>
            </select>
          </div>
          <input type="number" placeholder={settings["Risk Mode"] === "%" ? "e.g. 1.5" : "e.g. 150"} value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
        </div>
      </div>

      {/* --- PROJECTIONS ROW --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
         <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input type="number" value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} disabled className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" />
         </div>
         <button type="button" className={`w-full h-[42px] mt-auto bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setProjectionOpen(true)} disabled={!calculatedSummary}>Win Projections</button>
         <button type="button" className={`w-full h-[42px] mt-auto bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setLossProjectionOpen(true)} disabled={!calculatedSummary}>Loss Projections</button>
      </div>

    </div>
  );

const renderOptionsSettings = () => (
  <div className="space-y-6">
    
{/* --- COLLAPSIBLE BALANCES SECTION --- */}
    <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span>Account Cash Status</span>
          <div className="flex flex-wrap gap-2 text-sm font-normal">
            <span className="text-gray-500">
              Total Equity: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(settings["Beginning Balance"] || 0)}</span>
            </span>
            <span className="text-gray-500 hidden sm:inline">|</span>
            <span className="text-gray-500">
              Buying Power: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"] || 0)}</span>
            </span>
          </div>
        </div>
        <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
        <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
      </summary>
      
      {/* FIX: Changed md:grid-cols-4 to md:grid-cols-5 to fit all 5 fields on one line */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
          <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Account Equity</label>
          <input type="number" value={settings["Beginning Balance"] ?? ""} onChange={e => handleSettingChange("Beginning Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
        </div>
        {/* UNSETTLED FUNDS COLUMN */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <label className="block text-xs font-bold text-gray-500 uppercase">Unsettled Funds</label>
            <Tooltip variant="neutral" backdrop content={
              <div className="space-y-2 p-1">
                <p className="text-[15px] leading-relaxed text-gray-100">
                  Funds from closed Options trades that are waiting to settle (T+1 rule).
                </p>
                <p className="text-[15px] leading-relaxed text-blue-100">
                  Most Brokers replenish the Buying Power overnight. A <strong>Replenish</strong> button will appear when there is unsettled cash and you can also click it to manually settle them immediately.
                </p>
              </div>
            }>
              <span className="cursor-help text-[10px] text-gray-400 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
            </Tooltip>
          </div>
          <input type="number" disabled value={settings["Unsettled Balance"] || 0} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-600 bg-gray-100 cursor-not-allowed" />
        </div>

        {/* BUYING POWER COLUMN */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1">
              <label className="block text-xs font-bold text-gray-500 uppercase">Buying Power</label>
              <Tooltip variant="neutral" backdrop content={
                <div className="space-y-2 p-1">
                  <p className="text-[15px] leading-relaxed text-gray-100">
                    Your settled cash available to trade.
                  </p>
                  <p className="text-[15px] leading-relaxed text-blue-100">
                    When you enter an Options trade, the cost is deducted from here. When you close it, the proceeds go to <strong>Unsettled Funds</strong> until they clear.
                  </p>
                </div>
              }>
                <span className="cursor-help text-[10px] text-gray-400 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
              </Tooltip>
            </div>
            

          </div>
         <input type="number" value={settings["Running Balance"] !== undefined && settings["Running Balance"] !== "" ? Math.round(settings["Running Balance"] * 100) / 100 : ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
                    {Number(settings["Unsettled Balance"]) > 0 && (
              <button 
                onClick={handleReplenish} 
                disabled={savingSettings}
                type="button" 
                className="text-[10px] font-bold text-blue-500 hover:text-blue-700 underline flex items-center gap-1 disabled:opacity-50 mt-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                Replenish
              </button>
            )}
        </div>
      </div>
    </details>

    {/* --- STEP 1: THE SETUP --- */}
    <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
      {/* BUY ONLY NOTICE */}
  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
    <div className="bg-gray-300 dark:bg-gray-500 text-white rounded-full p-1 shadow-sm">
        <span className="flex items-center justify-center text-blue-500 dark:text-blue-400 hover:text-blue-500 transition-colors cursor-help">
          <FaExclamationTriangle size={16} />
        </span>
    </div>
   <div className="flex-1">
    <p className="text-[13px] font-bold text-blue-700 dark:text-blue-300">
      This calculator is currently configured for <span className="underline">Buying</span> Calls and Puts only for a Cash Account.
    </p>
    <p className="text-[12px] text-blue-600 dark:text-blue-400 mt-0.5">
      <strong>More Advanced Option Strategies coming soon!</strong> 
    </p>
  </div>
  </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Symbol<span className="text-red-500 text-lg">*</span></label>
          <input type="text" placeholder="e.g. SPY or AAPL" value={settings["Product"] || ""} onChange={e => handleSettingChange("Product", e.target.value.toUpperCase())} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
            Entry Premium {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
          </label>
          <input 
            type="number" 
            placeholder="e.g. 4500.25" 
            value={settings["Entry Price"] || ""} 
            onChange={e => handleSettingChange("Entry Price", e.target.value)} 
            className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100  transition-all outline-none ${
              showEntryWarning 
                ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
            }`} 
          />
          {showEntryWarning && (
            <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
              <FaExclamationTriangle size={10} /> Entry Price required for Price-based exits
            </p>
          )}
        </div>
      </div>
    </div>

    {/* --- STEP 2: EXITS --- */}
    <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Stop Loss Group */}
        <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
          <div className="flex justify-between items-center mb-2">
            <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
            <select 
              value={settings["Stop Loss Mode"] || "Premium"} 
              onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
              className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
            >
              <option value="Premium">by Premium</option>
              <option value="Ticks">by Ticks</option>
              <option value="Price">by Underlying Asset Price</option>
            </select>
          </div>
          <input 
            type="number" 
            placeholder={settings["Stop Loss Mode"] === "Price" ? "Underlying Price" : (settings["Stop Loss Mode"] === "Ticks" ? "Ticks" : "Premium Price")} 
            value={settings["Stop Loss"] || ""} 
            onChange={e=>handleSettingChange("Stop Loss",e.target.value)} 
            className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400" 
          />
          {/* Dynamic Helper Label */}
          {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && settings["Underlying Entry Price"] > 0 && settings["Delta"] > 0 && (
            <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
              {(() => {
                const entry = Number(settings["Entry Price"]);
                const uEntry = Number(settings["Underlying Entry Price"]);
                const delta = Number(settings["Delta"]);
                const slVal = Number(settings["Stop Loss"]);
                const optType = settings["Option Type"] || "Call";

                if (settings["Stop Loss Mode"] === "Price") {
                  let diff = slVal - uEntry;
                  if (optType === "Put") diff = uEntry - slVal;
                  const stopPremium = entry + (diff * delta);
                  
                  const uMove = Math.abs(slVal - uEntry);
                  const uTicks = Math.round(uMove * 100);
                  
                  return `Est. Stop Premium: $${Math.max(0, stopPremium).toFixed(2)} (Underlying Move: ${uMove.toFixed(2)} pts / ${uTicks} ticks)`;
                } else if (settings["Stop Loss Mode"] === "Ticks") {
                  const tickDist = slVal * 0.01;
                  const stopPrem = entry - tickDist;
                  const diffPrem = stopPrem - entry;
                  const stopPrice = optType === "Put" ? uEntry - (diffPrem / delta) : uEntry + (diffPrem / delta);
                  const uPoints = Math.abs(stopPrice - uEntry);
                  const uTicks = Math.round(uPoints * 100);
                  return `Est. Stop Premium: $${Math.max(0, stopPrem).toFixed(2)} / Underlying Stop: $${Math.max(0, stopPrice).toFixed(2)} (${uPoints.toFixed(2)} pts / ${uTicks} ticks)`;
                } else {
                  let diffPrem = slVal - entry;
                  const stopPrice = optType === "Put" ? uEntry - (diffPrem / delta) : uEntry + (diffPrem / delta);
                  const uPoints = Math.abs(stopPrice - uEntry);
                  const uTicks = Math.round(uPoints * 100);
                  return `Est. Underlying Stop: $${Math.max(0, stopPrice).toFixed(2)} (${uPoints.toFixed(2)} pts / ${uTicks} ticks)`;
                }
              })()}
            </p>
          )}
        </div>

        {/* Take Profit Group */}
        <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
          <div className="flex justify-between items-center mb-2">
            <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
            <select 
              value={settings["Take Profit Mode"] || "Premium"} 
              onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
              className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
            >
              <option value="Premium">by Premium</option>
              <option value="Ticks">by Ticks</option>
              <option value="Price">by Underlying Asset Price</option>
            </select>
          </div>
          <input 
            type="number" 
            placeholder={settings["Take Profit Mode"] === "Price" ? "Underlying Price" : (settings["Take Profit Mode"] === "Ticks" ? "Ticks" : "Premium Price")} 
            value={settings["Take Profit"] || ""} 
            onChange={e=>handleSettingChange("Take Profit",e.target.value)} 
            className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" 
          />
          {/* Dynamic Helper Label */}
          {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && settings["Underlying Entry Price"] > 0 && settings["Delta"] > 0 && (
            <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
              {(() => {
                const entry = Number(settings["Entry Price"]);
                const uEntry = Number(settings["Underlying Entry Price"]);
                const delta = Number(settings["Delta"]);
                const tpVal = Number(settings["Take Profit"]);
                const optType = settings["Option Type"] || "Call";

                if (settings["Take Profit Mode"] === "Price") {
                  let diff = tpVal - uEntry;
                  if (optType === "Put") diff = uEntry - tpVal;
                  const tpPremium = entry + (diff * delta);
                  
                  const uMove = Math.abs(tpVal - uEntry);
                  const uTicks = Math.round(uMove * 100);
                  
                  return `Est. Target Premium: $${Math.max(0, tpPremium).toFixed(2)} (Underlying Move: ${uMove.toFixed(2)} pts / ${uTicks} ticks)`;
                } else if (settings["Take Profit Mode"] === "Ticks") {
                  const tickDist = tpVal * 0.01;
                  const tpPrem = entry + tickDist;
                  const diffPrem = tpPrem - entry;
                  const tpPrice = optType === "Put" ? uEntry - (diffPrem / delta) : uEntry + (diffPrem / delta);
                  const uPoints = Math.abs(tpPrice - uEntry);
                  const uTicks = Math.round(uPoints * 100);
                  return `Est. Target Premium: $${Math.max(0, tpPrem).toFixed(2)} / Underlying Target: $${Math.max(0, tpPrice).toFixed(2)} (${uPoints.toFixed(2)} pts / ${uTicks} ticks)`;
                } else {
                  let diffPrem = tpVal - entry;
                  const tpPrice = optType === "Put" ? uEntry - (diffPrem / delta) : uEntry + (diffPrem / delta);
                  const uPoints = Math.abs(tpPrice - uEntry);
                  const uTicks = Math.round(uPoints * 100);
                  return `Est. Underlying Target: $${Math.max(0, tpPrice).toFixed(2)} (${uPoints.toFixed(2)} pts / ${uTicks} ticks)`;
                }
              })()}
            </p>
          )}
        </div>

      </div>

      <div className="mt-4">
        {(() => {
           const forceShowAdvanced = settings["Stop Loss Mode"] === 'Price' || settings["Take Profit Mode"] === 'Price';
           const missingData = !settings["Underlying Entry Price"] || !settings["Delta"];
           const showYellowWarning = forceShowAdvanced && missingData;

           return (
             <>
               {!forceShowAdvanced && (
                 <button 
                   type="button" 
                   onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} 
                   className="text-sm font-bold flex flex-wrap items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                 >
                   {showAdvancedOptions ? "− Hide" : "+ Add"} Underlying Entry & Delta 
                   <span className="text-gray-500 dark:text-gray-400 font-normal ml-1">
                     (Optional: Shows Stock/Asset Price targets in Summary)
                   </span>
                 </button>
               )}
               {(showAdvancedOptions || forceShowAdvanced) && (
                 <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border mt-3 transition-all duration-300 ${
                   showYellowWarning 
                     ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500 shadow-[0_0_15px_rgba(234,179,8,0.2)] ring-1 ring-yellow-500" 
                     : "bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800"
                 }`}>
                   
                   {/* DYNAMIC HEADER */}
                   <div className="md:col-span-3 mb-[-5px]">
                     {forceShowAdvanced ? (
                       <div className={`text-xs font-bold flex items-center gap-1.5 ${showYellowWarning ? "text-yellow-700 dark:text-yellow-400" : "text-blue-700 dark:text-blue-300"}`}>
                         {showYellowWarning && <FaExclamationTriangle />}
                         Required for Underlying Price calculations:
                       </div>
                     ) : (
                       <div className="text-xs font-bold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                         <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                         Optional: Enter these to see equivalent Stock/Asset prices in the Summary table.
                       </div>
                     )}
                   </div>

                   <div>
                     <label className="block text-xs font-bold mb-1 text-gray-500 uppercase">Option Type</label>
                     <select 
                       value={settings["Option Type"] || "Call"} 
                       onChange={e => handleSettingChange("Option Type", e.target.value)} 
                       className="w-full px-3 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 outline-none"
                     >
                        <option value="Call">Call</option>
                        <option value="Put">Put</option>
                     </select>
                   </div>
                   
                   {/* UNDERLYING ENTRY WITH FORCED GLOW */}
                   <div>
                     <label className={`block text-xs font-bold mb-1 uppercase ${showYellowWarning && !settings["Underlying Entry Price"] ? "text-yellow-700 dark:text-yellow-400" : "text-gray-500"}`}>
                       Underlying Entry {forceShowAdvanced && "*"}
                     </label>
                     <input 
                       type="number" 
                       placeholder="e.g. 602.50" 
                       value={settings["Underlying Entry Price"] || ""} 
                       onChange={e => handleSettingChange("Underlying Entry Price", e.target.value)} 
                       className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-800 dark:text-white outline-none transition-all ${
                         showYellowWarning && !settings["Underlying Entry Price"] 
                           ? "border-yellow-500 ring-2 ring-yellow-500/50" 
                           : "border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
                       }`} 
                     />
                   </div>
                   
                   {/* DELTA WITH FORCED GLOW */}
                   <div>
                     <div className="flex items-center gap-1 mb-1">
                       <label className={`block text-xs font-bold uppercase ${showYellowWarning && !settings["Delta"] ? "text-yellow-700 dark:text-yellow-400" : "text-gray-500"}`}>
                         Delta {forceShowAdvanced && "*"}
                       </label>
                       <Tooltip variant="neutral" backdrop content={
                         <div className="space-y-2 p-1">
                           <p className="text-[14px] leading-relaxed text-gray-100">
                             Delta estimates how much the option's premium will change for a $1 move in the underlying stock.
                           </p>
                           <p className="text-[14px] leading-relaxed font-bold text-blue-200">
                             Because Delta constantly changes as the stock price moves, exact projections are difficult. Simply use the <span className="text-yellow-400">current Delta</span> as a reasonable estimate for your risk and targets.
                           </p>
                         </div>
                       }>
                         <span className="cursor-help text-[10px] text-gray-400 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                       </Tooltip>
                     </div>
                     <input 
                       type="number" 
                       step="0.01" 
                       placeholder="e.g. 0.45" 
                       value={settings["Delta"] || ""} 
                       onChange={e => handleSettingChange("Delta", e.target.value)} 
                       className={`w-full px-3 py-2 rounded-lg border bg-white dark:bg-gray-800 dark:text-white outline-none transition-all ${
                         showYellowWarning && !settings["Delta"] 
                           ? "border-yellow-500 ring-2 ring-yellow-500/50" 
                           : "border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
                       }`} 
                     />
                   </div>

                 </div>
               )}
             </>
           );
        })()}
      </div>
    </div>

{/* --- STEP 3: RISK PARAMETERS --- */}
    <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
      <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Capital Limits</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1">
              <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget</label>
              <Tooltip variant="neutral" backdrop content="Max amount you are willing to lose if stopped out.">
                {/* FIXED: Changed dark:text-gray-400 to dark:text-gray-200 */}
                <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
              </Tooltip>
            </div>
            <select value={settings["Risk Mode"]||"$"} onChange={e=>handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
              <option value="$">$ Amount</option>
              <option value="%">% of Balance</option>
            </select>
          </div>
          <input type="number" value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1">
              <label className="font-bold text-gray-800 dark:text-gray-200">Investment Cap</label>
              <Tooltip variant="neutral" backdrop content="Max cash tied up in the trade upfront.">
                {/* FIXED: Changed dark:text-gray-400 to dark:text-gray-200 */}
                <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
              </Tooltip>
            </div>
            <select value={settings["Investment Cap Mode"]||"$"} onChange={e=>handleSettingChange("Investment Cap Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
              <option value="$">$ Amount</option>
              <option value="%">% of Balance</option>
            </select>
          </div>
          <input type="number" placeholder={settings["Investment Cap Mode"] === "%" ? "e.g. 50" : "e.g. 500"} value={settings["Investment Amount"] || ""} onChange={e => handleSettingChange("Investment Amount", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
        </div>
      </div>
    </div>

    {/* --- PROJECTIONS ROW --- */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
       <div>
          <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
          <input type="number" value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} disabled className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" />
       </div>
       <button type="button" className={`w-full h-[42px] mt-auto bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setProjectionOpen(true)} disabled={!calculatedSummary}>Win Projections</button>
       <button type="button" className={`w-full h-[42px] mt-auto bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setLossProjectionOpen(true)} disabled={!calculatedSummary}>Loss Projections</button>
    </div>

  </div>
);

const renderStocksSettings = () => (
    <div className="space-y-6">
      
{/* --- COLLAPSIBLE BALANCES SECTION --- */}
    <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
      <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span>Account Cash Status</span>
          <div className="flex flex-wrap gap-2 text-sm font-normal">
            <span className="text-gray-500">
              Total Equity: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(settings["Beginning Balance"] || 0)}</span>
            </span>
            <span className="text-gray-500 hidden sm:inline">|</span>
            <span className="text-gray-500">
              Buying Power: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"] || 0)}</span>
            </span>
          </div>
        </div>
        <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
        <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
      </summary>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
          <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Account Equity</label>
          <input type="number" value={settings["Beginning Balance"] ?? ""} onChange={e => handleSettingChange("Beginning Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buying Power</label>
          <input type="number" value={settings["Running Balance"] !== undefined && settings["Running Balance"] !== "" ? Math.round(settings["Running Balance"] * 100) / 100 : ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
      </div>
    </details>

      {/* --- STEP 1: THE SETUP --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
        {/* STOCKS LONG ONLY NOTICE */}
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-3">
            <div className="bg-gray-300 dark:bg-gray-500 text-white rounded-full p-1 shadow-sm">
        <span className="flex items-center justify-center text-blue-500 dark:text-blue-400 hover:text-blue-500 transition-colors cursor-help">
          <FaExclamationTriangle size={16} />
        </span>
    </div>
            <p className="text-[13px] font-bold text-blue-700 dark:text-blue-300">
                This calculator is currently configured for <span className="underline">Buying</span> Stocks only. <span className="text-blue-500 dark:text-blue-400 font-normal ml-1">Shorting capabilities are coming soon!</span>
              </p>
          </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Ticker Symbol<span className="text-red-500 text-lg">*</span></label>
            <input type="text" placeholder="e.g. AAPL" value={settings["Product"] || ""} onChange={e => handleSettingChange("Product", e.target.value.toUpperCase())} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
              Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
            </label>
            <input 
              type="number" 
              placeholder="e.g. 4500.25" 
              value={settings["Entry Price"] || ""} 
              onChange={e => handleSettingChange("Entry Price", e.target.value)} 
              className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${
                showEntryWarning 
                  ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                  : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
              }`} 
            />
            {showEntryWarning && (
              <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
                <FaExclamationTriangle size={10} /> Entry Price required for Price-based exits
              </p>
            )}
          </div>
        </div>
      </div>

      {/* --- STEP 2: EXITS --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
                <Tooltip 
                  variant="blue" 
                  backdrop 
                  content={
                    <div className="space-y-3 p-1">
                      <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                        Speed Tip
                      </div>
                      <p className="text-[15px] leading-relaxed text-gray-100">
                        In fast markets, you don't need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">$0.10-0.25$</span> pts on low-priced stocks, <span className="font-bold text-white">$1.00-2.00$</span> pts on high-beta names).
                      </p>
                      <p className="text-[15px] font-bold text-blue-300">
                        Backtesting your strategy is an excellent way to determine that number.
                      </p>
                      <p className="text-[13px] text-gray-400 border-t border-white/10 pt-2 italic">
                        The goal is to pre-calculate risk so you already know your share quantity when the setup appears.
                      </p>
                    </div>
                  }
                >
                  <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                </Tooltip>
              </div>
            <select 
                value={settings["Stop Loss Mode"] || "Points"} 
                onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Points">by Points ($)</option>
                <option value="Ticks">by Ticks</option>  {/* <-- ADDED */}
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : (settings["Stop Loss Mode"] === "Ticks" ? "Ticks" : "Points")} value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400"/>
            
            {/* Dynamic Helper */}
            {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Stop Loss Mode"] === "Price" 
                  ? `Points: ${Math.abs(settings["Entry Price"] - settings["Stop Loss"]).toFixed(2)}`
                  : settings["Stop Loss Mode"] === "Ticks"
                  ? `Est. Stop Price: ${formatCurrency(Number(settings["Entry Price"]) - (Number(settings["Stop Loss"]) * 0.01))}`
                  : `Est. Stop Price: ${formatCurrency(Number(settings["Entry Price"]) - Number(settings["Stop Loss"]))}`}
              </p>
            )}
          </div>

          {/* Take Profit Group */}
          <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
            <div className="flex justify-between items-center mb-2">
              <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
              <select 
                value={settings["Take Profit Mode"] || "Points"} 
                onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Points">by Points ($)</option>
                <option value="Ticks">by Ticks</option> {/* <-- ADDED */}
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : (settings["Take Profit Mode"] === "Ticks" ? "Ticks" : "Points")} value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
            
            {/* Dynamic Helper */}
            {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Take Profit Mode"] === "Price" 
                  ? `Points: ${Math.abs(settings["Take Profit"] - settings["Entry Price"]).toFixed(2)}`
                  : settings["Take Profit Mode"] === "Ticks"
                  ? `Est. Target Price: ${formatCurrency(Number(settings["Entry Price"]) + (Number(settings["Take Profit"]) * 0.01))}`
                  : `Est. Target Price: ${formatCurrency(Number(settings["Entry Price"]) + Number(settings["Take Profit"]))}`}
              </p>
            )}
          </div>

        </div>
      </div>


{/* --- STEP 3: CAPITAL LIMITS (Stocks) --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Capital Limits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Risk Budget Input */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget</label>
                <Tooltip variant="neutral" backdrop content="The maximum dollar amount or % you are willing to lose if your stop loss is hit.">
                  <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                </Tooltip>
              </div>
              <select value={settings["Risk Mode"]||"$"} onChange={e=>handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
                <option value="$">$ Amount</option>
                <option value="%">% of Balance</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Risk Mode"] === "%" ? "e.g. 1" : "e.g. 100"} value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
          </div>

          {/* NEW: Investment Cap Input */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Investment Cap</label>
                <Tooltip variant="neutral" backdrop content="The maximum amount of total cash (buying power) you are willing to tie up in this one position.">
                  <span className="cursor-help text-[10px] text-gray-600 dark:text-gray-200 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                </Tooltip>
              </div>
              <select value={settings["Investment Cap Mode"]||"$"} onChange={e=>handleSettingChange("Investment Cap Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
                <option value="$">$ Amount</option>
                <option value="%">% of Balance</option>
              </select>
            </div>
            <input type="number" placeholder="Optional Cap" value={settings["Investment Amount"] || ""} onChange={e => handleSettingChange("Investment Amount", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                 <div>
                    <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
                    <input 
                      type="number" 
                      value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} 
                      disabled 
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" 
                    />
                 </div>
                 <button 
                   type="button" 
                   className={`w-full h-[42px] mt-auto bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} 
                   onClick={() => setProjectionOpen(true)} 
                   disabled={!calculatedSummary}
                 >
                   Win Projections
                 </button>
                 <button 
                   type="button" 
                   className={`w-full h-[42px] mt-auto bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} 
                   onClick={() => setLossProjectionOpen(true)} 
                   disabled={!calculatedSummary}
                 >
                   Loss Projections
                 </button>
              </div>

    </div>
  );

  const renderForexSettings = () => (

    <div className="space-y-6">
      
{/* --- COLLAPSIBLE BALANCES SECTION --- */}
    <details className="group bg-gray-50 dark:bg-gray-800/30 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <summary className="font-bold text-gray-700 dark:text-gray-300 cursor-pointer list-none flex justify-between items-center">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
          <span>Account Cash Status</span>
          <div className="flex flex-wrap gap-2 text-sm font-normal">
            <span className="text-gray-500">
              Total Equity: <span className="font-bold text-blue-600 dark:text-blue-400">{isPrivacyMode ? "******" : formatCurrency(settings["Beginning Balance"] || 0)}</span>
            </span>
            <span className="text-gray-500 hidden sm:inline">|</span>
            <span className="text-gray-500">
              Buying Power: <span className="font-bold text-green-600 dark:text-green-400">{isPrivacyMode ? "******" : formatCurrency(settings["Running Balance"] || 0)}</span>
            </span>
          </div>
        </div>
        <span className="text-blue-500 text-sm group-open:hidden">+ Edit Balances</span>
        <span className="text-blue-500 text-sm hidden group-open:block">- Hide</span>
      </summary>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Initial Balance</label>
              <input 
                type="text" 
                readOnly 
                /* Ensures $0.00 is shown instead of a blank field if the value is 0 */
                value={formatCurrency(settings["Initial Balance"] ?? 0)} 
                className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-900 cursor-not-allowed" 
              />
            </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Account Goal</label>
          <input type="number" value={settings["Account Goal"] || ""} onChange={e => handleSettingChange("Account Goal", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Total Account Equity</label>
          <input type="number" value={settings["Beginning Balance"] ?? ""} onChange={e => handleSettingChange("Beginning Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buying Power</label>
          <input type="number" value={settings["Running Balance"] !== undefined && settings["Running Balance"] !== "" ? Math.round(settings["Running Balance"] * 100) / 100 : ""} onChange={e => handleSettingChange("Running Balance", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400" />
        </div>
      </div>
    </details>

      {/* --- STEP 1: THE SETUP --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400 mb-4 uppercase tracking-wide">1. Trade Setup</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Wrap the Pair label and select in a div to take up one grid cell */}
          <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Pair<span className="text-red-500 text-lg">*</span></label>
            <select 
              value={settings["Product"] || ""} 
              onChange={e => handleSettingChange("Product", e.target.value)} 
              className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 bg-gray-50 focus:ring-2 focus:ring-blue-400"
            >
              <option value="">Select Forex Pair</option>
              {favOpts.length > 0 && (
                <optgroup label="♥ Favorites">
                  {favOpts.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All Forex">
                {nonFavOpts.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Entry Price already has a wrapper div, so it takes up the second grid cell */}
          <div>
            <label className={`block font-bold mb-1 transition-colors ${showEntryWarning ? "text-red-600 dark:text-red-400" : "text-gray-800 dark:text-gray-200"}`}>
              Entry Price {entryRequired ? <span className="text-red-500">* (Required)</span> : "(Optional)"}
            </label>
            <input 
              type="number" 
              placeholder="e.g. 4500.25" 
              value={settings["Entry Price"] || ""} 
              onChange={e => handleSettingChange("Entry Price", e.target.value)} 
              className={`w-full px-4 py-2 rounded-lg border dark:bg-gray-700 bg-gray-100 transition-all outline-none ${
                showEntryWarning 
                  ? "border-red-500 ring-2 ring-red-500/20 focus:ring-red-400 shadow-[0_0_10px_rgba(239,68,68,0.1)]" 
                  : "dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"
              }`} 
            />
            {showEntryWarning && (
              <p className="text-[10px] font-bold text-red-500 mt-1 uppercase flex items-center gap-1 animate-pulse">
                <FaExclamationTriangle size={10} /> Entry Price required for Price-based exits
              </p>
            )}
          </div>

        </div>
      </div>

{/* --- STEP 2: EXITS --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400 mb-4 uppercase tracking-wide">2. Planned Exits</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          <div className="bg-red-50/50 dark:bg-red-900/10 p-3 rounded-lg border border-red-100 dark:border-red-900/30">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-1">
                <label className="font-bold text-gray-800 dark:text-gray-200">Stop Loss<span className="text-red-500 text-lg">*</span></label>
                <Tooltip 
                  variant="blue" 
                  backdrop 
                  content={
                    <div className="space-y-3 p-1">
                      <div className="font-bold text-blue-400 uppercase text-[13px] tracking-wider border-b border-white/10 pb-1">
                        Speed Tip
                      </div>
                      <p className="text-[15px] leading-relaxed text-gray-100">
                        In fast markets, you don't need an exact stop loss price. Use a reasonable estimate based on your strategy (example: <span className="font-bold text-white">$5-10$</span> pips on EURUSD, <span className="font-bold text-white">$10-20$</span> pips on GBPJPY).
                      </p>
                      <p className="text-[15px] font-bold text-blue-300">
                        Backtesting your strategy is an excellent way to determine that number.
                      </p>
                      <p className="text-[13px] text-gray-400 border-t border-white/10 pt-2 italic">
                        The goal is to pre-calculate risk so you already know your lot size when the setup appears.
                      </p>
                    </div>
                  }
                >
                  <span className="cursor-help text-[10px] text-blue-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span>
                </Tooltip>
              </div>              <select 
                value={settings["Stop Loss Mode"] || "Pips"} 
                onChange={e => handleSettingChange("Stop Loss Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Pips">by {fxUnit}</option>
                {isIdx && <option value="Ticks">by Ticks</option>}
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Stop Loss Mode"] === "Price" ? "Exit Price" : (settings["Stop Loss Mode"] === "Ticks" ? "Ticks" : fxUnit)} value={settings["Stop Loss"]||""} onChange={e=>handleSettingChange("Stop Loss",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-red-400"/>
            
            {settings["Entry Price"] > 0 && settings["Stop Loss"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Stop Loss Mode"] === "Price" 
                  ? (() => {
                      const dist = Math.abs(settings["Entry Price"] - settings["Stop Loss"]) / getPipSizeForSymbol(settings["Product"]);
                      return `Est. Distance: ${Math.round(dist * 100) / 100} ${fxUnit} / ${isIdx ? Math.round(dist * 100) : Math.round(dist * 10 * 100) / 100} Ticks`;
                    })()
                  : `Est. Stop Price: ${(Number(settings["Entry Price"]) - ((settings["Stop Loss Mode"] === "Ticks" ? Number(settings["Stop Loss"]) / (isIdx ? 100 : 10) : Number(settings["Stop Loss"])) * getPipSizeForSymbol(settings["Product"]))).toFixed(isIdx ? 2 : 5)}`}
              </p>
            )}
          </div>

          <div className="bg-green-50/50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
            <div className="flex justify-between items-center mb-2">
              <label className="font-bold text-gray-800 dark:text-gray-200">Target</label>
              <select 
                value={settings["Take Profit Mode"] || "Pips"} 
                onChange={e => handleSettingChange("Take Profit Mode", e.target.value)}
                className="text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1"
              >
                <option value="Pips">by {fxUnit}</option>
                {isIdx && <option value="Ticks">by Ticks</option>}
                <option value="Price">by Price</option>
              </select>
            </div>
            <input type="number" placeholder={settings["Take Profit Mode"] === "Price" ? "Exit Price" : (settings["Take Profit Mode"] === "Ticks" ? "Ticks" : fxUnit)} value={settings["Take Profit"]||""} onChange={e=>handleSettingChange("Take Profit",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-400"/>
            
            {settings["Entry Price"] > 0 && settings["Take Profit"] > 0 && (
              <p className="text-[11px] font-bold text-gray-600 dark:text-gray-400 mt-2 italic">
                {settings["Take Profit Mode"] === "Price" 
                  ? (() => {
                      const dist = Math.abs(settings["Take Profit"] - settings["Entry Price"]) / getPipSizeForSymbol(settings["Product"]);
                      return `Est. Distance: ${Math.round(dist * 100) / 100} ${fxUnit} / ${isIdx ? Math.round(dist * 100) : Math.round(dist * 10 * 100) / 100} Ticks`;
                    })()
                  : `Est. Target Price: ${(Number(settings["Entry Price"]) + ((settings["Take Profit Mode"] === "Ticks" ? Number(settings["Take Profit"]) / (isIdx ? 100 : 10) : Number(settings["Take Profit"])) * getPipSizeForSymbol(settings["Product"]))).toFixed(isIdx ? 2 : 5)}`}
              </p>
            )}          
          </div>
        </div>
      </div>

      {/* --- STEP 3: RISK PARAMETERS --- */}
      <div className="p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-bold text-orange-600 dark:text-orange-400 mb-4 uppercase tracking-wide">3. Risk Parameters</h3>
        <div>
          <div className="flex justify-between items-center mb-1">
            <div className="flex items-center gap-1">
              <label className="font-bold text-gray-800 dark:text-gray-200">Risk Budget<span className="text-red-500 text-lg">*</span></label>
              <Tooltip variant="neutral" backdrop content="The calculated dollar amount you are willing to lose if stopped out."><span className="cursor-help text-[10px] text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full w-4 h-4 flex items-center justify-center font-bold">?</span></Tooltip>
            </div>
            <select value={settings["Risk Mode"]||"$"} onChange={e=>handleSettingChange("Risk Mode", e.target.value)} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded p-1">
              <option value="$">$ Amount</option>
              <option value="%">% of Balance</option>
            </select>
          </div>
          <input type="number" placeholder={settings["Risk Mode"] === "%" ? "e.g. 1.5" : "e.g. 150"} value={settings["Risk Mode"]==='$' ? settings["Risk Dollar Amount"] : settings["Risk Percentage"]} onChange={e=>handleSettingChange(settings["Risk Mode"]==='$'?"Risk Dollar Amount":"Risk Percentage", e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-orange-400" />
        </div>
      </div>

      {/* --- PROJECTIONS ROW --- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
         <div>
            <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200"># Trades to Goal</label>
            <input type="number" value={calculatedSummary && winProjection ? winProjection.tradeCount : ""} disabled className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 dark:text-white cursor-not-allowed" />
         </div>
         <button type="button" className={`w-full h-[42px] mt-auto bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setProjectionOpen(true)} disabled={!calculatedSummary}>Win Projections</button>
         <button type="button" className={`w-full h-[42px] mt-auto bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition ${!calculatedSummary ? "opacity-50 cursor-not-allowed" : ""}`} onClick={() => setLossProjectionOpen(true)} disabled={!calculatedSummary}>Loss Projections</button>
      </div>

    </div>
  );

    const isIdx = isIndexSymbol(settings["Product"]);
    const fxUnit = isIdx ? "Points" : "Pips";

    // Entry is required if either exit is set to 'Price' and has a value
  const entryRequired = 
    assetClass === "Options" || // Always required for Options
    assetClass === "Stocks" ||  // Always required for Stocks
    settings["Stop Loss Mode"] === "Price" || // Required for Futures/Forex if SL is by Price
    settings["Take Profit Mode"] === "Price"; // Required for Futures/Forex if TP is by Price
  
  const showEntryWarning = entryRequired && !settings["Entry Price"];

  return (
    <div className="space-y-8 max-w-5xl mx-auto py-10">
      
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
          <div className="flex items-center gap-2">
            <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300 rounded-full p-2 shadow-sm"><FaChartLine size={20} /></span>
            <h2 className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">Trade Setup (Mixed)</h2>
          </div>
                      {/* NEW STRATEGY CHECKLIST BUTTON */}
          <button 
            onClick={() => setShowStrategyModal(true)}
            className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50 font-bold px-4 py-2 rounded-lg transition"
          >
            <FaClipboardList /> Strategy Checklist
          </button>
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
           {/* Filter ASSET_CLASSES to completely hide Stocks, and restrict Options to Admins */}
           {ASSET_CLASSES.filter(ac => ac !== "Stocks" && (isAdmin || ac !== "Options")).map(ac => (
             <button
               key={ac}
               onClick={() => handleAssetClassChange(ac)}
               className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${assetClass === ac ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-300 shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-gray-700"}`}
             >
               {ac}
             </button>
           ))}
          </div>
        </div>

        {assetClass === "Futures" && renderFuturesSettings()}
        {assetClass === "Forex" && renderForexSettings()}
        {assetClass === "Stocks" && renderStocksSettings()}
        {assetClass === "Options" && renderOptionsSettings()}

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Goal</span>
              <span>{formatCurrency(dailyGoalProg.done)} / {formatCurrency(dailyGoalProg.target)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${dailyGoalProg.pct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Daily Loss Limit</span>
              <span>{formatCurrency(dailyLossProg.lost)} / {formatCurrency(dailyLossProg.limit)}</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              <div className="h-full bg-red-500 transition-all duration-500" style={{ width: `${dailyLossProg.pct}%` }} />
            </div>
          </div>
        </div>
        
        <div className="mt-6">
            <div className="flex justify-between mb-1 text-sm font-bold text-gray-800 dark:text-gray-200">
              <span>Account Goal Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
                <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
        </div>

        <div className="col-span-3 border-t border-gray-200 dark:border-gray-700 my-8" />

        <div className="max-w-lg mx-auto border rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
          <h3 className="text-lg font-bold mb-2 flex items-center dark:text-white gap-2"><IconGoal />Summary</h3>
          {renderSummaryContent()}
        </div>

        <div className="flex justify-end mt-6">
          <button 
            // FIX: Wake up auto-sync if they manually click Calculate
            onClick={() => { setAutoSyncEnabled(true); handleCalculate(true, false); }} 
            disabled={savingSettings || !canCalculate}
            className="bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold px-6 py-3 rounded-xl shadow hover:from-blue-700 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSettings ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>


{!settings["Calculator Only"] && (
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl border border-gray-100 dark:border-gray-700 p-8 animate-fadeIn">
        <div className="flex items-center mb-6 border-b border-gray-100 dark:border-gray-700 pb-4">
          <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 rounded-full p-2 mr-2 shadow-sm">
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </span>
          <div>
            <h3 className="text-xl font-bold text-yellow-700 dark:text-yellow-400">Journal Entry</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Log your trade details below. <span className="text-red-500">* denotes required</span></p>
          </div>
        </div>

        <div className="space-y-8">
          
          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-blue-500 pl-3">1. Setup & Context</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Date<span className="text-red-500 text-lg">*</span></label>
                <input type="date" value={journal.Date} onChange={e=>handleJournalChange("Date",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
              </div>

              {!settings["Hide Session"] && (
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Session</label>
                <select id="session" value={journal.Session} onChange={e=>handleJournalChange("Session",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400">
                  <option value="">Select</option>
                  {SESSION_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              )}

              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Timeframe</label>
                <input id="timeframe" list="tf-options" value={journal.Timeframe} onChange={e=>handleJournalChange("Timeframe",e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                <datalist id="tf-options">{TIMEFRAME_OPTIONS.map(t=><option key={t} value={t}/>)}</datalist>
              </div>
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Position</label>
                <select 
                  id="position" 
                  // LOCKS TO "BUY" FOR OPTIONS OR STOCKS
                  value={(assetClass === "Options" || assetClass === "Stocks") ? "Buy" : journal.Position} 
                  onChange={e => handleJournalChange("Position", e.target.value)} 
                  disabled={assetClass === "Options" || assetClass === "Stocks"}
                  className={`w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 ${(assetClass === "Options" || assetClass === "Stocks") ? "bg-gray-100 cursor-not-allowed opacity-75" : ""}`}
                >
                  <option value="">Select</option>
                  {POSITION_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {(assetClass === "Options" || assetClass === "Stocks") && (
                  <p className="text-[10px] text-blue-500 font-bold mt-1 uppercase italic">Locked to Buy (Long Only)</p>
                )}
              </div>
            </div>
            {assetClass === "Options" && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
                <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Option Type</label>
                  <select value={journal["Option Type"] || "Call"} onChange={e => handleJournalChange("Option Type", e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400">
                    <option value="Call">Call</option>
                    <option value="Put">Put</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Strike Price</label>
                  <input type="text" placeholder="e.g. 605" value={journal["Strike"] || ""} onChange={e => handleJournalChange("Strike", e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
                </div>
                <div>
                  <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Exp. Date</label>
                  <input type="date" value={journal["Expiration"] || ""} onChange={e => handleJournalChange("Expiration", e.target.value)} className="w-full px-4 py-2 h-[42px] rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
                </div>
              </div>
            )}
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-purple-500 pl-3">2. Execution</h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Entry Details</label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                    <div className="relative flex items-center">
                      <input 
                        id="entryTime" 
                        type="time" 
                        ref={entryTimeRef} 
                        value={to24HourTime(journal["Entry Time"])} 
                        onChange={e => {
                          const rawTime24 = e.target.value;
                          setJournal(prev => {
                            const nextState = { ...prev, "Entry Time": toAmPm(rawTime24) };
                            
                            // Auto-populate Session only if it is currently empty
                            if (!prev.Session || prev.Session === "") {
                              const calculatedSession = getSessionFromTime(rawTime24);
                              if (calculatedSession) {
                                nextState.Session = calculatedSession;
                              }
                            }
                            
                            return nextState;
                          });
                        }} 
                        className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" 
                      />
                      <FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={()=>entryTimeRef.current?.showPicker && entryTimeRef.current.showPicker()} />
                    </div>
                  </div>
                  {!settings["Hide Entry Price"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Price</label>
                    <input id="entryPrice" type="number" value={journal["Entry Price"]||""} onChange={e=>handleJournalChange("Entry Price",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                  </div>
                  )}

                  {!settings["Hide Entry Strategy"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                    <input id="entryStrategy" list="entry-strats" value={journal["Entry Strategy"]} onChange={e=>handleJournalChange("Entry Strategy",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                    <datalist id="entry-strats">{entryStrategies.map(s=><option key={s} value={s}/>)}</datalist>
                  </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Exit Details</label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Time</label>
                    <div className="relative flex items-center">
                      <input id="exitTime" type="time" ref={exitTimeRef} value={to24HourTime(journal["Exit Time"])} onChange={e=>setJournal(prev=>({...prev,"Exit Time":toAmPm(e.target.value)}))} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400" />
                      <FaRegClock className="absolute right-3 text-lg text-gray-400 cursor-pointer hover:text-blue-500" onClick={()=>exitTimeRef.current?.showPicker && exitTimeRef.current.showPicker()} />
                    </div>
                  </div>

                   {!settings["Hide Exit Price"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                      {assetClass === "Options" ? "Exit Premium ($)" : "Exit Price"}
                      {assetClass === "Options" && (
                        <Tooltip 
                          variant="neutral" 
                          backdrop 
                          content={
                            settings["Take Profit Mode"] === "Price" ? (
                              <div className="text-xs space-y-1">
                                <p className="font-bold border-b border-white/20 pb-1">Target Projection:</p>
                                <p>Stock Move: {(Number(settings["Take Profit"]) - Number(settings["Underlying Entry Price"])).toFixed(2)} pts</p>
                                <p>Prem. Gain: (Move × {settings["Delta"] || 0}) = +{( (Number(settings["Take Profit"]) - Number(settings["Underlying Entry Price"])) * Number(settings["Delta"] || 0) ).toFixed(2)}</p>
                                <p className="text-green-400 font-bold">Projected Exit: ${(Number(settings["Entry Price"]) + ((Number(settings["Take Profit"]) - Number(settings["Underlying Entry Price"])) * Number(settings["Delta"] || 0))).toFixed(2)}</p>
                              </div>
                            ) : "Enter your target exit premium."
                          }
                        >
                          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-gray-200 dark:bg-gray-600 text-[9px] font-bold text-gray-600 dark:text-gray-300 cursor-help">?</span>
                        </Tooltip>
                      )}
                    </label>
                    <input id="exitPrice" type="number" value={journal["Exit Price"]||""} onChange={e=>handleJournalChange("Exit Price",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                  </div>
                  )}

                   {!settings["Hide Exit Strategy"] && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Strategy</label>
                    <input id="exitStrategy" list="exit-strats" value={journal["Exit Strategy"]} onChange={e=>handleJournalChange("Exit Strategy",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400"/>
                    <datalist id="exit-strats">{exitStrategies.map(s=><option key={s} value={s}/>)}</datalist>
                  </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col justify-center bg-blue-50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800">
                <label className="block text-xs font-bold text-gray-400 dark:text-gray-300 uppercase mb-3">Quantity</label>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                      Actual {getUnitLabel(assetClass)}<span className="text-red-500 text-lg">*</span>
                    </label>
                    <input id="contracts" type="number" value={journal.Size||""} onChange={e=>handleJournalChange("Size",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 font-mono" placeholder={calculatedSummary?.size||"0"} />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200">
                      {assetClass === "Futures" ? "Ticks" : assetClass === "Forex" ? "Pips Captured/Lost" : assetClass === "Options" ? "Captured Ticks (or Prem pts)" : "Points Captured"}
                    </label>
                    <input 
                       id="ticks" 
                       type="number" 
                       value={journal["Movement"]||""} 
                       onChange={e=>handleJournalChange("Movement",e.target.value)} 
                       className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 font-mono" 
                       placeholder={assetClass === "Options" ? "e.g. 5.00 (pts)" : "# Captured Units"} 
                    />
                    
                  {/* STRICT Live Preview */}
                    {!journal["Movement"] && journal["Entry Price"] && journal["Exit Price"] && (
                      <p className="text-[11px] text-blue-500 dark:text-blue-400 font-semibold mt-1.5 flex items-center gap-1">
                         <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                         Auto-saving as: {(() => {
                             const entry = Number(journal["Entry Price"]);
                             const exit = Number(journal["Exit Price"]);
                             const diff = Math.abs(exit - entry);
                             
                             if (assetClass === "Forex") {
                                 const isIdx = isIndexSymbol(settings["Product"]);
                                 const ps = getPipSizeForSymbol(settings["Product"]);
                                 let pips = diff / ps;
                                 if (isIdx) pips = pips * 10;
                                 return `${Math.round(pips * 100) / 100} pips`;
                             } else if (assetClass === "Futures") {
                                 const ts = getTickSizeForProduct(settings["Product"]) || 0.25;
                                 return `${Math.round(diff / ts)} ticks`;
                             } else if (assetClass === "Options") {
                                 const delta = Number(settings["Delta"] || 0);
                                 if (delta > 0) {
                                     const uPoints = diff / delta;
                                     // Shows: "2.50 underlying pts (0.75 premium pts)"
                                     return `${uPoints.toFixed(2)} underlying pts (${diff.toFixed(2)} premium pts)`;
                                 }
                                 return `${diff.toFixed(2)} premium points`;
                             } else {
                                 return `${Math.round(diff * 100) / 100} points`;
                             }
                         })()}
                      </p>
                    )}                 
                  </div>

                  {!settings["Hide Potential"] && (
                  <div>
                    <label className="block text-sm font-bold mb-1 text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      {assetClass === "Futures" ? "Potential Ticks" : assetClass === "Forex" ? "Potential Pips" : assetClass === "Options" ? "Potential Premium" : "Potential Points"}
                      <Tooltip content="Amount that could have been captured"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-gray-600 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 text-[12px] font-bold cursor-help">?</span></Tooltip>
                    </label>
                    <input id="potential" type="number" value={journal["Potential"]||""} onChange={e=>handleJournalChange("Potential",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-400 font-mono" />
                  </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm">
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-300 font-bold mb-4 border-l-4 border-green-500 pl-3">3. Outcome</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 items-start">
              <div>
                <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">Result<span className="text-red-500 text-lg">*</span></label>
                <div className="flex gap-4">
                  <button type="button" onClick={()=>setJournal(j=>({...j,Win:true,Loss:false}))} className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${journal.Win?"bg-green-500 text-white border-green-600":"bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600"}`}>WON</button>
                  <button type="button" onClick={()=>setJournal(j=>({...j,Win:false,Loss:true}))} className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm border ${journal.Loss?"bg-red-500 text-white border-red-600":"bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-300 border-gray-200 dark:border-gray-600"}`}>LOSS</button>
                </div>
              </div>
              <div>
                <label className="block font-bold mb-2 text-gray-800 dark:text-gray-200">New Account Balance<span className="text-red-500 text-lg">*</span></label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                  <input 
                    id="actual-balance-input" 
                    type="text" 
                    inputMode="decimal" 
                    ref={actualBalRef} 
                    value={journal["Actual Balance"] ?? ""} 
                    onChange={e => handleJournalChange("Actual Balance", e.target.value)} 
                    onBlur={e => handleJournalChange("Actual Balance", parseCurrency(e.target.value))} 
                    className="w-full pl-8 px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-500 focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white bg-white transition font-bold text-lg shadow-inner" 
                    placeholder="0.00" 
                  />
                </div>
              </div>
              {/* NEW: Target Reached Checkbox */}
      <div className="mt-6 flex items-center bg-green-50 dark:bg-green-900/10 p-3 rounded-xl border border-green-100 dark:border-green-800">
         <input 
             type="checkbox" 
             id="targetReachedFut" 
             checked={!!journal["Target Reached"]} 
             onChange={e => handleJournalChange("Target Reached", e.target.checked)} 
             className="mr-3 w-5 h-5 text-green-600 rounded border-gray-300 focus:ring-green-500 cursor-pointer" 
         />
         <label htmlFor="targetReachedFut" className="text-sm font-bold text-green-700 dark:text-green-400 cursor-pointer">
             Target Reached?
         </label>
      </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400 font-bold mb-4 border-l-4 border-yellow-500 pl-3">4. Psychology & Review</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Followed Rules?<span className="text-red-500 text-lg">*</span></label>
                <select id="rules" value={journal["Did I follow my Rules?"]} onChange={e=>handleJournalChange("Did I follow my Rules?",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700">
                  <option value="">Select</option>
                  {RULES_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>

               {!settings["Hide Mood"] && (
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Mood (1-5)</label>
                <select id="mood" value={journal["Mood_Mindset"]} onChange={e=>handleJournalChange("Mood_Mindset",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700">
                  <option value="">Select</option>
                  {MOOD_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              )}

              {!settings["Hide Grade"] && (
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Grade</label>
                <select id="grade" value={journal["Grade"]} onChange={e=>handleJournalChange("Grade",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700">
                  <option value="">Select</option>
                  {GRADE_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              )}
            </div>
            <div className="space-y-6">
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Link / Screenshot</label>
                <input  id="link" type="text" value={journal["Links_Screenshots"]} onChange={e=>handleJournalChange("Links_Screenshots",e.target.value)} placeholder="Paste URL here..." className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400" />
              </div>
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">Notes</label>
                <textarea rows={3} value={journal["Notes"]} onChange={e=>handleJournalChange("Notes",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700" />
              </div>

              {/* ADD THE EMOTIONAL NOTES BLOCK HERE */}
              <div>
                <label className="block font-bold mb-1 text-gray-800 dark:text-gray-200">
                  Emotional Notes
                </label>
                <textarea 
                  rows={3} 
                  value={journal["Emotional Notes"] || ""} 
                  onChange={e => handleJournalChange("Emotional Notes", e.target.value)} 
                  placeholder="How were you feeling during this execution? Anxious? Confident? Rushed?"
                  className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:text-white border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-400" 
                />
              </div>

              {/* FLAG FOR REVIEW CHECKBOX */}
              <div className="mt-2">
                <label className="inline-flex items-center text-red-600 dark:text-red-400 font-bold cursor-pointer select-none">
                  <input 
                    type="checkbox" 
                    checked={!!journal["Needs Review"]} 
                    onChange={e => handleJournalChange("Needs Review", e.target.checked)} 
                    className="mr-2 h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500" 
                  />
                  Flag for Review
                </label>
              </div>

              {!settings["Hide Lesson"] && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800/50">
                <label className="block font-bold mb-2 text-blue-800 dark:text-blue-300">Lesson (Optional)</label>
                <textarea id="lesson" rows={2} value={journal["Lesson"]} onChange={e=>handleJournalChange("Lesson",e.target.value)} className="w-full px-4 py-2 rounded-lg border dark:bg-gray-800 dark:text-white border-blue-200 dark:border-blue-700" />
                <div className="mt-3">
                  <label  className="inline-flex items-center text-blue-800 dark:text-blue-300 font-semibold cursor-pointer select-none">
                    <input type="checkbox" checked={journal["LogLesson"]} onChange={e=>setJournal(prev=>({...prev, LogLesson:e.target.checked}))} className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    Save to Lessons Log
                  </label>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-10 pt-6 border-t border-gray-100 dark:border-gray-700">
          <button 
            onClick={handleSaveTrade} 
            disabled={savingTrade}
            className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-orange-500 to-orange-400 dark:from-orange-700 dark:to-orange-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:from-orange-600 hover:to-orange-500 transform hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {savingTrade ? "Saving..." : "Save Trade"}
          </button>
        </div>
      </div>
)} {/* <--- 2. ADD THIS CLOSING BRACE/PARENTHESIS HERE */}
      
{currentPopup?.id === 'tradeSaved' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-20 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center border-2 border-blue-500 animate-fadeIn">
      <span className="flex items-center justify-center bg-blue-100 dark:bg-blue-800 rounded-full mb-3" style={{ width: 60, height: 60 }}>
        <svg className="w-10 h-10 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="22" stroke="currentColor" strokeWidth="4" fill="currentColor" opacity="0.13"/>
          <path d="M16 24l6 6 10-14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        </svg>
      </span>
      <div className="text-2xl font-extrabold text-blue-700 dark:text-blue-300 mb-1">
        Trade Saved!
      </div>
      <div className="text-gray-700 dark:text-gray-200 mb-1 text-center">
        Your journal entry has been saved.
      </div>
      <button
        className="mt-2 px-5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition"
        onClick={closeCurrentPopup}
      >
        OK
      </button>
    </div>
  </div>
)}

{missingFields.length > 0 && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setMissingFields([])}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Required Fields Missing
      </h2>
      <ul className="mb-4 text-gray-800 dark:text-gray-200">
        {missingFields.map((f) => (
   <li key={f} className="pl-2">{f}</li>
 ))}

      </ul>
      <button
        onClick={() => setMissingFields([])}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold px-4 py-2 rounded shadow"
      >
        Close
      </button>
    </div>
  </div>
)}

      {/* WIN TABLE */}
      {projectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-blue-50/90 via-white/90 to-blue-100/80 dark:from-[#1e293b] dark:via-gray-800 dark:to-[#1e293b]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-blue-300 dark:border-blue-900 p-8 relative overflow-auto max-h-[85vh]">
            
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-blue-100 hover:dark:bg-blue-800 transition"
              onClick={() => setProjectionOpen(false)}
              aria-label="Close"
            >×</button>
              
            <h3 className="text-2xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#2563eb" strokeWidth="2" />
                <path d="M8 12l2 2l4-4" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Win Projection Table
            </h3>

            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many trades {assetClass === "Stocks" ? "" : "(using at least your minimum size setting) "} 
              it would take to reach your goal. Any trades with the red background indicate when you are over your risk tolerance. 
              The totals are estimates and don't take into account broker fees and commissions.
              {assetClass !== "Stocks" && (
                <>
                  <br /><br />
                  If you prefer to always trade the same {getUnitLabel(assetClass).toLowerCase()}, you can set the minimum and maximum 
                  {assetClass === "Forex" ? " lot size" : " contracts"} to the same number in the settings.
                </>
              )}
            </p>

            {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
            {/* 👆 END TOGGLE BLOCK 👆 */}
            
            {(() => {
              const accountGoal = Number(settings["Account Goal"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const dailyGoal = Number(settings["Daily Goal"] || 0);
              const tradeCount = winProjection?.tradeCount || 0;

              // 1. Check if account is blown (Prop Firm Max Loss or $0)
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const isProp = (init > 0 && maxLoss > 0);
              const ruinThreshold = isProp ? Math.max(0, init - maxLoss) : 0;

              // If blown, return ONLY the red warning. This hides the blue stats box.
              if (running <= ruinThreshold) {
                return (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm font-bold text-red-800 dark:text-red-200">
                    Your account balance has reached your maximum loss limit or $0. Please consider switching to your demo account to review your plan before refunding your account.
                  </div>
                );
              }

              if (accountGoal <= 0) return null;
              if (running >= accountGoal) {
                return (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 text-sm font-bold text-green-800 dark:text-green-200">
                    You’re already at or above your account goal of {formatCurrency(accountGoal)}.
                  </div>
                );
              }

              let estimatedDays = 0;
              let currentDayAccumulator = 0;
              const trades = winProjection?.trades || [];

              if (dailyGoal > 0) {
                for (const t of trades) {
                  const profit = t.tradeProfit || 0;
                  if (profit >= dailyGoal) {
                    estimatedDays++;
                    currentDayAccumulator = 0;
                  } else {
                    currentDayAccumulator += profit;
                    if (currentDayAccumulator >= dailyGoal) {
                      estimatedDays++;
                      currentDayAccumulator = 0;
                    }
                  }
                }
                if (currentDayAccumulator > 0) estimatedDays++;
              } else {
                estimatedDays = tradeCount;
              }

              return (
                <div className="mb-6 p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
                  <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                    <FaRegClock className="text-blue-500" />
                    Estimated Time to Goal
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-blue-700 dark:text-blue-300">
                    {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">winning trades in ≈ </span> {estimatedDays} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">trading days</span> 
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                    To reach <strong>{formatCurrency(accountGoal)}</strong> requires approximately <strong>{tradeCount}</strong> winning trades in about {estimatedDays} if you reached your trading daily goal.
                  </p>
                </div>

                    <div className="flex-1 text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-100 dark:border-gray-700 shadow-sm">
                      <strong className="block text-gray-800 dark:text-gray-200 mb-1">How is this calculated?</strong>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>
                          While trade profit &lt; <strong>{formatCurrency(dailyGoal)}</strong>, we assume you trade until you hit your daily goal.
                        </li>
                        <li>
                          Once trade profit &ge; <strong>{formatCurrency(dailyGoal)}</strong>, we assume 1 trade per day.
                        </li>
                        <li>
                          As your balance compounds, your position sizes and profits will <strong>increase</strong>. Make sure to update your <strong>Daily Goal</strong> in the settings to reflect your growing account size!
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })()}

            {winProjection?.tradeCount === 0 && (
              <div className={`text-center py-4 font-semibold ${
                (() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : (assetClass === "Stocks" ? 1 : 0);
                  return r <= thresh;
                })() 
                  ? "text-red-600 dark:text-red-400" 
                  : "text-green-600 dark:text-green-300"
              }`}>
                {(() => {
                  const r = Number(settings["Running Balance"] || 0);
                  const i = Number(settings["Initial Balance"] || 0);
                  const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                  const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : (assetClass === "Stocks" ? 1 : 0);
                  if (r <= thresh) {
                    return "No trades can be taken. Your account is blown. Please consider switching to a demo account and refine your strategy before refunding your account.";
                  }
                  return "No trades can be taken with the current settings. If you have reached your goal you can adjust it and continue trading.";
                })()}
              </div>
            )}

            {winProjection?.tradeCount > 0 && (() => {
              // Only allow the table to render if the account is NOT blown
              const r = Number(settings["Running Balance"] || 0);
              const i = Number(settings["Initial Balance"] || 0);
              const m = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const thresh = (i > 0 && m > 0) ? Math.max(0, i - m) : 0;
              return r > thresh; 
            })() && (
              <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-blue-200 dark:border-blue-800 shadow">
                <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                  <thead className="bg-blue-100 dark:bg-blue-900/80 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Trade #</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Balance</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">
                          {assetClass === "Stocks" ? "Investment" : assetClass === "Options" ? "Risk Budget" : "Max Drawdown"}
                      </th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">
                          {getUnitLabel(assetClass)}
                      </th>
                      {(assetClass === "Options") && (
                          <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">
                              Est. Cost (Capital)
                          </th>
                      )}
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Risk</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">Total Profit</th>
                      <th className="px-3 py-2 border-b-2 border-blue-200 dark:border-blue-800 text-left text-blue-900 dark:text-blue-200 font-semibold">End Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rowsToShow = isTrialing 
                                      ? winProjection.trades.slice(0, 10) 
                                      : winProjection.trades;

                      let currentDay = 1;
                      let dayAccumulator = 0;
                      const dailyGoalAmt = Number(settings["Daily Goal"] || 0);

                      const tradesWithDays = rowsToShow.map((row) => {
                        const assignedDay = currentDay;
                        if (dailyGoalAmt > 0) {
                          dayAccumulator += (row.tradeProfit || 0);
                          if (dayAccumulator >= dailyGoalAmt) {
                            currentDay++;
                            dayAccumulator = 0;
                          }
                        }
                        return { ...row, day: assignedDay };
                      });

                      return tradesWithDays.map((row, i) => {
                        const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDays[i-1].day;

                        return (
                          <React.Fragment key={i}>
                            {isNewDay && (
                              <tr className="bg-blue-100/50 dark:bg-blue-900/30">
                                <td colSpan="10" className="px-3 py-2 text-center text-xs font-bold text-blue-800 dark:text-blue-300 uppercase tracking-widest border-y border-blue-200 dark:border-blue-800">
                                  — Day {row.day} —
                                </td>
                              </tr>
                            )}
                            <tr
                              className={`${
                                row.underfunded 
                                  ? "bg-red-100 dark:bg-red-900/80" 
                                  : "hover:bg-blue-50 dark:hover:bg-blue-900/30 bg-white dark:bg-gray-900"
                              } transition border-b border-gray-100 dark:border-gray-800`}
                            >
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.balance)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.riskDollar)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                                {assetClass === "Forex" 
                                  ? Number(row.size || 0).toFixed(2) 
                                  : (row.size)}
                              </td>
                              {(assetClass === "Options") && (
                                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100 italic">
                                      {formatCurrency(row.cost)}
                                  </td>
                              )}
                              <td className={`px-3 py-2 ${row.size === 0 ? "text-red-600 font-bold" : "text-gray-900 dark:text-gray-100"}`}>
                                {formatCurrency(row.totalRisk)}
                              </td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.tradeProfit)}</td>
                              <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{formatCurrency(row.nextBalance)}</td>
                            </tr>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              {isTrialing && winProjection.trades.length > 5 && (
                <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                    Full projection table available after Free Trial
                  </p>
                </div>
              )}
            </div>
            )}
    </div>
  </div>
)}

      {lossProjectionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn">
          <div className="bg-gradient-to-br from-red-50/90 via-white/90 to-red-100/80 dark:from-[#2d1616] dark:via-gray-800 dark:to-[#2d1616]
            max-w-4xl w-full rounded-2xl shadow-2xl border-4 border-red-300 dark:border-red-900 p-8 relative overflow-auto max-h-[85vh]">
            
            <button
              className="absolute top-2 right-2 bg-gray-200 dark:bg-gray-900 text-gray-600 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-100 hover:dark:bg-red-800 transition"
              onClick={() => setLossProjectionOpen(false)}
              aria-label="Close"
            >×</button>

            <h3 className="text-2xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center gap-2">
              <svg width="28" height="28" fill="none" viewBox="0 0 24 24" className="mr-2">
                <circle cx="12" cy="12" r="10" stroke="#dc2626" strokeWidth="2" />
                <path d="M12 8v4l3 3" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Loss Projection Table
            </h3>

            <p className="mb-6 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
              Projections show how many consecutive full-loss trades 
              {assetClass === "Stocks" ? " " : "(using at least your minimum size setting) "}
              it would take to blow your account. The totals are estimates and do not include fees.
              
              {/* Show this advice for Futures, Forex, AND Options */}
              {assetClass !== "Stocks" && (
                <>
                  <br /><br />
                  If you prefer to always trade the same {getUnitLabel(assetClass).toLowerCase()}, 
                  you can set the minimum and maximum {
                    assetClass === "Forex" ? "lot size" : 
                    (assetClass === "Options" || assetClass === "Futures") ? "contracts" : "units"
                  } to the same number in the settings.
                </>
              )}
            </p>

            {/* 👇 ADD THIS TOGGLE BLOCK 👇 */}
            <div className="mb-4 flex items-center justify-end">
              <label className="flex items-center cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-300 select-none">
                <span className="mr-3">Group by Trading Day</span>
                <div className="relative flex items-center">
                  <input 
                    type="checkbox" 
                    className="sr-only" 
                    checked={showDaySeparators} 
                    onChange={() => setShowDaySeparators(!showDaySeparators)} 
                  />
                  <div className={`w-10 h-5 rounded-full shadow-inner transition-colors duration-200 ease-in-out ${showDaySeparators ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-700'}`}></div>
                  <div className={`absolute left-0 top-0 w-5 h-5 bg-white rounded-full shadow border border-gray-200 transform transition-transform duration-200 ease-in-out ${showDaySeparators ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
              </label>
            </div>
            {/* 👆 END TOGGLE BLOCK 👆 */}

            {(() => {
              const dailyLoss = Number(settings["Daily Loss Limit"] || 0);
              const init = Number(settings["Initial Balance"] || 0);
              const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
              const running = Number(settings["Running Balance"] || 0);
              const tradeCount = lossProjection?.tradeCount || 0;

              if (!(dailyLoss > 0)) return null;

             let isProp = (init > 0 && maxLoss > 0);
             let ruinThreshold = 0;

             if (isProp) {
               ruinThreshold = Math.max(0, init - maxLoss);
             } else {
               ruinThreshold = (assetClass === "Stocks") ? 1 : 0;
             }

             const distance = Math.max(0, running - ruinThreshold);
             const daysToRuin = dailyLoss > 0 ? Math.ceil(distance / dailyLoss) : 0;

              return (
                <div className="mb-6 p-5 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800/50">
                  <h4 className="font-bold text-red-900 dark:text-red-100 mb-3 flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500" />
                    Risk to Ruin Analysis
                  </h4>
                  
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="flex-1">
                      <div className="text-3xl font-extrabold text-red-700 dark:text-red-300">
                        {tradeCount} <span className="text-lg font-medium text-gray-600 dark:text-gray-400">consecutive losses</span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-relaxed">
                        To reach your failure threshold of <strong>{formatCurrency(ruinThreshold)}</strong> based on your current risk settings.
                      </p>
                    </div>

                    <div className="flex-1 bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900 shadow-sm">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold tracking-wider mb-1">Time to Ruin (Daily Limit)</div>
                      <div className="text-xl font-bold text-gray-800 dark:text-gray-100">
                        {daysToRuin} <span className="text-sm font-medium text-gray-500">trading days</span>
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                        If you hit your Daily Loss Limit ({formatCurrency(dailyLoss)}) every single day.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="overflow-auto max-h-[50vh] rounded-xl border-2 border-red-200 dark:border-red-800 shadow">
              <table className="min-w-full border-separate border-spacing-0 bg-white dark:bg-gray-900">
                <thead className="bg-red-100 dark:bg-red-900/80 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Trade #</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance Before</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">{getUnitLabel(assetClass)}</th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">
                          {assetClass === "Options" ? "Total Premium Loss" : "Loss Amount"}
                        </th>
                    <th className="px-4 py-3 border-b-2 border-red-200 dark:border-red-800 text-left text-red-900 dark:text-red-200 font-semibold">Balance After</th>
                  </tr>
                </thead>
                <tbody>
                    {(lossProjection?.trades?.length ?? 0) === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic">
                          No trades can be taken with the current settings.
                        </td>
                      </tr>
                    ) : (
                      (() => {
                        const rowsToShow = isTrialing ? lossProjection.trades.slice(0, 10) : lossProjection.trades;
                        
                        let currentLossDay = 1;
                        let lossAccumulator = 0;
                        const dailyLossAmt = Number(settings["Daily Loss Limit"] || 0);

                        const tradesWithDaysLoss = rowsToShow.map(row => {
                          const assignedDay = currentLossDay;
                          if (dailyLossAmt > 0) {
                            lossAccumulator += (row.loss || 0);
                            if (lossAccumulator >= dailyLossAmt) {
                              currentLossDay++;
                              lossAccumulator = 0; // Reset for the next day
                            }
                          }
                          return { ...row, day: assignedDay };
                        });

                        return tradesWithDaysLoss.map((row, i) => {
                          const isNewDay = showDaySeparators && i > 0 && row.day !== tradesWithDaysLoss[i-1].day;

                          return (
                            <React.Fragment key={i}>
                              {isNewDay && (
                                <tr className="bg-red-100/50 dark:bg-red-900/30">
                                  <td colSpan="10" className="px-4 py-2 text-center text-xs font-bold text-red-800 dark:text-red-300 uppercase tracking-widest border-y border-red-200 dark:border-red-800">
                                    — Day {row.day} —
                                  </td>
                                </tr>
                              )}
                              
                              {/* MasterCalculate 5-Column Row */}
                              <tr className="hover:bg-red-50 dark:hover:bg-red-900/40 transition group border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                                <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{row.trade}</td>
                                <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                  {formatCurrency(row.balanceBefore)}
                                </td>
                                <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                  {assetClass === "Forex" 
                                    ? Number(row.size || row.lots || 0).toFixed(2) 
                                    : (row.size || row.contracts || row.lots)}
                                </td>
                                <td className="px-4 py-2 text-red-600 dark:text-red-400 font-bold group-hover:text-red-700">
                                  {formatCurrency(row.loss)}
                                </td>
                                <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                                  {formatCurrency(row.balanceAfter)}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        });
                      })()
                    )}
                  </tbody>
              </table>
        {isTrialing && (lossProjection?.trades?.length ?? 0) > 5 && (
          <div className="p-4 bg-gray-100 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700 text-center">
            <p className="text-lg font-bold text-gray-600 dark:text-red-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
              Full projection table available after Free Trial
            </p>
          </div>
        )}
      </div>
    </div>
  </div>
)}


{showZeroContractsPopup && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={() => setShowZeroContractsPopup(false)}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        Based on the product you selected, your account balance, risk tolerance, and stop loss size, the suggested number of {getUnitLabel(assetClass).toLowerCase()} for this trade is <span className="font-bold text-red-500">0</span>.
      </p>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You are currently risking <span className="font-bold text-red-500">{lastActualRiskPercent}%</span> of your balance, which is <span className="font-bold text-red-500">{formatCurrency(lastTotalRisk)}</span>.
      </p>

      {(() => {
        const minQty = Number(calculatedSummary?.minContracts || settings["Minimum Contracts"] || 1);
        const riskDollar = Number(calculatedSummary?.riskDollar || 0);

        // --- OPTIONS TIP ---
        if (assetClass === "Options") {
          const maxRiskPerShare = riskDollar / (minQty * 100);
          const maxRiskFormatted = formatCurrency(maxRiskPerShare);
          const entry = Number(calculatedSummary?.entryPrice || 0);
          const totalCost = entry * 100 * minQty;
          const runningBal = Number(settings["Running Balance"] || 0);
          
          return (
            <div className="mb-6 space-y-2 text-gray-800 dark:text-gray-100">
              <p>
                <strong>Tip:</strong> To trade <span className="font-semibold">{minQty}</span> contract{minQty > 1 ? "s" : ""} within your risk budget, your stop loss premium can be no more than <span className="font-bold text-yellow-600">{maxRiskFormatted}</span> below your entry.
              </p>
              {totalCost > runningBal && (
                <p className="text-red-500 text-sm font-semibold mt-2">
                  * Note: The total cost of this minimum trade ({formatCurrency(totalCost)}) exceeds your available Buying Power ({formatCurrency(runningBal)}).
                </p>
              )}
            </div>
          );
        }

        // --- STOCKS TIP ---
        if (assetClass === "Stocks") {
            const maxRiskPerShare = formatCurrency(riskDollar / minQty);
            return (
                <p className="mb-6 text-gray-800 dark:text-gray-100">
                  <strong>Tip:</strong> To trade <span className="font-semibold">{minQty}</span> share{minQty > 1 ? "s" : ""} within your risk, your stop loss must be ≤ <span className="font-bold text-yellow-600">{maxRiskPerShare}</span> below your entry.
                </p>
            );
        }

        // --- FUTURES & FOREX TIP ---
        const mult = Number(calculatedSummary?.multiplier || 0);
        const maxSLForMin = (mult > 0 && minQty > 0)
          ? Math.floor(riskDollar / (minQty * mult))
          : 0;

        if (maxSLForMin <= 0) return null; 
        
        const isFx = assetClass === "Forex";
        const isIdx = isIndexSymbol(settings["Product"]);
        
        let displayStr = "";
        if (isFx) {
            const formatPts = Math.round(maxSLForMin * 100) / 100;
            const formatTks = isIdx ? Math.round(maxSLForMin * 100) : Math.round(maxSLForMin * 10 * 100) / 100;
            displayStr = isIdx 
              ? `${formatPts} points / ${formatTks} ticks` 
              : `${formatPts} pips / ${formatTks} ticks`;
        } else {            displayStr = `${maxSLForMin} ${assetClass === "Futures" ? "ticks" : "points"}`;
        }

        return (
          <p className="mb-6 text-gray-800 dark:text-gray-100">
            <strong>Tip:</strong> To trade <span className="font-semibold">{minQty}</span> {getUnitLabel(assetClass).toLowerCase()} within your risk, your stop loss must be ≤{" "}
            <span className="font-bold text-yellow-600">{displayStr}</span>.
          </p>
        );
      })()}

      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider choosing a different product, adjusting your risk limit, or waiting for a trade setup that requires a smaller stop loss.
      </p>

      <button
        onClick={() => setShowZeroContractsPopup(false)}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'dailyGoalPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>

      <h2 className="text-2xl font-bold mb-4 text-green-700 dark:text-green-500 flex items-center">
        <svg className="w-7 h-7 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations!
      </h2>

      <div className="mb-4 text-lg text-gray-800 dark:text-gray-100 font-semibold text-center">
        You reached your{" "}
        <span className="text-green-600 dark:text-green-300 font-bold">Daily Goal</span>!
        <br />
        {currentPopup.data?.dailyGoal !== undefined && (
          <span className="block mt-2 text-base text-green-700 dark:text-green-500 font-bold">
            Goal:{" "}
            <span className="text-green-700 dark:text-green-500">
              {Number(currentPopup.data.dailyGoal).toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 2,
              })}
            </span>
          </span>
        )}

        <span className="block mt-2 font-bold text-lg text-gray-700 dark:text-gray-200">
          Take a moment to celebrate your discipline and progress.<br /><br />
          Size down if you continue to trade, you don't want to risk giving your earnings back to the market.<br /><br />
          {getDailyGoalMessage()}
        </span>
      </div>

      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'overtradingPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="overtrading-title"
      aria-describedby="overtrading-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="overtrading-title"
        className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning: Overtrading!
      </h2>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-yellow-700 dark:text-yellow-300">You have logged more trades than your daily plan allows.</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum trades per day is: </span>
        <span className="font-bold text-yellow-700 dark:text-yellow-300">
          {currentPopup.data.maxTradesPerDay}
        </span>
      </p>

      <p id="overtrading-desc" className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Overtrading can lead to impulsive decisions and unnecessary risk by draining your mental capital.
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Review your trades, lock your account, and come back tomorrow recharged for a strong start!
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Below is a really good video from Psychology of Profits regarding protecting your mental capital.
      </p>

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/watch?v=6vZG4epFEOM"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-yellow-500 text-yellow-700 dark:text-yellow-300 font-bold px-4 py-2 rounded hover:bg-yellow-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Watch: Mental Capital
          <span className="sr-only">(opens in a new tab)</span>
        </a>

        <button
          onClick={closeCurrentPopup}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>

)}


{currentPopup?.id === 'maxLossLimitBlown' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-600 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
        <div className="flex items-center mb-4">

          <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
          </svg>

          <h2 className="text-xl font-bold text-red-600 dark:text-red-400">
            Account Blown
          </h2>
          
        </div>
      <p className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        Your balance has reached <span className="font-bold text-red-600">$0</span>. <br/><br/>This is a good time to switch to your demo account to review and refine your plan before depositing more funds.
      </p>
      <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        The key is to preserve your capital.
      </p>
        <p className="font-bold mt-4 mb-4 text-gray-800 dark:text-gray-100">
        Spend time backtesting until you've mastered your strategy and are consistently profitable. This is the best way to prepare before investing more of your hard-earned money.
      </p>

      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-gray-100">
        Trading is 80% Psychology.  Review mindset and risk-management refreshers, the Psychology of Profit has some really good videos.
      </p>

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <a
          href="https://www.youtube.com/@Psychology-Of-Profit" 
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-red-600 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600 transition"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M6 4l10 6-10 6V4z" />
          </svg>
          Visit the Psychology of Profit Youtube Channel
          <span className="sr-only">(opens in a new tab)</span>
        </a>

             </div>

      <p className="font-bold mt-4 mb-4 text-red-800 dark:text-red-400">
        Don't give up. You can do this!  
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>

    </div>
  </div>
)}



{currentPopup?.id === 'tooManyLossesPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Daily Lost Trades Limit Reached!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          You've hit your max daily loss count.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Your maximum losses per day is: </span>
        <span className="font-bold text-red-700 dark:text-red-300">
          {currentPopup.data.maxLossesPerDay}
        </span>
      </p>
      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
         Consider stopping for the day to protect your capital. <br/> <br/>Review your trades and reset for tomorrow.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'lossLimitWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Daily Loss Limit Exceeded!
      </h2>
      <p className="mb-3  font-semibold text-lg text-gray-800 dark:text-gray-100">
        Your total loss for today is <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossForDay)}</span>,
        which exceeds your daily loss limit of <span className="font-bold text-red-600">{formatCurrency(currentPopup.data.lossLimitAmount)}</span>.
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-400">
        Please consider stopping trading for the day to protect your account.
      </p>
      <p className="mb-6 font-semibold text-red-700 dark:text-red-400">
        You are worthy of success!  Don't give up!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === "win10NoRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - Big Wins Without Following Rules
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line">
        {currentPopup.data?.message
          ?.split('\n')
          .map((line, i) =>
            line.trim() === "Discipline > excitement!" ? (
              <span key={i} className="font-bold text-blue-700 dark:text-blue-500">
                Discipline &gt; excitement!
                <br />
              </span>
            ) : (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
            )
          )
        }
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === "win10WithRulesPopup" && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Congratulations – Following Your Rules!
      </h2>
      <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 whitespace-pre-line">
       {(currentPopup.data?.message || "Amazing job! You achieved 10 wins in a row while sticking to your rules. This is the true mark of a disciplined trader.")
         .split('\n')
         .map((line, i) => (
           <React.Fragment key={i}>
             {formatThreeLossMessage(line)}
             <br />
           </React.Fragment>
         ))}
      </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'moodVolatilityPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Mood Volatility Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">
          Your emotional state appears to be heightened.
        </span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Trading with intense emotions—whether it's excitement or stress—can lead to decisions based on feeling, not strategy. Your greatest asset is a calm and objective mind.
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Before your next trade, journal what you're feeling and why. We recommend you pause until your focus is back on your process.
      </p>
       <p className="font-bold mb-6 text-gray-700 dark:text-gray-300">
        If you consistently trade in a heightened emotional state, you may be using a position size, symbol, or timeframe that doesn't fit your personality.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'twoLossAfterGoalWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning – Losses After Goal
      </h2>
      <div className="font-bold mb-4 text-gray-800 dark:text-gray-100 whitespace-pre-line text-left overflow-visible">
          {(currentPopup.data?.message || "You had 2 losses after reaching your daily goal.")
            .split('\n')
            .map((line, i) => (
              <React.Fragment key={i}>
                {renderWithTiltTooltip(line)}
                <br />
              </React.Fragment>
          ))}
        </div>
      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        OK
      </button>
    </div>
  </div>
)}




{currentPopup?.id === 'overRiskWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning!
      </h2>

      <p className="mb-3 text-gray-800 dark:text-gray-100">
        You lost more than your planned risk amount.
      </p>

      <p className="mb-1">
        <span className="font-extrabold text-yellow-600 dark:text-yellow-400">
          Risk amount: {formatCurrency(currentPopup.data.riskAmount)}
        </span>
      </p>

      <p className="mb-4">
        <span className="font-extrabold text-yellow-600 dark:text-yellow-400">
          Amount lost: {formatCurrency(currentPopup.data.amountLost)}
        </span>
      </p>

      <p className="mb-4 font-semibold text-gray-700 dark:text-gray-300">
        Make sure that you are always using the proper stop loss and not moving it. If the trade is going too far against your analysis, you can always get out and get back in at the right time.
      </p>

      <p className="mb-2 font-extrabold text-yellow-600 dark:text-yellow-400">
        Keep your losers small and your winners big and never marry a trade idea!
      </p>

      <button
        onClick={closeCurrentPopup}
        className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'drawdownWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-purple-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 20v-6m0 0V4m0 10c-2 0-4-1-4-4s2-4 4-4 4 1 4 4-2 4-4 4z" strokeWidth="2"/>
        </svg>
        Drawdown Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-purple-700 dark:text-purple-300">
          Your account has experienced a drawdown of more than <span className="underline">{currentPopup.data.drawdownLimitPct}%</span> from its peak.
        </span>
      </p>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold">Drawdown amount: </span>
        <span className="font-bold text-purple-700 dark:text-purple-300">
          ${currentPopup.data.drawdownAmount?.toLocaleString(undefined, {maximumFractionDigits:2})}
        </span>
        <span className="ml-2 text-sm text-purple-500 dark:text-purple-300">
          ({currentPopup.data.drawdownPct?.toFixed(2)}%)
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Consider pausing to review your trades and protect your capital before proceeding.
      </p>
      <p className="font-bold mt-6 mb-6 text-gray-700 dark:text-gray-300">
        No one wins 100% of the time and there is nothing wrong with switching to demo until you are winning consistently and growing your account.
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-purple-500 hover:bg-purple-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'consecutiveWinsPopup' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M8 12l2 2l4-4" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Hot Streak!
      </h2>
      <p className="mb-3 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">You've won 5 trades in a row!</span>
      </p>
      <p className="text-gray-700 font-semibold dark:text-gray-300">
        Stay disciplined—don&apos;t increase your risk or change your plan just because you&apos;re on a hot streak.
      </p>
      <p className="mt-4 mb-6 font-semibold text-gray-700 dark:text-blue-300">
        Stick to your process and keep your edge!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'accountGoalReached' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <Confetti width={width} height={height} numberOfPieces={500} recycle={false} />
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-green-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-green-700 dark:text-green-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" strokeWidth="2" />
        </svg>
        Account Goal Achieved!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Congratulations! You've reached your account goal!
        </span>
      </p>
      <p className="mb-6 text-gray-700 dark:text-gray-300">
        Your balance is  <span className="font-bold">{formatCurrency(settings["Running Balance"])}</span><br />
        Your Goal was <span className="font-bold">{formatCurrency(settings["Account Goal"])}</span>
      </p>

      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-green-700 dark:text-green-300">
          Your hard work has paid off!
        </span>
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}



{currentPopup?.id === 'lessonsLearnedStreakWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-blue-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 6v6l4 2" strokeWidth="2" />
        </svg>
        Lessons Learned Streak!
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-blue-700 dark:text-blue-300">Great job extracting value from your trades!</span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">Consistently recording your lessons learned will accelerate your growth. <br/><br/> Review them every day during your pretrade routine until they become seared in your brain!</p>
      <button
        onClick={closeCurrentPopup}
        className="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}

{currentPopup?.id === 'riskOfRuinWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-red-700 dark:text-red-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
        </svg>
        Risk of Ruin Warning
      </h2>
      <p className="mb-4 text-gray-800 dark:text-gray-100">
        <span className="font-bold text-red-700 dark:text-red-300">
          Based on your current risk, your account could be wiped out in just <span className="underline">{lossProjection.tradeCount}</span> consecutive losses.
        </span>
      </p>
      <p className="mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Consider lowering your risk per trade to protect your remaining capital.
      </p>
      <p className="mt-6 mb-6 font-semibold text-gray-700 dark:text-gray-300">
        Switch to your demo account and trade there until you are winning consistently. 
      </p>
      <p className="mt-6 mb-6 font-semibold text-blue-700 dark:text-blue-400">
        Don't be discouraged, you can do this!
      </p>
      <button
        onClick={closeCurrentPopup}
        className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'twoLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-yellow-500 relative animate-fadeIn">
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >×</button>
      <h2 className="text-xl font-bold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
        <svg className="w-6 h-6 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" strokeWidth="2"/>
        </svg>
        Warning - 2 Consecutive Losses!
      </h2>

      <div className="mb-4 font-semibold text-gray-800 dark:text-gray-100 text-left">
        {TWO_LOSS_WARNING_TEXT.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {highlightHundredPercent(line)}
            <br />
          </React.Fragment>
        ))}
      </div>

      <button
        onClick={closeCurrentPopup}
        className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold px-6 py-2 rounded shadow transition"
      >
        Close
      </button>
    </div>
  </div>
)}


{currentPopup?.id === 'threeLossWarning' && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div
      className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 max-w-lg w-full border-2 border-red-500 relative animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="three-loss-title"
      aria-describedby="three-loss-desc"
    >
      <button
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
        onClick={closeCurrentPopup}
        aria-label="Close"
      >
        ×
      </button>

      <h2
        id="three-loss-title"
        className="text-xl font-bold mb-4 text-red-700 dark:text-red-500 flex items-center"
      >
        <svg className="w-6 h-6 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" strokeWidth="2" />
          <path d="M12 8v4l3 3" strokeWidth="2" strokeLinecap="round" />
        </svg>
        Danger!
      </h2>

      <div id="three-loss-desc" className="mb-4 font-semibold text-gray-800 dark:text-gray-100">
        {currentPopup.data?.message.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {formatThreeLossMessage(line)}
            <br />
          </React.Fragment>
        ))}

        <div className="mt-4 text-blue-400 font-bold">
          {"You got this!!"}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        {currentPopup.data?.videoLink && (
          <a
            href={currentPopup.data.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-red-500 text-red-700 dark:text-red-300 font-bold px-4 py-2 rounded hover:bg-red-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-gray-900 transition"
          >
            <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6 4l10 6-10 6V4z" />
            </svg>
            Watch: Dealing with Trading Losses
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}

        <button
          onClick={closeCurrentPopup}
          className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-2 rounded shadow transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

<FirstTradeAnalysisPopup 
  currentPopup={currentPopup} 
  onClose={closeCurrentPopup} 
/>

{showChecklist && (
  <PreTradeChecklistModal
    onClose={handleChecklistConfirm}
    onConfirm={handleChecklistConfirm}
  />
)}

{/* Strategy Checklist Floating Panel */}
      {showStrategyModal && (
        <div className="fixed top-24 right-8 z-40 animate-fadeIn pointer-events-auto w-96 max-w-[calc(100vw-2rem)] shadow-2xl">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6 border-2 border-blue-500 relative max-h-[80vh] flex flex-col">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              onClick={() => setShowStrategyModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="text-xl font-bold mb-4 text-blue-700 dark:text-blue-300 flex items-center gap-2 shrink-0">
              <FaClipboardList className="text-blue-500" />
              Strategy Checklist
            </h2>
            
            <div className="overflow-y-auto mb-4 pr-2 flex-1">
              {userPrefs?.tradingRules ? (
                <div className="space-y-3">
                  {userPrefs.tradingRules.split('\n').filter(rule => rule.trim() !== '').map((rule, index) => (
                    <label key={index} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition">
                      <input 
                        type="checkbox" 
                        className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700" 
                      />
                      <span className="text-gray-800 dark:text-gray-200 font-medium leading-relaxed text-sm">
                        {rule}
                      </span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <p className="mb-2">No Trading Rules defined yet.</p>
                  <p className="text-sm">Head over to the Lessons page to define your core strategy.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
              <button
                onClick={() => setShowStrategyModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-2 rounded-xl shadow transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Calculator */}
      <button onClick={() => setShowCalculator(true)} className="fixed bottom-12 right-6 z-40 bg-gray-800 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
        <FaCalculator />
      </button>
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
    </div>
  );
}